import { DialogService } from "src/app/services/dialog.service";
import { API_ERROR_COPY } from "src/app/services/api-error-notifier.service";
import { Component, ElementRef, HostListener, OnDestroy, OnInit, OnChanges, AfterViewInit, SimpleChanges, ViewChild, ViewChildren, QueryList, Input, ChangeDetectorRef } from "@angular/core";
import { AiChatServiceService } from "../../service/ai-chat-service.service";
import { ChatMessage, ChatHistoryItem, AsyncInvokePayload, AsyncInvokeResponseBody, JobStatusResponseBody, ExecuteSqlPayload, ExecuteSqlResponse, SuggestedQueryCategory, RagCitation, ContractRagTable, ContractRagSegment, MultiMetricQuery, OpsMonitor, ScraiiBrandOption, FloatingChatSnapshot } from "../../interface/scraii-ai-interface";
import { SessionService } from "src/app/services/session.service";
import { ColDef } from "ag-grid-community";
import 'ag-grid-enterprise';
import { Chart, ChartConfiguration, Plugin, registerables } from "chart.js";
import { timeout, retry, catchError, take, switchMap, map, takeWhile } from "rxjs/operators";
import { throwError, Observable, Subscription, of, interval, forkJoin } from "rxjs";
import { SpeechService } from "src/app/services/speech.service";
import { MatDialog } from "@angular/material/dialog";
import { PopupDeleteComponent } from "src/app/components/shared/popup-delete/popup-delete.component";
import { formatColumnDisplayName } from "src/app/pipe/column-display-name.pipe";
import { environment } from "src/environments/environment";
import { DataService } from "src/app/services/data.service";
import { ScraiiGanttChartComponent } from "./gantt-chart/scraii-gantt-chart.component";
import { GANTT_TABLE_COLUMN_CONFIG } from "./gantt-chart/gantt-table-columns.config";

/** Optional env flag — safe when omitted or commented out in environment files. */
function isScraiiGanttPocEnabled(): boolean {
  return (environment as { scraiiGanttPocEnabled?: boolean }).scraiiGanttPocEnabled === true;
}

// Chart configuration interface
interface ChartConfig {
  chart_type: "bar" | "line" | "mixed" | "area_line";
  chart_variant?:
  | "simple"
  | "grouped"
  | "multi"
  | "clustered"
  | "stacked"
  | "cluster_stacked"
  | "horizontal"
  | "area"
  | "stepped"
  | "percentage"; // Chart variation
  sql: string;
  x_axis: string;
  y_axis: string | string[]; // Support multiple y-axis for multi-line/clustered charts
  group_by?: string; // For grouping data (e.g., rx_type, source_nm) - used for stacked charts
  title?: string;
  datasets?: string[]; // Names of datasets for multi-line/clustered charts
  mixed_types?: ("bar" | "line")[]; // For mixed charts - specify type for each dataset
}

@Component({
    selector: "app-scraii-ai",
    templateUrl: "./scraii-ai.component.html",
    styleUrls: ["./scraii-ai.component.scss"],
    standalone: false
})
export class ScraiiAiComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {

  @Input() isFloatingView: boolean = false;
  @Input() isFloatingExpanded: boolean = false;
  @Input() brandKey: number | null = null;
  /** Page context sent to the AI backend (e.g. "patient_list", "pat_therapy", or ""). */
  @Input() pageName: string = '';

  filterOptions: ScraiiBrandOption[] = [
    // { brand_key: 200104, brand_name: "Jupiterol", client_key: 2001, tbl_name_suffix: "jupiterol", time_cycle_string: null },
    // { brand_key: 200105, brand_name: "Cablivi", client_key: 2001, tbl_name_suffix: "cablivi", time_cycle_string: null },
    // { brand_key: 200101, brand_name: "Dupixent", client_key: 2001, tbl_name_suffix: "dupi", time_cycle_string: null },
    // { brand_key: 200110, brand_name: "Hemophilia", client_key: 2001, tbl_name_suffix: "hem", time_cycle_string: null },
    // { brand_key: 200102, brand_name: "Kevzara", client_key: 2001, tbl_name_suffix: "kevzara", time_cycle_string: null },
    // { brand_key: 200999, brand_name: "RBD", client_key: 2001, tbl_name_suffix: "rbd", time_cycle_string: null },
    { brand_key: 200104, brand_name: "Rezurock", client_key: 2001, tbl_name_suffix: "rezu", time_cycle_string: null },
    // { brand_key: 200111, brand_name: "Sarclisa", client_key: 2001, tbl_name_suffix: "sarclisa", time_cycle_string: null },
    // { brand_key: 200112, brand_name: "Tzield", client_key: 2001, tbl_name_suffix: "tzield", time_cycle_string: null },
    // { brand_key: 200116, brand_name: "Wayrilz", client_key: 2001, tbl_name_suffix: "wayrilz", time_cycle_string: null }
  ];

  // TYPING ANIMATION CONFIGURATION
  private readonly ENABLE_TYPING_ANIMATION = true;
  private readonly TYPING_SPEED_MS = 15; // milliseconds per character

  // ASYNC POLLING CONFIGURATION
  private readonly POLL_INTERVAL_MS = 2500;
  private readonly MAX_POLL_ATTEMPTS = 240; // 240 × 2.5s = 10 minutes
  private readonly SUBMIT_TIMEOUT_MS = 60000;
  private readonly STATUS_ROTATION_MS = 3000;
  private readonly SUMMARY_DELAY_CHART_MS = 800;
  private readonly SUMMARY_DELAY_TABLE_MS = 200;
  private readonly SUMMARY_DELAY_DEFAULT_MS = 100;
  private readonly EXECUTE_SQL_TIMEOUT_MS = 60000;
  private readonly AI_MODE_KEY = 'ai-mode';
  // Set when the user explicitly starts a new chat (before it has any messages/history), so a page refresh resumes the blank chat instead of re-loading the last saved conversation. Cleared once a chat is opened or a message is sent.
  private readonly AI_NEW_CHAT_KEY = 'ai-new-chat-pending';
  // conversation_id the user is actively viewing, so a refresh resumes THAT
  // chat (not just whichever conversation happens to be newest by created_at).
  private readonly AI_ACTIVE_CHAT_KEY = 'ai-active-conversation-id';
  // Timestamp of the last unload/chat-open in this tab. sessionStorage alone
  // can't mark a session boundary: a tab opened via window.open/target=_blank
  // INHERITS a copy of the opener's sessionStorage, and browser session
  // restore ("Continue where you left off", Ctrl+Shift+T) replays it verbatim.
  // Both look like a fresh tab while still holding yesterday's active chat, so
  // resume is gated on recency rather than on the key merely existing.
  private readonly AI_ACTIVE_CHAT_TS_KEY = 'ai-active-conversation-ts';
  private readonly RESUME_WINDOW_MS = 30 * 60 * 1000; // ponytail: fixed 30 min; make configurable only if users ask

  // ASYNC JOB STATUS CONSTANTS
  private readonly JOB_STATUS_COMPLETED = 'COMPLETED';
  private readonly JOB_STATUS_FAILED = 'FAILED';

  // Chart colors palette - HEX
  private readonly CHART_COLORS: string[] = [
    "#E74028", "#2CB239", "#25D3EB", "#FFB039", "#1566DE",
    "#7848FF", "#7AD045", "#CB04DC", "#FA195C", "#9E0000",
    "#EACD37", "#DD630B", "#BCD03F", "#14BDA9", "#8A05F3",
    "#D9476A", "#2DADB6", "#E468C2", "#199CD4", "#0934CC",
    "#e74c3c", "#3498db", "#2ecc71", "#f1c40f", "#9b59b6",
    "#1abc9c", "#34495e", "#e67e22", "#16a085", "#d35400",
    "#2980b9", "#8e44ad", "#c0392b", "#27ae60", "#f39c12",
    "#7f8c8d", "#e74c3c", "#3498db", "#2ecc71", "#f1c40f",
    "#9b59b6", "#1abc9c", "#34495e", "#e67e22", "#16a085",
    "#d35400", "#2980b9", "#8e44ad", "#c0392b", "#27ae60",
    "#f39c12", "#7f8c8d", "#e74c3c", "#3498db", "#2ecc71",
    "#f1c40f", "#9b59b6", "#1abc9c", "#34495e", "#e67e22",
    "#16a085", "#d35400", "#2980b9", "#8e44ad", "#c0392b",
    "#27ae60", "#f39c12", "#7f8c8d",
  ];

  private chartSubscription?: Subscription;
  private messageSubscription?: Subscription;
  private autoExecuteSubscription?: Subscription;
  private pollingSubscription: Subscription | null = null;
  private fetchResultSubscription: Subscription | null = null;
  private historySubscription?: Subscription;
  private executeSqlSubscription?: Subscription;
  // Tracks the in-flight getChatMessages() call so it can be cancelled
  // when the user navigates away (new chat, switching chats, destroy).
  private loadChatMessagesSubscription?: Subscription;
  private pollAttemptCount = 0;
  asyncDataParam = 'Y'; // Always 'Y' — requests result/data inclusion. Summary generation is controlled via the summary payload param from summaryInsightEnabled.
  isAutoExecuting = false; // Component-level flag for auto-execute operations

  // AI Insights toggle — controls ONLY whether the summary is displayed for NEW messages.
  // The toggle state is captured onto each message at send time (msg.summaryHidden),
  // so flipping the toggle afterward does NOT retroactively hide/show past summaries.
  summaryInsightEnabled: boolean = false;
  user_team: string = '';
  emp_dim_key: number | null = null;

  toggleSummaryInsight(): void {
    // The pill is disabled (via [disabled] on the checkbox and the
    // .scraii-ai-deep-toggle-disabled class) while a request is in flight —
    // guard the handler as well so programmatic callers and any edge-case
    // label activation can't flip the state mid-operation.
    if (this.isAnyOperationRunning) return;
    this.summaryInsightEnabled = !this.summaryInsightEnabled;
  }

  // Summary typing animation
  private summaryTypingIntervals: Map<number, any> = new Map();

  // Pending summary typing delays (setTimeout handles from processAsyncResponse / generateChartFromData)
  // Tracked so stopCurrentOperation() can cancel them before they fire.
  private pendingSummaryTimeouts: any[] = [];

  // Rotating status messages during polling
  private statusRotationInterval: any = null;
  private opsRevealInterval: any = null;
  private opsRevealTimerIds: number[] = [];
  private statusMessageIndex = 0;
  processingStatusMessage = '';
  private readonly STATUS_MESSAGES: string[] = [
    // Query processing
    'Analyzing your query...',
    'Interpreting your question...',
    'Understanding what you need...',
    'Breaking down your request...',
    'Parsing the intent...',
  
    // SQL generation
    'Generating SQL...',
    'Crafting the perfect query...',
    'Building your data pipeline...',
    'Translating to database language...',
    'Assembling query logic...',
  
    // Data processing
    'Crunching the numbers...',
    'Scanning millions of records...',
    'Sifting through dispense data...',
    'Querying the data warehouse...',
    'Connecting the data dots...',
  
    // Analysis
    'Spotting trends and patterns...',
    'Running the analysis...',
    'Cross-referencing data points...',
    'Comparing across specialty pharmacies...',
    'Evaluating the results...',
  
    // Results preparation
    'Preparing your results...',
    'Formatting the output...',
    'Rendering your visualization...',
    'Putting the finishing touches...',
    'Packaging your insights...',
  
    // Almost done
    'Almost there...',
    'Just a moment more...',
    'Wrapping things up...',
    'Final checks in progress...',
    'Delivering your answer...',
  ];

  private readonly OPS_STATUS_MESSAGES: string[] = [
    'Checking monitor health...',
    'Aggregating KPI metrics...',
    'Reviewing alerts across monitors...',
    'Comparing status across specialty pharmacies...',
    'Building your operations overview...',
    'Almost ready...',
  ];

  private statusMessagePool: string[] = this.STATUS_MESSAGES;
  private lastQuerySource: 'dispense' | 'status' | 'journey' | 'other' = 'other';
  fullScreenTableIndex: number | null = null; // Tracks which per-message table is fullscreen
  fullscreenToastVisible = false;
  private fullscreenToastTimeout: any = null;

  constructor(
    private chatService: AiChatServiceService, private dialogService: DialogService,
    private sessionService: SessionService, private speechService: SpeechService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private dataService: DataService,
  ) {
    // Register Chart.js components
    Chart.register(...registerables);

    // Initialize isRecording$ observable
    this.isRecording$ = this.speechService.isRecording$;
  }

  ngOnInit(): void {
    if (this.isFloatingView) {
      this.clearFullscreenState();
      this.userInput = "";
    }
    // Full-page view: resume the most recent conversation on refresh instead of always starting blank. Floating view keeps its own snapshot-based restore (restoreFloatingChatSnapshot) and doesn't need this. Skip the
    // resume if the user had explicitly started a (still-empty) new chat — that intent should survive a refresh instead of snapping back to the last saved conversation.
    if (!this.isFloatingView && sessionStorage.getItem(this.AI_NEW_CHAT_KEY) !== '1') {
      // Show the loading state (not the blank "new chat" welcome screen) while we check whether there's a conversation to resume.
      this.isLoadingMessages = true;
      this.loadChatHistory(true);
    } else {
      this.generateConversationId();
      this.activeConversationId = this.conversation_id;
    }
    let user: any = sessionStorage.getItem("userInformation");
    user = JSON.parse(user);
    this.userName = user.usr_display_nm;
    this.user_team = user.user_team ?? 'ops';
    this.emp_dim_key = user.employee_key;
    // Multiselect default: all brands selected on first load. UI collapses
    // this to a single "All brands" pill + tick. User can narrow via the
    // dropdown (search + checkboxes) without ever landing on an empty state.
    this.selectedBrands = Array.isArray(this.filterOptions) ? [...this.filterOptions] : [];
    this.initializeSpeechRecognition(); // Initialize speech recognition

    // Default to new executeSql mode; preserve any existing developer override
    if (sessionStorage.getItem(this.AI_MODE_KEY) === null) {
      sessionStorage.setItem(this.AI_MODE_KEY, 'false');
    }
  }

  loadQuerybasedonRole(role: string): void {
    // if role is not ops or admin dont load the data valdation, data standardization, file validation, audit columns
    if (role !== 'ops' && role !== 'admin') {
      this.suggestedQueryCategories = this.suggestedQueryCategories.filter(category => category.id !== 'dv' && category.id !== 'ds' && category.id !== 'fv' && category.id !== 'ac');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['isFloatingExpanded'] &&
      !changes['isFloatingExpanded'].firstChange &&
      this.isFloatingView
    ) {
      this.relayoutFloatingContent();
      setTimeout(() => this.syncActiveTextareaHeight(), 0);
    }
    if (changes['isFloatingView'] && this.isFloatingView) {
      setTimeout(() => this.initializeTextareaHeights(), 0);
    }
  }

  ngAfterViewInit(): void {
    this.initializeTextareaHeights();
  }

  private initializeTextareaHeights(): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.syncActiveTextareaHeight();
      });
    });
  }

  syncActiveTextareaHeight(): void {
    const centered = this.centeredTextarea?.nativeElement;
    const bottom = this.bottomTextarea?.nativeElement;

    if (this.messages.length === 0 && centered) {
      this.adjustTextareaHeight(centered);
    } else if (bottom) {
      this.adjustTextareaHeight(bottom);
    } else if (centered) {
      this.adjustTextareaHeight(centered);
    }
  }

  @ViewChild("chatContainer") private chatContainer!: ElementRef;
  @ViewChild("centeredTextarea")
  private centeredTextarea?: ElementRef<HTMLTextAreaElement>;
  @ViewChild("bottomTextarea")
  private bottomTextarea?: ElementRef<HTMLTextAreaElement>;
  @ViewChildren(ScraiiGanttChartComponent) ganttCharts!: QueryList<ScraiiGanttChartComponent>;

  messages: ChatMessage[] | any = [];
  userInput = "";
  isLoading = false;
  copiedIndex: number | null = null;
  copiedMetricKey: string | null = null; // `<msgIndex>-<metricIndex>` of the last copied metric SQL
  copiedSqlIndex: number | null = null; // Briefly flips to `i` after SQL copy to show a "Copied!" state
  conversation_id: string = ""; // Unique conversation ID
  /** messageId from the latest async invoke — attached to the resulting assistant message */
  private pendingMessageId: string | null = null;

  // Feedback dialog properties
  showFeedbackDialog = false;
  feedbackText = "";
  currentFeedbackIndex: number | null = null;
  currentselectedMessage: string = "";

  // Query execution properties
  showQueryDialog = false;
  queryToExecute = "";
  showQueryResults = false;
  isExecutingQuery = false;
  currentQueryMessageIndex: number | null = null;
  activeResultIndex: number | null = null; // Track which message displays the results
  queryError: string | null = null;

  // Result Table Properties
  queryResults: any[] = [];
  queryResultColumns: string[] = [];
  columnDefs: ColDef[] = [];
  defaultColDef: ColDef = {
    flex: 1,
    minWidth: 100,
    resizable: true,
  };
  /** ag-grid defaults for RAG tables (citations + contract inline). */
  readonly emptyRagGridRows: Record<string, string>[] = [];
  readonly emptyRagGridColumns: ColDef[] = [];
  readonly ragGridDomLayout: 'autoHeight' | 'normal' = 'normal';
  readonly ragGridPageSize = 10;
  ragGridDefaultColDef: ColDef = {
    editable: false,
    sortable: true,
    filter: true,
    resizable: true,
    flex: 1,
    minWidth: 100,
    cellClass: 'scraii-ai-rag-ag-cell',
  };
  private gridApi: any;
  private readonly messageGridApis = new Map<number, any>();
  private readonly ragGridApis = new Map<string, any>();

  // scraii ai history
  oldChats: ChatHistoryItem[] = [];
  filteredOldChats: ChatHistoryItem[] = []; // Filtered list for search

  // Grouped view of filteredOldChats, computed on every list mutation.
  // Template reads this field (NOT a getter) so change detection doesn't
  // recompute groupings during typing animations. Refreshed from exactly
  // four call-sites: loadChatHistory success, addNewConversationToSidebar,
  // filterChats, and (via filterChats) deleteChat and its revert path.
  chatGroups: { label: string, chats: ChatHistoryItem[] }[] = [];

  // Search functionality properties — the search input is now always visible
  // in the redesigned sidebar, so isSearchVisible is no longer needed.
  searchQuery = "";
  isLoadingHistory = false;
  chatHistoryLoaded = false; // Flag to prevent multiple API calls
  showHistoryRefresh = false; // Show retry button when chat history fails to load
  isLoadingMessages = false; // Flag for loading chat messages
  activeConversationId: string = ""; // Track the active conversation
  isHistoricalChat = false; // Flag to indicate loading a historical conversation

  // Chart properties
  isGeneratingChart = false;
  activeChartIndex: number | null = null;
  chartConfig: ChartConfiguration | null = null;
  currentChartTitle: string = "Chart Visualization"; // Dynamic chart title

  // Typing animation properties
  isTyping = false;
  typingMessageIndex: number | null = null;
  private typingInterval: any = null;

  isExpanded = false;
  sendBtnPulse = false; // Briefly true to animate send button after re-run fills textarea
  userName = "";

  // Voice input properties
  isRecording$: Observable<boolean>;
  speechSupported = false;
  private transcriptionSubscription?: Subscription;
  private errorSubscription?: Subscription;
  private initialUserInput = ""; // Store input before voice recording starts

  // Sample queries dropdown properties
  showSampleQueries = false;
  showStatusQueries = false;
  showPatientJourneyQueries = false;

  // Welcome-screen suggestion grid: expand/collapse all queries
  showAllSuggestions = false;

  // Browse Queries popup (shown inside the bottom input bar, messages.length > 0)
  isQueryPopupOpen = false;
  activeQueryTab: 'dispense' | 'status' | 'journey' = 'dispense';

  // Brand filter dropdown state
  showBrandDropdown = false;
  // Multiselect: each entry is an item from filterOptions. Default is "all
  // selected", which the UI represents as the single pill "All brands" + tick.
  selectedBrands: ScraiiBrandOption[] = [];
  // Local search string for filtering the dropdown list. Case-insensitive,
  // matches substring of brand_name. Not persisted, resets when not needed.
  brandSearchQuery: string = '';
  suggestedQueryCategories: SuggestedQueryCategory[] = [
    {
      id: 'dispense',
      label: 'Dispense',
      badgeClass: 'scraii-ai-badge-dispense',
      includeInBrowsePopup: true,
      queries: [
        "Plot a bar chart showing the monthly dispense volume in 2025 of top 1 Sps by MMM-YYYY",
        "show me the dispense for the past one year on month over month by payer type",
        "How many dispense records in 2025 have a Referral Source listed as 'Direct'?",
        "Show an area chart with monthly dispense volume trends for the top 3 SPs in 2025, with months formatted as MMM-YYYY on the x-axis.",
        "Show the total dispense count by NDC for each month over the past 12 months, displayed as a table.",
        "How many patients received two consecutive shipments from Biologics within a gap of 15 days or less in 2025?",
      ],
    },
    {
      id: 'status',
      label: 'Status',
      badgeClass: 'scraii-ai-badge-status',
      includeInBrowsePopup: true,
      queries: [
        "Show me the number of status transactions month over month by sp where the status code is CS07 for the year 2025, presented as a bar chart.",
        "How many patients are currently in a Pending status at ONCO360 for more than 10 days without a status update?",
        "How many unique patients had a Cancelled status  in the Hub transactions in 2025?",
        "How many patients at hub have received copay denied status",
        "What is the count of patients whose Prior Authorization was denied?",
      ],
    },
    {
      id: 'journey',
      label: 'Journey',
      badgeClass: 'scraii-ai-badge-journey',
      includeInBrowsePopup: true,
      queries: [
        "I want to see the patient journey over time for the patient id = 10000848 ",
        "Give the list of patients who have been from hub to commercial to non commercial",
        "Retrieve all transactions with a pending status for patient ID 10000852",
      ],
    },
    // {
    //   id: 'dv',
    //   label: 'Data Validation',
    //   badgeClass: 'scraii-ai-badge-dv',
    //   queries: [
    //     "Is the Referral Number a required field in the Dispense file? What happens if it's missing?",
    //     "What date formats are accepted for Ship Date? Will the record get rejected if the format is wrong?",
    //     "What are the allowed values for Patient Gender in the Dispense file? Does the system automatically clean them up?",
    //     "If an SP Patient ID fails validation in Enhanced Services, does the entire record get rejected or just flagged?",
    //     "Are there any fields in the Status file that get cross-checked against each other?",
    //   ],
    // },
    // {
    //   id: 'ds',
    //   label: 'Data Standardization',
    //   badgeClass: 'scraii-ai-badge-ds',
    //   queries: [
    //     "What automatic cleanup happens to the Referral Number before it's validated?",
    //     "Which fields in the Dispense file get encrypted for patient privacy?",
    //     "How does the system standardize payer type values? We're seeing different abbreviations from the SP — does it normalize them?",
    //     "We noticed the Inventory file has a date field — does the system handle two-digit year corrections automatically?",
    //     "What transformations happen to data in the Enhanced Services file before it gets loaded?",
    //   ],
    // },
    // {
    //   id: 'fv',
    //   label: 'File Validation',
    //   badgeClass: 'scraii-ai-badge-fv',
    //   queries: [
    //     "How many columns should the Dispense file have? What happens if we receive a file with extra or missing columns?",
    //     "Does the system check the file header before processing? How does it catch corrupted or truncated files?",
    //     "What validations run on the Enhanced Services file before any data processing starts?",
    //     "We received a file that was partially truncated — would the system catch that automatically?",
    //     "What delimiter are our SP files expected to use? Does it differ between file types?",
    //   ],
    // },
    // {
    //   id: 'ac',
    //   label: 'Audit Columns',
    //   badgeClass: 'scraii-ai-badge-ac',
    //   queries: [
    //     "What tracking fields does the system automatically add to Dispense records after processing?",
    //     "How does the system determine the 'status' value on processed Dispense records? We're seeing codes like AB01 and AI01.",
    //     "Is there a quantity UOM standardization that happens automatically? Where does it look up the standard unit of measure?",
    //     "What system-generated columns get added to Enhanced Services records for traceability?",
    //     "How does the system assign the ds_key on processed records? Is it a lookup or a hardcoded value?",
    //     "What audit columns are configured for the Enhanced Services file group and what are their derivation types?",
    //   ],
    // },
  ];

  floatingExpandedCategoryId: string | null = null;

  get totalSuggestedQueryCount(): number {
    return this.suggestedQueryCategories.reduce((sum, category) => sum + category.queries.length, 0);
  }

  /** Hide suggested-question welcome card on page-specific patient contexts. */
  get showFloatingWelcomeCard(): boolean {
    return (
      this.isFloatingView &&
      this.pageName !== 'patient_list' &&
      this.pageName !== 'pat_therapy'
    );
  }

  toggleFloatingCategory(categoryId: string): void {
    this.floatingExpandedCategoryId =
      this.floatingExpandedCategoryId === categoryId ? null : categoryId;
  }

  isFloatingCategoryExpanded(categoryId: string): boolean {
    return this.floatingExpandedCategoryId === categoryId;
  }
  private clickOutsideHandler = (event: Event) => this.onClickOutside(event);


  // Sample queries dropdown methods
  toggleSampleQueries(event: Event): void {
    event.stopPropagation();
    this.showSampleQueries = !this.showSampleQueries;
    if (this.showSampleQueries) {
      this.showStatusQueries = false; // Close other dropdown
      this.showPatientJourneyQueries = false; // Close other dropdown
      // Add click-outside listener with a slight delay to avoid immediate close
      setTimeout(() => {
        document.addEventListener('click', this.clickOutsideHandler);
      });
      // Scroll to bottom when opening Dispense Queries
      this.scrollToBottom();
    } else {
      document.removeEventListener('click', this.clickOutsideHandler);
    }
  }

  toggleStatusQueries(event: Event): void {
    event.stopPropagation();
    this.showStatusQueries = !this.showStatusQueries;
    if (this.showStatusQueries) {
      this.showSampleQueries = false; // Close other dropdown
      this.showPatientJourneyQueries = false; // Close other dropdown
      setTimeout(() => {
        document.addEventListener('click', this.clickOutsideHandler);
      });
      this.scrollToBottom();
    } else {
      document.removeEventListener('click', this.clickOutsideHandler);
    }
  }

  closeSampleQueries(): void {
    this.showSampleQueries = false;
    this.showStatusQueries = false;
    this.showPatientJourneyQueries = false;
    document.removeEventListener('click', this.clickOutsideHandler);
  }

  selectSampleQuery(query: string, sourceCategory?: string): void {
    if (sourceCategory) {
      this.lastQuerySource = this.normalizeQuerySource(sourceCategory);
    } else {
      const category = this.suggestedQueryCategories.find(c => c.queries.includes(query));
      this.lastQuerySource = this.normalizeQuerySource(category?.id);
    }
    this.userInput = query;
    // this.userInput != '' ? this.sendChatbotMessage() : '';
    this.closeSampleQueries();

    // Determine which textarea to focus and resize
    const textarea = this.messages.length === 0
      ? this.centeredTextarea?.nativeElement
      : this.bottomTextarea?.nativeElement;

    if (textarea) {
      // Small delay to ensure ngModel has updated the value in the DOM
      setTimeout(() => {
        this.adjustTextareaHeight(textarea);
        textarea.focus();
        // Position cursor at the end of the text
        const length = textarea.value.length;
        textarea.setSelectionRange(length, length);
      });
    }
  }

  private onClickOutside(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.scraii-ai-sample-queries-wrapper')) {
      this.closeSampleQueries();
    }
    if (!target.closest('.scraii-ai-brand-dropdown-wrapper')) {
      this.closeBrandDropdown();
    }
    if (!target.closest('.scraii-ai-query-popup-wrapper')) {
      this.closeQueryPopup();
    }
  }

  // ============ Browse Queries Popup ============
  // Toggle the popup shown inside the bottom input bar. Reuses browse-enabled
  // categories from suggestedQueryCategories as the single data source.
  toggleQueryPopup(event: Event): void {
    event.stopPropagation();
    if (this.isQueryPopupOpen) {
      this.closeQueryPopup();
      return;
    }
    this.isQueryPopupOpen = true;
    this.activeQueryTab = 'dispense';
    // Register the shared click-outside handler — it also drives the
    // welcome-screen dropdowns and the brand selector.
    setTimeout(() => {
      document.addEventListener('click', this.clickOutsideHandler);
    });
  }

  closeQueryPopup(): void {
    if (!this.isQueryPopupOpen) return;
    this.isQueryPopupOpen = false;
    // Only detach if none of the other popups need the handler.
    if (!this.showSampleQueries && !this.showStatusQueries && !this.showBrandDropdown) {
      document.removeEventListener('click', this.clickOutsideHandler);
    }
  }

  switchQueryTab(tab: 'dispense' | 'status' | 'journey'): void {
    this.activeQueryTab = tab;
  }

  getQueriesForTab(): string[] {
    const category = this.suggestedQueryCategories.find(c => c.id === this.activeQueryTab);
    return category?.queries ? [...category.queries] : [];
  }

  // Picked from the popup — reuse the existing selectSampleQuery flow so the
  // textarea focus / height logic stays centralised.
  selectQueryFromPopup(query: string): void {
    this.lastQuerySource = this.activeQueryTab;
    this.closeQueryPopup();
    this.selectSampleQuery(query, this.activeQueryTab);
  }

  // Handle Escape key: close popup first (higher priority), then exit fullscreen.
  @HostListener('document:keydown.escape')
  onDocumentEscape(): void {
    if (this.isQueryPopupOpen) {
      this.closeQueryPopup();
      return;
    }
    this.exitAnyFullScreen();
  }

  // Brand filter dropdown methods
  toggleBrandDropdown(event: Event): void {
    event.stopPropagation();
    this.showBrandDropdown = !this.showBrandDropdown;
    if (this.showBrandDropdown) {
      // Close other dropdowns
      this.showSampleQueries = false;
      this.showStatusQueries = false;
      setTimeout(() => {
        document.addEventListener('click', this.clickOutsideHandler);
      });
    } else {
      document.removeEventListener('click', this.clickOutsideHandler);
    }
  }

  closeBrandDropdown(): void {
    this.showBrandDropdown = false;
    // Reset the search so the next open starts fresh. Keeping the selection
    // across opens is intentional — only the search string is ephemeral.
    this.brandSearchQuery = '';
    document.removeEventListener('click', this.clickOutsideHandler);
  }

  // private resolveActiveBrandKey(): number | null {
  //   if (!this.isFloatingView) {
  //     return null;
  //   }
  //   return this.brandKey;
  // }

  private resolveActiveBrandKey(): number | null {
    if (this.isFloatingView) {
      return this.brandKey;
    }
    // Full-page AI: derive from the in-component brand multiselect.
    // API accepts a single brand_key — send it when exactly one brand is selected.
    if (this.selectedBrands.length === 1) {
      return this.selectedBrands[0].brand_key;
    }
    return null;
  }

  exportFloatingChatSnapshot(): FloatingChatSnapshot {
    const messages = this.messages.map((msg: ChatMessage) => this.sanitizeMessageForSnapshot(msg));

    return {
      messages: JSON.parse(JSON.stringify(messages)),
      conversation_id: this.conversation_id,
      activeConversationId: this.activeConversationId,
      userInput: this.userInput,
      isHistoricalChat: this.isHistoricalChat,
    };
  }

  private sanitizeMessageForSnapshot(msg: ChatMessage): ChatMessage {
    const { chartInstance, ...rest } = msg;
    return {
      ...rest,
      isGenerating: false,
      isLoadingTable: false,
      isSummaryTyping: false,
      isLoadingOpsOverview: false,
    };
  }

  restoreFloatingChatSnapshot(snapshot: FloatingChatSnapshot): void {
    if (!this.isFloatingView) {
      return;
    }

    this.resetAllOperationState();
    this.destroyAllCharts();

    this.conversation_id = snapshot.conversation_id;
    this.activeConversationId = snapshot.activeConversationId;
    this.userInput = snapshot.userInput;
    this.isHistoricalChat = snapshot.isHistoricalChat;
    this.messages = JSON.parse(JSON.stringify(snapshot.messages));
    this.activeChartIndex = null;
    this.chartConfig = null;
    this.currentChartTitle = 'Chart Visualization';
    this.fullScreenTableIndex = null;
    this.closeQueryResults();

    this.cdr.detectChanges();

    setTimeout(() => {
      this.messages.forEach((msg: ChatMessage, index: number) => {
        if (msg.chartConfig && msg.resultData?.length) {
          this.activeChartIndex = index;
          this.renderChart(msg.chartConfig as any, msg.resultData);
        }
      });
      this.relayoutFloatingContent();
      this.syncActiveTextareaHeight();
    }, 100);
  }

  // ── Multiselect brand filter helpers ─────────────────────────────────────
  // Membership check by brand_key (brand objects themselves are not
  // reference-stable when filterOptions is mutated elsewhere).
  isBrandSelected(brand: any): boolean {
    if (!brand) return false;
    return this.selectedBrands.some(b => b.brand_key === brand.brand_key);
  }

  // True when every option in filterOptions has a match in selectedBrands —
  // drives the "All brands" pill label + tick on the trigger button.
  get isAllBrandsSelected(): boolean {
    if (!Array.isArray(this.filterOptions) || this.filterOptions.length === 0) return false;
    return this.selectedBrands.length === this.filterOptions.length;
  }

  // Case-insensitive substring filter on brand_name; returns the full list
  // when the query is blank. Kept as a getter (small list, cheap to recompute).
  get filteredBrands(): any[] {
    const q = (this.brandSearchQuery || '').toLowerCase().trim();
    if (!q) return this.filterOptions || [];
    return (this.filterOptions || []).filter(
      (b: any) => (b.brand_name || '').toLowerCase().includes(q)
    );
  }

  // Trigger-button label:
  //   all selected → "All brands"
  //   none         → "No brands selected"   (transient — toggle-all restores)
  //   one          → the brand name
  //   many         → "<first> +<rest>"      (keeps the pill compact)
  get selectedBrandsLabel(): string {
    const n = this.selectedBrands.length;
    if (n === 0) return 'No brands selected';
    // if (this.isAllBrandsSelected) return 'All brands';
    if (n === 1) return this.selectedBrands[0].brand_name;
    return `${this.selectedBrands[0].brand_name} +${n - 1}`;
  }

  // Toggle a single brand in/out of the selection. Does NOT close the
  // dropdown — multi-select flow lets the user pick several before closing.
  toggleBrandSelection(brand: any, event?: Event): void {
    if (event) event.stopPropagation();
    if (!brand) return;
    const idx = this.selectedBrands.findIndex(b => b.brand_key === brand.brand_key);
    if (idx >= 0) {
      this.selectedBrands.splice(idx, 1);
    } else {
      this.selectedBrands.push(brand);
    }
  }

  // "All brands" row handler: if everything is already selected, clear;
  // otherwise, select everything. Using a fresh array avoids sharing the
  // filterOptions reference with selectedBrands.
  toggleAllBrands(event?: Event): void {
    if (event) event.stopPropagation();
    this.selectedBrands = this.isAllBrandsSelected
      ? []
      : [...(this.filterOptions || [])];
  }

  toggleAiChats() {
    this.isExpanded = !this.isExpanded;
    // Load chat history when sidebar is opened for the first time
    if (this.isExpanded && !this.chatHistoryLoaded) {
      this.loadChatHistory();
    }
  }

  // Generate a unique conversation ID using UUID
  generateConversationId(): void {
    this.conversation_id = crypto.randomUUID();
  }

  // Stamp "this tab was alive here" — on unload (so a reload counts as recent)
  // and whenever the active chat changes. Closing a tab also fires this, but
  // an inherited/restored sessionStorage then carries THAT moment's timestamp,
  // which has aged past the window by the time the user comes back.
  @HostListener('window:beforeunload')
  markSessionActivity(): void {
    sessionStorage.setItem(this.AI_ACTIVE_CHAT_TS_KEY, String(Date.now()));
  }

  // True only if this tab was active within RESUME_WINDOW_MS.
  private hasRecentSession(): boolean {
    const ts = Number(sessionStorage.getItem(this.AI_ACTIVE_CHAT_TS_KEY));
    return ts > 0 && (Date.now() - ts) < this.RESUME_WINDOW_MS;
  }

  startNewChat() {
    // Close any transient popups before resetting conversation state.
    this.closeQueryPopup();
    // add scroll to top
    this.chatContainer?.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });

    // Stop any ongoing operations
    // Bug 2: cancel any in-flight getChatMessages load so its late response
    // cannot overwrite the fresh new-chat state.
    if (this.loadChatMessagesSubscription) {
      this.loadChatMessagesSubscription.unsubscribe();
      this.loadChatMessagesSubscription = undefined;
    }
    this.isLoadingMessages = false;

    if (this.messageSubscription) {
      this.messageSubscription.unsubscribe();
    }
    if (this.chartSubscription) {
      this.chartSubscription.unsubscribe();
    }
    if (this.autoExecuteSubscription) {
      this.autoExecuteSubscription.unsubscribe();
      this.autoExecuteSubscription = undefined;
    }
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
      this.pollAttemptCount = 0;
    }
    if (this.fetchResultSubscription) {
      this.fetchResultSubscription.unsubscribe();
      this.fetchResultSubscription = null;
    }
    if (this.typingInterval) {
      clearInterval(this.typingInterval);
      this.typingInterval = null;
    }
    this.stopStatusRotation();
    this.summaryTypingIntervals.forEach(interval => clearInterval(interval));
    this.summaryTypingIntervals.clear();

    // Reset all operation states
    this.isLoading = false;
    this.isTyping = false;
    this.isGeneratingChart = false;
    this.isExecutingQuery = false;
    this.isAutoExecuting = false;
    this.typingMessageIndex = null;
    this.userInput = ""; // Clear user input
    this.resetTextareaHeight();  // Reset textarea height
    this.isHistoricalChat = false; // Reset historical chat flag

    // Bug 3: these resets must run unconditionally. If a load is in-flight
    // when New Chat is clicked, messages is temporarily [] and the old
    // `if (this.messages.length > 0)` guard would skip conversation_id
    // regeneration — leaving the component pointing at the chat being loaded.
    this.generateConversationId();
    this.activeConversationId = "";
    sessionStorage.setItem(this.AI_NEW_CHAT_KEY, '1');
    this.pendingMessageId = null;
    this.closeQueryResults();
    this.activeChartIndex = null;
    this.chartConfig = null;
    this.currentChartTitle = "Chart Visualization";
    this.fullScreenTableIndex = null;

    // Chart instances and the messages array only need cleanup when
    // there were actually messages rendered.
    if (this.messages.length > 0) {
      this.destroyAllCharts();
      this.messages = [];
    }

    setTimeout(() => this.initializeTextareaHeights(), 0);

    // Bug 6: previously a dead boolean expression. If the sidebar is
    // expanded and history hasn't loaded yet, trigger the load now.
    if (this.isExpanded && !this.chatHistoryLoaded) {
      this.loadChatHistory();
    }
  }

  // Add a new conversation to the sidebar
  addNewConversationToSidebar(firstMessage: string) {
    // This chat now has a real message and is persisted server-side, so a refresh should resume it like any other saved conversation.
    sessionStorage.removeItem(this.AI_NEW_CHAT_KEY);
    sessionStorage.setItem(this.AI_ACTIVE_CHAT_KEY, this.conversation_id);
    this.markSessionActivity();

    // Deduplicate: if loadChatHistory() already returned this conversation, skip the insert
    const alreadyExists = this.oldChats.some(
      (chat) => chat.conversation_id === this.conversation_id
    );
    if (alreadyExists) {
      this.activeConversationId = this.conversation_id;
      return;
    }

    // Create a conversation name from the first message (truncate if too long)
    const conversationName = firstMessage.length > 50 ? firstMessage.substring(0, 50) + "..." : firstMessage;

    // Create new chat history item
    const newChat: ChatHistoryItem = {
      conversation_id: this.conversation_id,
      name: conversationName,
      created_at: new Date().toISOString(),
    };

    // Prepend via spread so both arrays become new references — keeps the
    // grouping recomputation cheap and keeps downstream OnPush-friendly code
    // (if any is added later) working correctly.
    this.oldChats = [newChat, ...this.oldChats];
    this.filteredOldChats = [newChat, ...this.filteredOldChats];
    this.refreshChatGroups();
    // Set as active conversation
    this.activeConversationId = this.conversation_id;
  }

  // Load chat history from API.
  // Exhaust-map semantics: if a history load is already in flight, let it finish —
  // do NOT cancel it on re-entry (rapid sidebar toggles, loadChatMessages re-entry, etc.).
  // No artificial RxJS timeout: rely on HttpClient/server natural timeouts so slow
  // backends can still complete. On failure, any previously loaded chats are kept
  // as a stale cache (Bug 3 graceful degradation) and the refresh icon is shown.
  // This subscription is intentionally independent of stopCurrentOperation().
  loadChatHistory(resumeLastChat = false) {
    // Guard against re-entry while a request is in flight.
    if (this.isLoadingHistory) {
      return;
    }

    this.isLoadingHistory = true;
    this.showHistoryRefresh = false;
    const user_id = this.sessionService.getUsername();
    this.historySubscription = this.chatService.loadChatHistory(user_id)
      .subscribe({
        next: (response) => {
          // Sort conversations by created_at in descending order (newest first)
          response.conversations.sort(
            (a: ChatHistoryItem, b: ChatHistoryItem) => {
              const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
              const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
              return dateB - dateA;
            }
          );
          this.oldChats = response.conversations;
          // Initialize filtered list
          this.filteredOldChats = [...this.oldChats];
          this.refreshChatGroups();
          this.isLoadingHistory = false;
          this.chatHistoryLoaded = true;
          this.showHistoryRefresh = false;

          if (resumeLastChat && this.hasRecentSession() && this.oldChats.length > 0) {
            // Resume the conversation the user was actually viewing, not
            // just whichever one is newest by created_at. The oldChats[0]
            // fallback stays: a conversation started moments ago may not be
            // in the server's history list yet, and it is still the right
            // chat to resume. hasRecentSession() is what stops that fallback
            // from reopening yesterday's chat on a cold start.
            const lastActiveId = sessionStorage.getItem(this.AI_ACTIVE_CHAT_KEY);
            const chatToResume = (lastActiveId && this.oldChats.find(c => c.conversation_id === lastActiveId))
              || this.oldChats[0];
            this.loadChat(chatToResume);
          } else if (resumeLastChat) {
            this.generateConversationId();
            this.activeConversationId = this.conversation_id;
            this.isLoadingMessages = false; // nothing to resume, drop to the welcome screen
          }
        },
        error: (err) => {
          this.isLoadingHistory = false;
          this.showHistoryRefresh = true;
          // Do NOT clear oldChats/filteredOldChats — keep stale cache visible.
          // chatGroups also stays frozen at its last good value for the same
          // reason (user keeps seeing their previous chats until they retry).
          this.dialogService.openSnackBar(this.sanitizeApiError(err, 'fetch'), "OK");
          console.error("loadChatHistory error:", err);

          if (resumeLastChat) {
            this.generateConversationId();
            this.activeConversationId = this.conversation_id;
            this.isLoadingMessages = false; // resume failed, drop to the welcome screen
          }
        },
      });
  }

  // ── Chat-history grouping + timestamp helpers ───────────────────────────
  // Groups filteredOldChats into Today / Yesterday / Earlier, preserving
  // existing recency order inside each bucket. Invalid / missing timestamps
  // fall to Earlier so we never drop a chat from the list.
  private buildChatGroups(chats: ChatHistoryItem[]): { label: string, chats: ChatHistoryItem[] }[] {
    if (!chats || chats.length === 0) return [];

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfYesterday = startOfToday - 86400000;

    const today: ChatHistoryItem[] = [];
    const yesterday: ChatHistoryItem[] = [];
    const earlier: ChatHistoryItem[] = [];

    for (const chat of chats) {
      const t = this.parseTimestamp(chat.created_at);
      if (t == null) { earlier.push(chat); continue; }
      if (t >= startOfToday) today.push(chat);
      else if (t >= startOfYesterday) yesterday.push(chat);
      else earlier.push(chat);
    }

    const groups: { label: string, chats: ChatHistoryItem[] }[] = [];
    if (today.length) groups.push({ label: 'Today', chats: today });
    if (yesterday.length) groups.push({ label: 'Yesterday', chats: yesterday });
    if (earlier.length) groups.push({ label: 'Earlier', chats: earlier });
    return groups;
  }

  // Single entry point for recomputing groups after any mutation to
  // filteredOldChats. Safe to call liberally — buildChatGroups is O(n).
  private refreshChatGroups(): void {
    this.chatGroups = this.buildChatGroups(this.filteredOldChats);
  }

  private parseTimestamp(dateStr?: string): number | null {
    if (!dateStr) return null;
    const t = new Date(dateStr).getTime();
    return isNaN(t) ? null : t;
  }

  // Compact, locale-aware timestamp shown on each chat item:
  //   Today / Yesterday  → "3:45 PM" (12-hour en-US, 24-hour elsewhere)
  //   Same year          → "Nov 15"
  //   Older              → "Nov 15, 2024"
  // The group label already conveys Today / Yesterday context, so showing
  // the time alone inside those groups avoids redundancy.
  formatChatTimestamp(dateStr?: string): string {
    const t = this.parseTimestamp(dateStr);
    if (t == null) return '';
    const date = new Date(t);
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfYesterday = startOfToday - 86400000;

    if (t >= startOfYesterday) {
      return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }
    return date.getFullYear() === now.getFullYear()
      ? date.toLocaleDateString([], { month: 'short', day: 'numeric' })
      : date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  }

  // Full locale date-time used as the hover tooltip — gives users the
  // precise moment without bloating the inline timestamp.
  formatChatTimestampFull(dateStr?: string): string {
    const t = this.parseTimestamp(dateStr);
    return t == null ? '' : new Date(t).toLocaleString();
  }

  // trackBy for the grouped *ngFor — labels are stable across renders.
  trackGroupByLabel = (_: number, g: { label: string }): string => g.label;
  // trackBy for the inner *ngFor — conversation_id is unique and stable.
  trackChatByConversationId = (_: number, c: ChatHistoryItem): string => c.conversation_id;

  // Filter chat history based on search query. Refreshes the grouped view
  // as the single post-condition so every caller (search input, deleteChat,
  // delete-revert) gets consistent groupings without extra plumbing.
  filterChats() {
    if (!this.searchQuery.trim()) {
      this.filteredOldChats = [...this.oldChats];
    } else {
      const query = this.searchQuery.toLowerCase();
      this.filteredOldChats = this.oldChats.filter((chat) =>
        chat.name.toLowerCase().includes(query)
      );
    }
    this.refreshChatGroups();
  }

  clearSearch() {
    this.searchQuery = "";
    this.filterChats();
  }

  // Helper method for delete popup size
  sizeOftheDeletePopup(): string {
    if (window.innerWidth > 768) {
      return '400px';
    } else {
      return '90vw';
    }
  }

  // Delete a chat conversation
  deleteChat(conversationId: string): void {
    const dialogRef = this.dialog.open(PopupDeleteComponent, {
      width: this.sizeOftheDeletePopup(),
      maxWidth: '90vw',
      disableClose: true,
      panelClass: "du-custom-modalbox",
      data: 'Chat'
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (!result || result !== '1') {
        return; // User cancelled
      }

      // 1. Capture the item to delete for potential restore
      const chatToDelete = this.oldChats.find(c => c.conversation_id === conversationId);
      if (!chatToDelete) return;

      const previousActiveId = this.activeConversationId;
      const wasActive = this.activeConversationId === conversationId;

      // 2. Optimistic Update: Remove from UI immediately
      this.oldChats = this.oldChats.filter((chat) => chat.conversation_id !== conversationId);
      this.filterChats(); // Update filtered list

      // If deleted chat was active, switch to new chat immediately
      if (wasActive) {
        this.startNewChat();
      }

      // 3. API Call in Background
      this.chatService.deleteConversation(conversationId).pipe(
        take(1),
        timeout(10000), // 10 second timeout
        catchError((error) => {
          console.error('Delete conversation failed:', error);

          // 4. Revert on Failure
          // Add the item back and resort
          this.oldChats = [...this.oldChats, chatToDelete].sort((a, b) => {
            const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
            const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
            return dateB - dateA;
          });
          this.filterChats(); // Update filtered list

          // Restore active state if needed (and if user hasn't started new chat)
          if (wasActive && this.messages.length === 0) {
            this.activeConversationId = previousActiveId;
          }

          const errorMessage = error.name === 'TimeoutError'
            ? 'Delete timed out. Restoring chat.'
            : 'Failed to delete. Restoring chat.';
          this.dialogService.openSnackBar(errorMessage, 'OK');
          return of(null);
        })
      ).subscribe((response) => {
        if (response) {
          // Success - state already updated
          const remainingChats = this.oldChats.length;
          const message = remainingChats === 0
            ? 'Conversation deleted. Start a new conversation below.'
            : 'Conversation deleted successfully';
          this.dialogService.openSnackBar(message, 'OK');
        }
      });
    });
  }

  // Load messages for a specific chat conversation
  loadChatMessages(conversation_id: string, user_id: string) {
    // Cancel any prior in-flight load so the late response can't overwrite
    // a newer chat's state (Bug 1).
    if (this.loadChatMessagesSubscription) {
      this.loadChatMessagesSubscription.unsubscribe();
      this.loadChatMessagesSubscription = undefined;
    }

    this.isLoadingMessages = true;

    // IMPORTANT: Destroy all chart instances from previous conversation
    this.destroyAllCharts();

    // Reset chart state when switching conversations
    this.activeChartIndex = null;
    this.chartConfig = null;
    this.currentChartTitle = "Chart Visualization";
    this.closeQueryResults();

    // Capture the requested conversation id so the response can be discarded
    // if the user navigated away (Bug 4 — belt-and-suspenders staleness guard).
    const requestedConversationId = conversation_id;

    this.loadChatMessagesSubscription = this.chatService.getChatMessages(conversation_id, user_id).subscribe({
      next: (response) => {
        // Staleness guard: user has moved to a different chat / new chat.
        if (this.conversation_id !== requestedConversationId) {
          return;
        }

        // Handle the API response format
        if (response && response.messages && Array.isArray(response.messages)) {
          // Update the conversation_id from API response to ensure continuity
          if (response.conversation_id) {
            this.conversation_id = response.conversation_id;

            // Only reload chat history if not already loaded
            if (!this.chatHistoryLoaded) {
              this.loadChatHistory();
            }
          }

          this.messages = response.messages.map((msg: any) => {
            let content = msg.text;
            let chartConfig: any = undefined;

            // Check if assistant message contains chart configuration
            if (msg.role === "assistant" && !msg.needs_clarification) {
              try { // Try to parse the text as JSON
                // Handle case where text might be already an object or a string
                const potentialJson = typeof msg.text === 'string' ? JSON.parse(msg.text) : msg.text;

                // Check if it looks like a chart config
                if (potentialJson && potentialJson.chart_type && potentialJson.sql) {
                  // It's a chart configuration
                  chartConfig = {
                    chart_type: potentialJson.chart_type,
                    chart_variant: potentialJson.chart_variant || 'simple',
                    x_axis: potentialJson.x_axis,
                    y_axis: potentialJson.y_axis,
                    title: potentialJson.title,
                    sql_query: potentialJson.sql
                  };

                  // Set content to just the SQL part for display
                  content = `🧠 SQL Generated:\n${potentialJson.sql}`;
                } else {
                  // Not a chart config, treat as normal SQL/text
                  content = `🧠 SQL Generated:\n${msg.text}`;
                }
              } catch (e) {
                // Not JSON, treat as normal text/SQL
                content = `🧠 SQL Generated:\n${msg.text}`;
              }
            } else if (msg.role === "assistant" && msg.needs_clarification) {
              content = `${msg.text}❓`;
            }

            return {
              role: msg.role,
              content: content,
              needs_clarification: msg.needs_clarification,
              feedback: msg.feedback,
              chartConfig: chartConfig,
              isHistorical: true,
              messageId: msg.message_id || msg.messageId || undefined,
              // Historical responses do not preserve raw row data, so the
              // toggle bar won't render (it's gated inside *ngIf="!msg.isHistorical"
              // and also requires resultData.length > 0). We still default
              // activeView for consistency with fresh chart messages.
              activeView: chartConfig ? 'chart' : undefined
            };
          });

          // Pair each assistant message with its preceding user prompt (for re-run)
          for (let i = 0; i < this.messages.length; i++) {
            if (this.messages[i].role === 'assistant' && i > 0) {
              for (let j = i - 1; j >= 0; j--) {
                if (this.messages[j].role === 'user') {
                  this.messages[i].originalPrompt = this.messages[j].content;
                  break;
                }
              }
            }
          }
        } else {
          this.messages = [];
        }

        this.isLoadingMessages = false;

        // On old-chat load: scroll to the TOP of the last message so the user
        // sees the full last exchange starting from its header — not the
        // absolute bottom of the container (which would land them at the
        // footer/timing chip of the last assistant reply).
        // scrollToSummary walks the anchor fallback chain:
        //   summary → chart container → inline results → message root
        // so historical messages (which show the re-run card and no summary)
        // still land on the message root. Works uniformly for every message
        // shape without the caller having to know the content type.
        if (this.messages.length > 0) {
          const lastIndex = this.messages.length - 1;
          setTimeout(() => this.scrollToSummary(lastIndex, 'top'), 100);
        } else {
          this.scrollToBottom();
        }
      },
      error: (err) => {
        // Staleness guard: drop errors for a conversation the user left (Bug 4).
        if (this.conversation_id !== requestedConversationId) {
          return;
        }
        this.isLoadingMessages = false;
        this.messages = [];
        this.dialogService.openSnackBar(this.sanitizeApiError(err, 'fetch'), "OK");
      },
    });
  }

  loadChat(chat: ChatHistoryItem) {
    // Close any transient popups before switching chats.
    this.closeQueryPopup();

    // Cancel any in-progress operations before switching chats
    if (this.loadChatMessagesSubscription) {
      this.loadChatMessagesSubscription.unsubscribe();
      this.loadChatMessagesSubscription = undefined;
    }
    if (this.messageSubscription) {
      this.messageSubscription.unsubscribe();
      this.messageSubscription = undefined;
    }
    if (this.chartSubscription) {
      this.chartSubscription.unsubscribe();
      this.chartSubscription = undefined;
    }
    if (this.autoExecuteSubscription) {
      this.autoExecuteSubscription.unsubscribe();
      this.autoExecuteSubscription = undefined;
    }
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
      this.pollAttemptCount = 0;
    }
    if (this.fetchResultSubscription) {
      this.fetchResultSubscription.unsubscribe();
      this.fetchResultSubscription = null;
    }
    if (this.typingInterval) {
      clearInterval(this.typingInterval);
      this.typingInterval = null;
    }
    this.stopStatusRotation();
    this.summaryTypingIntervals.forEach(interval => clearInterval(interval));
    this.summaryTypingIntervals.clear();

    // Reset all operation states
    this.isLoading = false;
    this.isTyping = false;
    this.isGeneratingChart = false;
    this.isExecutingQuery = false;
    this.isAutoExecuting = false;
    this.typingMessageIndex = null;

    this.isHistoricalChat = true; // Mark as historical chat
    this.messages = [];
    this.pendingMessageId = null;
    this.userInput = ""; // Clear user input
    this.conversation_id = chat.conversation_id;
    this.activeConversationId = chat.conversation_id; // Set as active conversation
    sessionStorage.removeItem(this.AI_NEW_CHAT_KEY);
    sessionStorage.setItem(this.AI_ACTIVE_CHAT_KEY, chat.conversation_id);
    this.markSessionActivity();
    const user_id = this.sessionService.getUsername(); // Get user_id from session
    this.loadChatMessages(chat.conversation_id, user_id); // Load messages for this conversation
    // Close sidebar on mobile/smaller screens (optional)
    // this.isExpanded = false;
  }

  // Re-run a historical message by directly calling the data API (no AI API call)
  // Uses the stored SQL/chartConfig from the old chat to render charts or tables
  reRunPrompt(messageIndex: number): void {
    const message = this.messages[messageIndex];
    if (!message) return;

    const originalPrompt = message.originalPrompt;
    this.userInput = originalPrompt;


    // Determine which textarea to focus and resize
    const textarea = this.messages.length === 0
      ? this.centeredTextarea?.nativeElement
      : this.bottomTextarea?.nativeElement;

    if (textarea) {
      // Small delay to ensure ngModel has updated the value in the DOM
      setTimeout(() => {
        this.adjustTextareaHeight(textarea);
        textarea.focus();
        // Position cursor at the end of the text
        const length = textarea.value.length;
        textarea.setSelectionRange(length, length);

        // Pulse the send button to draw attention
        this.sendBtnPulse = true;
        setTimeout(() => this.sendBtnPulse = false, 1800); // 3 cycles at 0.6s each
      });
    }

    // // Clear historical flag on this message so normalContent renders
    // message.isHistorical = false;

    // // Always hide SQL text — user should not see it
    // message.hideSql = true;

    // // Determine which flow to use based on stored data
    // if (message.chartConfig) {
    //   // Chart message: directly generate the chart using existing chartConfig
    //   this.generateChart(messageIndex);
    // } else if (this.hasSqlQuery(message.content)) {
    //   // SQL-only message (no chart): auto-execute query for per-message table rendering
    //   this.autoExecuteQuery(messageIndex);
    // }
  }

  // Auto-execute a SQL query and store results per-message for inline table rendering.
  // Uses per-message state (msg.tableData, msg.tableColumns) so each message retains
  // its own results independently — data persists when new prompts are sent.
  // forceLegacy: when true, always uses testSqlQuery (skips executeSql). Used by async flow fallback.
  autoExecuteQuery(messageIndex: number, sqlQuery?: string, forceLegacy?: boolean): void {
    const message = this.messages[messageIndex];
    if (!message) return;

    const query = sqlQuery || this.extractSqlQuery(message.content);
    if (!query) return;

    // Use cached result data if available (from async response) — skip API call
    if (message.resultData && message.resultData.length > 0) {
      this.isAutoExecuting = true;
      message.isLoadingTable = true;
      this.populateTableFromData(messageIndex, message.resultData);
      return;
    }

    // Cancel any previous subscriptions
    if (this.autoExecuteSubscription) {
      this.autoExecuteSubscription.unsubscribe();
    }
    if (this.executeSqlSubscription) {
      this.executeSqlSubscription.unsubscribe();
    }

    // Set component-level flag
    this.isAutoExecuting = true;

    // Set per-message loading state
    message.isLoadingTable = true;
    message.tableError = undefined;
    // Clear empty-state flag — a re-execution might return rows this time.
    message.tableEmpty = false;
    message.tableData = undefined;
    message.tableColumns = undefined;
    message.citationSources = undefined;
    message.summaryHidden = false; // Re-show summary on regeneration

    // New executeSql path (default) — only for user-initiated actions, not async flow fallback
    if (!forceLegacy && !this.isLegacyAiMode()) {
      const execPayload = this.buildExecuteSqlPayload(messageIndex, false);
      this.executeSqlSubscription = this.chatService.executeSql(execPayload)
        .pipe(timeout(this.EXECUTE_SQL_TIMEOUT_MS))
        .subscribe({
          next: (res: ExecuteSqlResponse) => this.handleExecuteSqlResponse(res, messageIndex),
          error: (error: any) => {
            message.isLoadingTable = false;
            this.isAutoExecuting = false;
            message.tableError = this.sanitizeApiError(error, 'sql');
            console.error("executeSql table error:", error);
          }
        });
      return;
    }

    // Legacy testSqlQuery path (ai-mode === 'true')
    const payload = { sqlQuery: query };

    this.autoExecuteSubscription = this.chatService.testSqlQuery(payload)
      .pipe(timeout(this.EXECUTE_SQL_TIMEOUT_MS))
      .subscribe({
        next: (response: any) => {
          // Extract results from API response
          let results: any[] = [];
          if (response && Array.isArray(response)) {
            results = response;
          } else if (response && response.data && Array.isArray(response.data)) {
            results = response.data;
          } else if (response && response.results && Array.isArray(response.results)) {
            results = response.results;
          }

          if (results.length > 0) {
            // Rows present → store + build ag-grid columns, clear stale empty flag.
            message.tableData = results;
            message.tableEmpty = false;
            const columns = Object.keys(results[0]);
            message.tableColumns = this.buildSqlTableColumns(columns);
          } else {
            // Legacy path returned zero rows — route to the info card via
            // tableEmpty, keep tableData undefined so the table container
            // stays unmounted (avoids duplicate "No results found" state).
            message.tableData = undefined;
            message.tableColumns = undefined;
            message.tableEmpty = true;
          }

          message.isLoadingTable = false;
          this.isAutoExecuting = false;
          setTimeout(() => this.scrollToMessage(messageIndex), 100);
        },
        error: (error: any) => {
          message.isLoadingTable = false;
          this.isAutoExecuting = false;
          message.tableData = [];
          message.tableColumns = [];
          message.tableError = this.sanitizeApiError(error, 'sql');
          setTimeout(() => this.scrollToMessage(messageIndex), 100);
          console.error('Auto SQL Execution Error:', error);
        }
      });
  }

  // Populate table directly from data (skips testSqlQuery HTTP call)
  private buildSqlTableColumns(columns: string[]): ColDef[] {
    return columns.map(col => ({
      field: col,
      headerName: formatColumnDisplayName(col),
      filter: true,
      sortable: true,
    }));
  }

  /** ag-Grid column defs for gantt chart table view (filtered + renamed headers). */
  private buildGanttTableColumns(): NonNullable<ChatMessage['tableColumns']> {
    return GANTT_TABLE_COLUMN_CONFIG.map(({ field, headerName }) => ({
      field,
      headerName,
      filter: true,
      sortable: true,
    }));
  }

  /** Prepare table view from cached chart rows without mutating resultData. */
  private ensureChartTableView(msg: ChatMessage): void {
    if (!msg.resultData?.length) {
      return;
    }
    if (this.isGanttChart(msg.chartConfig)) {
      msg.tableColumns = this.buildGanttTableColumns();
    } else if (!msg.tableColumns?.length) {
      const columns = Object.keys(msg.resultData[0]);
      msg.tableColumns = this.buildSqlTableColumns(columns) as NonNullable<ChatMessage['tableColumns']>;
    }
    msg.tableData = msg.resultData;
    msg.tableError = undefined;
    msg.isLoadingTable = false;
  }

  private populateTableFromData(messageIndex: number, data: any[]): void {
    const message = this.messages[messageIndex];
    if (!message) return;

    if (data.length > 0) {
      // Normal path: rows present → build ag-grid columns, render table,
      // clear any stale tableEmpty flag from a previous attempt.
      message.tableData = data;
      message.tableEmpty = false;
      const columns = Object.keys(data[0]);
      message.tableColumns = this.buildSqlTableColumns(columns);
    } else {
      // Regenerate returned zero rows — route to the info card via
      // tableEmpty. Leave tableData undefined so the table container
      // stays unmounted; otherwise the existing "No results found"
      // empty state would render alongside the info card.
      message.tableData = undefined;
      message.tableColumns = undefined;
      message.tableEmpty = true;
    }

    message.isLoadingTable = false;
    this.isAutoExecuting = false;
    setTimeout(() => this.scrollToMessage(messageIndex), 100);
  }

  // Toggle fullscreen for a per-message table
  toggleTableFullScreen(index: number): void {
    if (this.isFloatingView) {
      return;
    }
    this.fullScreenTableIndex = this.fullScreenTableIndex === index ? null : index;
    if (this.fullScreenTableIndex !== null) {
      this.showFullscreenSnackBar();
    } else {
      this.dismissFullscreenSnackBar();
    }
  }

  // Close/hide a per-message table
  closeMessageTable(index: number): void {
    const message = this.messages[index];
    if (message) {
      message.tableData = undefined;
      message.tableColumns = undefined;
      message.tableError = undefined;
      message.isLoadingTable = false;
      message.summaryHidden = true;
      // Stop summary typing if still running
      const summaryInterval = this.summaryTypingIntervals.get(index);
      if (summaryInterval) {
        clearInterval(summaryInterval);
        this.summaryTypingIntervals.delete(index);
        message.isSummaryTyping = false;
      }
    }
    if (this.fullScreenTableIndex === index) {
      this.fullScreenTableIndex = null;
    }
  }

  // Flip a chart message between its Chart.js visualization and an ag-Grid
  // table built from the same resultData. No API call is made — all data was
  // already cached on the message in processAsyncResponse. tableColumns are
  // built lazily the first time 'table' is requested so opening a table is
  // O(keys) rather than paying the cost up-front for messages the user never
  // toggles.
  //
  // IMPORTANT: when flipping AWAY from chart we must destroy the Chart.js
  // instance. The chart container's *ngIf unmounts the <canvas>, which leaves
  // msg.chartInstance pointing at a detached canvas. A stale-but-truthy
  // chartInstance would then block the rebuild guard below and the user would
  // see an empty canvas on flip-back.
  switchResultView(index: number, view: 'chart' | 'table'): void {
    const msg = this.messages[index];
    if (!msg) return;
    // Only valid for chart responses that have data to build a table from
    if (!msg.chartConfig || !msg.resultData || msg.resultData.length === 0) return;
    if (msg.activeView === view) return;

    if (view === 'table') {
      // Chart.js → tear down before the canvas unmounts
      if (msg.chartInstance) {
        try {
          msg.chartInstance.destroy();
        } catch (err) {
          console.error('Error destroying chart on view switch:', err);
        }
        msg.chartInstance = null;
      }
      // Brush listeners are bound to the chart canvas — drop them too
      this.destroyBrush(index);
      msg.brushState = undefined;
      // Exit chart fullscreen so the table doesn't inherit a stale state
      if (this.fullScreenChartIndex === index) {
        this.fullScreenChartIndex = null;
      }
      // Release the component-level "active chart" tracker so a subsequent
      // chart render on another message isn't mistakenly blocked
      if (this.activeChartIndex === index) {
        this.activeChartIndex = null;
        this.isGeneratingChart = false;
      }

      this.ensureChartTableView(msg);
      msg.activeView = 'table';
    } else {
      // Exit table fullscreen if it was active for this message
      if (this.fullScreenTableIndex === index) {
        this.fullScreenTableIndex = null;
      }
      msg.activeView = 'chart';
      // Rebuild from cached rows via the "cached" path, which does NOT set
      // isGenerating=true. That keeps the shimmer loader hidden and keeps
      // the view-toggle bar on screen while Chart.js attaches to the canvas
      // (the template now keeps the canvas mounted for this case — see the
      // chart-container/chart-wrapper *ngIfs).
      if (!msg.chartInstance) {
        this.renderCachedChart(index);
      }
    }
  }

  // Rebuild a chart from msg.resultData without flipping isGenerating.
  // Used by the view-toggle bar — we already have the rows and the canvas
  // stays in the DOM, so there's no need to flash "Generating chart..." or
  // briefly hide the toggle bar. Mirrors generateChartFromData minus the
  // loading-state plumbing.
  private renderCachedChart(messageIndex: number): void {
    const message = this.messages[messageIndex];
    if (!message || !message.chartConfig) return;
    if (!message.resultData || message.resultData.length === 0) return;

    // Control charts re-mount the D3 component directly — no Chart.js redraw.
    if (this.isControlChart(message.chartConfig)) {
      this.prepareControlChart(messageIndex);
      return;
    }

    if (this.isGanttChart(message.chartConfig)) {
      this.prepareGanttChart(messageIndex);
      this.restoreGanttTreeExpandState(messageIndex);
      return;
    }

    // Same type/variant validation as generateChartFromData
    const type = message.chartConfig.chart_type;
    const variant = message.chartConfig.chart_variant || 'simple';
    if (!this.SUPPORTED_TYPES.includes(type) || !this.SUPPORTED_VARIANTS.includes(variant)) {
      message.isUnsupportedChart = true;
      message.summaryHidden = true;
      return;
    }

    message.chartError = undefined;
    message.isUnsupportedChart = false;
    this.activeChartIndex = messageIndex;
    this.currentChartTitle = message.chartConfig.title || 'Chart Visualization';

    // renderChart() handles its own 300ms canvas-wait setTimeout; during that
    // window isGenerating stays false, so the bar stays visible and the
    // existing canvas remains in the DOM. Chart.js attaches on the same
    // canvas node without flicker.
    this.renderChart(message.chartConfig as any, message.resultData);
  }

  copyMessage(content: string, index: number): void {
    const cleanContent = content.replace(/^🧠 SQL Generated:\s*/m, "").replace(/^⚠️ Error:\s*/m, "").trim();

    navigator.clipboard.writeText(cleanContent).then(() => {
      this.copiedIndex = index;
      setTimeout(() => (this.copiedIndex = null), 2000);
    });
  }

  giveFeedback(index: number, type: "up" | "down"): void {
    const msg = this.messages[index];

    // If clicking down, show dialog
    if (type === "down") {
      this.currentFeedbackIndex = index;
      this.feedbackText = "";
      this.showFeedbackDialog = true;
      this.currentselectedMessage = msg.content;
      return;
    }

    // For thumbs up, send feedback immediately
    msg.feedback = msg.feedback === type ? undefined : type;
    if (msg.feedback) {
      this.sendFeedbackToServer(index, type, "");
    }
  }

  submitFeedback(): void {
    if (this.currentFeedbackIndex === null) return;

    const msg = this.messages[this.currentFeedbackIndex];
    msg.feedback = msg.feedback === "down" ? undefined : "down";

    this.sendFeedbackToServer(this.currentFeedbackIndex, "down", this.feedbackText);
    this.closeFeedbackDialog();
  }

  closeFeedbackDialog(): void {
    this.showFeedbackDialog = false;
    this.feedbackText = "";
    this.currentFeedbackIndex = null;
    this.currentselectedMessage = "";
  }

  sendFeedbackToServer(
    index: number,
    type: "up" | "down",
    suggestedQuery: string
  ): void {
    const msg = this.messages[index];
    const feedbackFlag = type === "down" ? "N" : "Y";

    if (!msg?.messageId || !this.conversation_id) {
      msg.feedback = undefined;
      this.dialogService.openSnackBar(
        "Unable to submit feedback for this message. Please try again.",
        "OK"
      );
      return;
    }

    // Find the most recent user prompt before this message
    const prompt = (() => {
      for (let i = index - 1; i >= 0; i--) {
        if (this.messages[i].role === "user") return this.messages[i].content;
      }
      return "";
    })();

    // Extract SQL from assistant content
    const sqlQuery = (msg.content || "").replace(/^🧠 SQL Generated:\s*/m, "").trim();

    const payload = {
      user_id: this.sessionService.getUsername(),
      prompt,
      feedback: feedbackFlag,
      sql_query: sqlQuery,
      suggested_sql_query: suggestedQuery,
      conversation_id: this.conversation_id,
      message_id: msg.messageId,
    };

    this.chatService.putFeedback(payload).subscribe({
      next: () => {
        this.dialogService.openSnackBar("Thanks for your feedback!", "OK");
      },
      error: (err) => {
        console.error("Feedback send error:", err);
      }
    });
  }

  scrollToBottom(): void {
    if (!this.chatContainer?.nativeElement) {
      return;
    }

    requestAnimationFrame(() => {
      const el = this.chatContainer.nativeElement as HTMLElement;
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    });
  }

  // Scroll to a specific message's results area by message index
  scrollToMessage(messageIndex: number): void {
    if (!this.chatContainer?.nativeElement) {
      return;
    }

    requestAnimationFrame(() => {
      const container = this.chatContainer.nativeElement as HTMLElement;
      // Find all message elements and target the specific one
      const messageElements = container.querySelectorAll('[class*="scraii-ai-message"].w-100');
      const targetMessage = messageElements[messageIndex];

      if (targetMessage) {
        // Scroll the inline results area into view, or the message itself
        const resultsArea = targetMessage.querySelector('.scraii-ai-inline-results');
        const scrollTarget = resultsArea || targetMessage;
        scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } else {
        // Fallback: scroll to bottom
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      }
    });
  }

  // Resolve the best scroll anchor inside a message. Priority chain:
  //   summary → chart container → inline results → message root
  // Returns null when the message itself isn't in the DOM yet.
  private resolveScrollAnchor(messageIndex: number): HTMLElement | null {
    if (!this.chatContainer?.nativeElement) return null;
    const container = this.chatContainer.nativeElement as HTMLElement;
    const messageElements = container.querySelectorAll('[class*="scraii-ai-message"].w-100');
    const targetMessage = messageElements[messageIndex] as HTMLElement | undefined;
    if (!targetMessage) return null;
    return (
      (targetMessage.querySelector('.scraii-ai-summary-container') as HTMLElement | null) ||
      (targetMessage.querySelector('.scraii-ai-chart-container') as HTMLElement | null) ||
      (targetMessage.querySelector('.scraii-ai-inline-results') as HTMLElement | null) ||
      targetMessage
    );
  }

  // Scroll a specific element to a specific offset inside the chat container
  // using scrollTop math. Preferred over Element.scrollIntoView because:
  //   1. scrollIntoView on elements inside a flex parent can inconsistently
  //      animate (Chromium vs Firefox differ on smooth behavior interruption),
  //   2. it avoids accidentally scrolling window / intermediate containers,
  //   3. we can give it an explicit top-padding offset so the summary doesn't
  //      jam against the very top of the viewport.
  private scrollElementIntoContainer(
    element: HTMLElement,
    mode: 'top' | 'nearest' = 'top',
    topPadding: number = 8
  ): void {
    const container = this.chatContainer?.nativeElement as HTMLElement | undefined;
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const elementTopInContainer = container.scrollTop + (elementRect.top - containerRect.top);

    if (mode === 'nearest') {
      // Only scroll when the element is outside the container viewport.
      const elementBottomInContainer = elementTopInContainer + elementRect.height;
      const viewTop = container.scrollTop;
      const viewBottom = viewTop + container.clientHeight;
      if (elementTopInContainer >= viewTop && elementBottomInContainer <= viewBottom) {
        return; // already fully visible
      }
    }

    container.scrollTo({
      top: Math.max(0, elementTopInContainer - topPadding),
      behavior: 'smooth',
    });
  }

  // Scroll so the summary of a given message sits near the top of the view.
  // Retries up to 3 animation frames when the message is still rendering so
  // the scroll doesn't silently no-op during Angular's first CD cycle after
  // a new message is pushed.
  private scrollToSummary(
    messageIndex: number,
    mode: 'top' | 'nearest' = 'top'
  ): void {
    if (!this.chatContainer?.nativeElement) return;
    let attempts = 0;
    const attempt = () => {
      const anchor = this.resolveScrollAnchor(messageIndex);
      if (!anchor) {
        if (attempts < 3) {
          attempts++;
          requestAnimationFrame(attempt);
        }
        return;
      }
      this.scrollElementIntoContainer(anchor, mode, 8);
    };
    requestAnimationFrame(attempt);
  }

  // Scroll the SQL card of a message fully into view. Used by toggleSqlCard
  // so the newly-expanded SQL isn't clipped at the bottom of the scroll
  // container. mode='nearest' — don't disturb if already fully visible.
  private scrollToSqlCard(messageIndex: number): void {
    if (!this.chatContainer?.nativeElement) return;
    const container = this.chatContainer.nativeElement as HTMLElement;
    let attempts = 0;
    const attempt = () => {
      const messageElements = container.querySelectorAll('[class*="scraii-ai-message"].w-100');
      const targetMessage = messageElements[messageIndex] as HTMLElement | undefined;
      const sqlCard = targetMessage?.querySelector('.scraii-ai-sql-card') as HTMLElement | null;
      if (!sqlCard) {
        if (attempts < 3) {
          attempts++;
          requestAnimationFrame(attempt);
        }
        return;
      }
      this.scrollElementIntoContainer(sqlCard, 'nearest', 12);
    };
    requestAnimationFrame(attempt);
  }

  // Is the summary container of a given message currently intersecting the
  // visible portion of the chat scroll container? Used to decide whether to
  // re-anchor after typing completes without disrupting a user who scrolled
  // elsewhere on purpose.
  private isSummaryVisible(messageIndex: number): boolean {
    if (!this.chatContainer?.nativeElement) return false;
    const container = this.chatContainer.nativeElement as HTMLElement;
    const messageElements = container.querySelectorAll('[class*="scraii-ai-message"].w-100');
    const targetMessage = messageElements[messageIndex] as HTMLElement | undefined;
    if (!targetMessage) return false;
    const summary = targetMessage.querySelector('.scraii-ai-summary-container') as HTMLElement | null;
    if (!summary) return false;
    const containerRect = container.getBoundingClientRect();
    const summaryRect = summary.getBoundingClientRect();
    // Any overlap counts as visible.
    return summaryRect.bottom > containerRect.top && summaryRect.top < containerRect.bottom;
  }

  // Dynamically adjust textarea height based on content
  adjustTextareaHeight(eventOrElement?: Event | HTMLTextAreaElement): void {
    const textarea = eventOrElement instanceof Event
      ? (eventOrElement.target as HTMLTextAreaElement)
      : eventOrElement;

    if (!textarea) return;

    const styles = getComputedStyle(textarea);
    const minHeight = this.parseCssPx(styles.minHeight, this.getTextareaMinHeight(textarea));
    const maxHeight = this.parseCssPx(styles.maxHeight, this.getTextareaMaxHeight(textarea));

    // Collapse before measuring so scrollHeight reflects actual content
    textarea.style.height = 'auto';
    textarea.style.overflowY = 'hidden';

    const contentHeight = textarea.scrollHeight;
    const newHeight = Math.min(Math.max(contentHeight, minHeight), maxHeight);
    textarea.style.height = `${newHeight}px`;
    textarea.style.overflowY = contentHeight > maxHeight ? 'auto' : 'hidden';
  }

  private parseCssPx(value: string, fallback: number): number {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) && parsed > 0 ? Math.ceil(parsed) : fallback;
  }

  private getTextareaMinHeight(textarea?: HTMLTextAreaElement): number {
    if (this.isFloatingView) {
      const el = textarea
        ?? this.centeredTextarea?.nativeElement
        ?? this.bottomTextarea?.nativeElement;
      if (el) {
        const minFromVar = this.parseCssPx(
          getComputedStyle(el).getPropertyValue('--fc-textarea-min-h'),
          0
        );
        if (minFromVar > 0) {
          return minFromVar;
        }
        const styles = getComputedStyle(el);
        const fontSize = parseFloat(styles.fontSize) || 12;
        const lineHeight = styles.lineHeight === 'normal'
          ? fontSize * 1.45
          : parseFloat(styles.lineHeight) || fontSize * 1.45;
        return Math.ceil(lineHeight);
      }
      return 36;
    }
    return 60;
  }

  private getTextareaMaxHeight(textarea?: HTMLTextAreaElement): number {
    if (this.isFloatingView) {
      const el = textarea
        ?? this.centeredTextarea?.nativeElement
        ?? this.bottomTextarea?.nativeElement;
      if (el) {
        const maxFromVar = this.parseCssPx(
          getComputedStyle(el).getPropertyValue('--fc-textarea-max-h'),
          0
        );
        if (maxFromVar > 0) {
          return maxFromVar;
        }
      }
      if (!this.isFloatingExpanded) {
        return Math.min(120, Math.max(72, Math.floor(window.innerHeight * 0.16)));
      }
      return Math.min(160, Math.max(100, Math.floor(window.innerHeight * 0.22)));
    }
    return 200;
  }

  // Reset textarea height to initial state
  resetTextareaHeight(): void {
    const centered = this.centeredTextarea?.nativeElement;
    const bottom = this.bottomTextarea?.nativeElement;

    if (centered) {
      this.adjustTextareaHeight(centered);
    }
    if (bottom) {
      this.adjustTextareaHeight(bottom);
    }
  }

  sendMessage() {
    const prompt = this.userInput.trim();
    if (!prompt) return;

    this.messages.push({ role: "user", content: prompt });
    this.userInput = "";
    this.scrollToBottom();
    this.isLoading = true;

    this.chatService.sendMessage(prompt).subscribe({
      next: (res) => {
        const responseText = res.sql_query
          ? `🧠 SQL Generated:\n${res.sql_query}`
          : `No SQL generated. (Status: ${res.statusCode})`;

        this.messages.push({ role: "assistant", content: responseText });

        this.isLoading = false;
        this.scrollToBottom();
      },
      error: (err) => {
        this.messages.push({
          role: "assistant",
          content: '',
          apiError: this.sanitizeApiError(err, 'submit'),
          hideSql: true,
        });
        this.isLoading = false;
      },
    });
  }

  // Async method to send message using the 3-step async API flow:
  // Step A: Submit job → Step B: Poll for completion → Step C: Fetch presigned result
  sendChatbotMessage() {
    const prompt = this.userInput.trim();
    if (!prompt) return;
    // Close the Browse Queries popup if it was still open.
    this.closeQueryPopup();

    // Prevent concurrent sends (including during typing animation and auto-execute)
    if (this.isLoading || this.isTyping || this.isAutoExecuting) {
      this.dialogService.openSnackBar("Please wait for the current operation to complete.", "OK");
      return;
    }

    // Cancel any in-progress auto-execute from previous message
    if (this.autoExecuteSubscription) {
      this.autoExecuteSubscription.unsubscribe();
      this.autoExecuteSubscription = undefined;
      this.isAutoExecuting = false;
    }

    // Fresh send — drop any stale invoke messageId from a prior request
    this.pendingMessageId = null;

    // Check if this is the first message in a new conversation
    const isFirstMessage = this.messages.length === 0;

    this.messages.push({ role: "user", content: prompt });
    this.userInput = "";
    this.resetTextareaHeight(); // Reset textarea height after clearing input
    this.scrollToBottom();

    const expectOpsOverview =
      this.isLikelyOpsOverviewQuery(prompt) || this.lastQuerySource === 'status';

    // Inline assistant pending bubble for every async request (ChatGPT-style)
    this.messages.push({
      role: 'assistant',
      isLoadingOpsOverview: true,
      hideSql: true,
      content: '',
    });
    this.lastQuerySource = 'other';

    this.isLoading = true;
    this.isTyping = true; // Show typing animation during async processing
    this.startStatusRotation(expectOpsOverview); // Start rotating status messages during polling

    const user_id = this.sessionService.getUsername();

    // Step A: Submit async job
    const asyncPayload: AsyncInvokePayload = {
      conversation_id: this.conversation_id,
      prompt: prompt,
      user_id: user_id,
      data: this.asyncDataParam,
      summary: this.summaryInsightEnabled ? 'Y' : 'N',
      team_name: this.user_team !== '' ? this.user_team.toLowerCase() : '',
      emp_dim_key: this.emp_dim_key,
      brand_key: this.resolveActiveBrandKey(),
      page_name: this.pageName,
    };

    this.messageSubscription = this.chatService.submitAsyncJob(asyncPayload)
      .pipe(
        timeout(this.SUBMIT_TIMEOUT_MS),
        catchError((error) => {
          this.resetAsyncState();
          const friendly = this.sanitizeApiError(error, 'submit');
          this.messages.push({ role: "assistant", content: '', apiError: friendly, hideSql: true });
          return throwError(() => error);
        })
      )
      .subscribe({
        next: (invokeRes: any) => {
          let parsedBody: AsyncInvokeResponseBody;
          try {
            parsedBody = typeof invokeRes.body === 'string' ? JSON.parse(invokeRes.body) : invokeRes.body;
          } catch (e) {
            this.resetAsyncState();
            this.messages.push({
              role: "assistant",
              content: '',
              apiError: this.sanitizeApiError(e, 'submit'),
              hideSql: true,
            });
            return;
          }

          // Capture per-message id from invoke — attached to the final assistant bubble
          this.pendingMessageId =
            parsedBody.messageId ||
            (parsedBody as { message_id?: string }).message_id ||
            null;

          const jobid = parsedBody.jobid;
          if (!jobid) {
            this.resetAsyncState();
            this.messages.push({
              role: "assistant",
              content: '',
              apiError: this.sanitizeApiError(null, 'submit'),
              hideSql: true,
              messageId: this.takePendingMessageId(),
            });
            return;
          }

          // Step B: Poll for completion
          this.pollAttemptCount = 0;
          this.pollingSubscription = interval(this.POLL_INTERVAL_MS).pipe(
            switchMap(() => {
              this.pollAttemptCount++;
              if (this.pollAttemptCount > this.MAX_POLL_ATTEMPTS) {
                return throwError(() => new Error("Request timed out. Please try again."));
              }
              return this.chatService.pollJobStatus(jobid);
            }),
            map((pollRes: any) => {
              try {
                return typeof pollRes.body === 'string' ? JSON.parse(pollRes.body) : pollRes.body;
              } catch (e) {
                throw new Error("Invalid poll response from server.");
              }
            }),
            takeWhile((body: JobStatusResponseBody) => body.status !== this.JOB_STATUS_COMPLETED && body.status !== this.JOB_STATUS_FAILED, true)
          ).subscribe({
            next: (body: JobStatusResponseBody) => {
              if (body.status === this.JOB_STATUS_COMPLETED) {
                // Step C: Fetch result from presigned URL
                if (!body.presigned_url) {
                  this.resetAsyncState();
                  this.messages.push({
                    role: "assistant",
                    content: '',
                    apiError: this.sanitizeApiError(null, 'fetch'),
                    hideSql: true,
                    messageId: this.takePendingMessageId(),
                  });
                  return;
                }
                this.isTyping = false; // Stop typing animation before fetching result
                if (this.fetchResultSubscription) {
                  this.fetchResultSubscription.unsubscribe();
                }
                this.fetchResultSubscription = this.chatService.getJobResults(jobid).subscribe({ // uncomment this when the getJobResults api is available on stage branch 
                // this.fetchResultSubscription = this.chatService.fetchPresignedResult(body.presigned_url).subscribe({
                  next: (res: any) => this.processAsyncResponse(res, isFirstMessage, prompt),
                  error: (err) => {
                    this.resetAsyncState();
                    this.messages.push({
                      role: "assistant",
                      content: '',
                      apiError: this.sanitizeApiError(err, 'fetch'),
                      hideSql: true,
                      messageId: this.takePendingMessageId(),
                    });
                  }
                });
              } else if (body.status === this.JOB_STATUS_FAILED) {
                this.resetAsyncState();
                this.messages.push({
                  role: "assistant",
                  content: '',
                  apiError: this.sanitizeApiError(body, 'sql'),
                  hideSql: true,
                  messageId: this.takePendingMessageId(),
                });
              }
              // PENDING/PROCESSING — continue polling (typing animation is already showing)
            },
            error: (err) => {
              this.resetAsyncState();
              this.messages.push({
                role: "assistant",
                content: '',
                apiError: this.sanitizeApiError(err, 'generic'),
                hideSql: true,
                messageId: this.takePendingMessageId(),
              });
            }
          });
        },
        error: (err) => {
          // Error already handled in catchError pipe — this handles uncaught cases
          if (this.isLoading) {
            this.resetAsyncState();
            this.messages.push({
              role: "assistant",
              content: '',
              apiError: this.sanitizeApiError(err, 'generic'),
              hideSql: true,
              messageId: this.takePendingMessageId(),
            });
          }
        },
      });
  }

  // Process the response from presigned URL — uses result[] data directly (no second testSqlQuery call)
  private extractCitationSources(res: any): string[] | undefined {
    const sources = res?.citation?.sources;
    if (!Array.isArray(sources)) return undefined;
    const filtered = sources
      .filter((s): s is string => typeof s === 'string')
      .map(s => s.trim())
      .filter(s => s.length > 0);
    return filtered.length > 0 ? filtered : undefined;
  }

  // Projects whose response is an answer bubble (no SQL/chart/table).
  isAnswerOnlyProject(project: string | undefined): boolean {
    return project === 'config_ai'
      || project === 'enrollment_rag'
      || project === 'contract_rag'
      || project === 'data_dictionary'
      || project === 'verification_audit';
  }

  // Returns verification_audit.answer when the SQL result is null or an empty
  // array; '' otherwise. Skips response kinds that never carry a `result`.
  private getVerificationAuditAnswer(res: any): string {
    if (!res || res.needs_clarification || res.is_ops_overview === true
      || res.response_kind === 'multi_sql' || this.isAnswerOnlyProject(res.project)) {
      return '';
    }
    const result = res.result;
    const isEmptyResult = result == null || (Array.isArray(result) && result.length === 0);
    if (!isEmptyResult) {
      return '';
    }
    const audit = res.verification_audit;
    if (!audit || typeof audit !== 'object') {
      return '';
    }
    return typeof audit.answer === 'string' ? audit.answer.trim() : '';
  }

  getRagCitationsTitle(project: string | undefined): string {
    return project === 'data_dictionary' ? 'Referenced Fields' : 'Citations';
  }

  getMonitorStatusClass(status: string): string {
    switch (status) {
      case 'critical': return 'ops-status-critical';
      case 'warning':  return 'ops-status-warning';
      case 'ok':
      case 'healthy': return 'ops-status-ok';
      case 'no_data': return 'ops-status-nodata';
      default:         return 'ops-status-nodata';
    }
  }

  hasActiveOpsPlaceholder(): boolean {
    return this.messages.some((m: ChatMessage) => m.isLoadingOpsOverview);
  }

  private normalizeQuerySource(id?: string): 'dispense' | 'status' | 'journey' | 'other' {
    if (id === 'dispense' || id === 'status' || id === 'journey') {
      return id;
    }
    return 'other';
  }

  private isLikelyOpsOverviewQuery(prompt: string): boolean {
    const p = prompt.toLowerCase();
    const keywords = [
      'monitor', 'status', 'ops', 'kpi', 'alert', 'health',
      'overview', 'sla', 'hub program', 'prior authorization',
    ];
    return keywords.some(k => p.includes(k));
  }

  private removeOpsPlaceholder(): void {
    const idx = this.messages.findIndex((m: ChatMessage) => m.isLoadingOpsOverview);
    if (idx >= 0) {
      this.messages.splice(idx, 1);
    }
    this.stopOpsReveal();
  }

  getVisibleOpsMonitors(msg: ChatMessage): OpsMonitor[] {
    if (!msg.opsMonitors?.length) {
      return [];
    }
    if (msg.opsRevealComplete || msg.isHistorical) {
      return msg.opsMonitors;
    }
    const count = msg.opsVisibleMonitorCount ?? 0;
    return msg.opsMonitors.slice(0, count);
  }

  shouldShowOpsBanner(msg: ChatMessage): boolean {
    if (!msg.opsOverallSummary) {
      return false;
    }
    if (msg.isHistorical || msg.opsRevealComplete) {
      return true;
    }
    return msg.opsBannerVisible === true;
  }

  isOpsRevealComplete(msg: ChatMessage): boolean {
    if (msg.isLoadingOpsOverview) {
      return false;
    }
    if (!msg.opsMonitors?.length) {
      return true;
    }
    return msg.opsRevealComplete === true || msg.isHistorical === true;
  }

  private stopOpsReveal(): void {
    if (this.opsRevealInterval) {
      clearInterval(this.opsRevealInterval);
      this.opsRevealInterval = null;
    }
    this.opsRevealTimerIds.forEach(id => clearTimeout(id));
    this.opsRevealTimerIds = [];
  }

  private startOpsReveal(msgIndex: number): void {
    this.stopOpsReveal();
    const msg = this.messages[msgIndex];
    if (!msg?.opsMonitors?.length) {
      if (msg) {
        msg.opsRevealComplete = true;
      }
      setTimeout(() => this.scrollToBottom(), 100);
      return;
    }

    msg.opsVisibleMonitorCount = 0;
    msg.opsBannerVisible = false;
    msg.opsRevealComplete = false;
    this.cdr.detectChanges();

    const bannerTimer = window.setTimeout(() => {
      msg.opsBannerVisible = true;
      this.cdr.detectChanges();
      this.scrollToBottom();
    }, 280);
    this.opsRevealTimerIds.push(bannerTimer);

    const cardsTimer = window.setTimeout(() => {
      const total = msg.opsMonitors!.length;
      let count = 1;
      msg.opsVisibleMonitorCount = count;
      this.cdr.detectChanges();
      this.scrollToBottom();

      if (total <= 1) {
        msg.opsRevealComplete = true;
        this.cdr.detectChanges();
        this.scrollToBottom();
        return;
      }

      this.opsRevealInterval = setInterval(() => {
        count++;
        msg.opsVisibleMonitorCount = count;
        this.cdr.detectChanges();
        this.scrollToBottom();
        if (count >= total) {
          this.stopOpsReveal();
          msg.opsRevealComplete = true;
          this.cdr.detectChanges();
          this.scrollToBottom();
        }
      }, 220);
    }, 520);
    this.opsRevealTimerIds.push(cardsTimer);
  }

  /** Returns a CSS color for an ops metric value based on its key or explicit color. */
  getMetricValueColor(metric: { key: string; color?: string }): string {
    if (metric.color) return metric.color;
    const key = (metric.key || '').toLowerCase();
    const COLOR_MAP: Record<string, string> = {
      expected: '#1e293b',
      received: '#16a34a',
      processed: '#0f766e',
      delayed: '#d97706',
      failed: '#dc2626',
      anomalies: '#dc2626',
      within_limits: '#0f4c81',
      total_kpis: '#1e293b',
    };
    return COLOR_MAP[key] ?? '#1e293b';
  }

  // Extract structured RAG citations — only a non-empty array of objects.
  private extractRagCitations(res: any): RagCitation[] | undefined {
    const citations = res?.citations;
    if (!Array.isArray(citations)) return undefined;
    const filtered = citations.filter(
      (c): c is RagCitation => !!c && typeof c === 'object' && !Array.isArray(c)
    );
    return filtered.length > 0 ? filtered : undefined;
  }

  // Derive ag-grid column defs dynamically from row keys (first-seen order).
  private buildRagGridColumns(
    rows: Record<string, any>[] | undefined,
    options?: {
      fieldOrder?: string[];
      headerName?: (field: string) => string;
      formatCell?: (field: string, value: any) => string;
    }
  ): ColDef[] | undefined {
    if (!rows || rows.length === 0) return undefined;
    const fields: string[] = options?.fieldOrder ? [...options.fieldOrder] : [];
    if (fields.length === 0) {
      for (const row of rows) {
        for (const key of Object.keys(row)) {
          if (!fields.includes(key)) {
            fields.push(key);
          }
        }
      }
    }
    if (fields.length === 0) return undefined;
    const headerName = options?.headerName ?? ((field: string) => this.humanizeCitationHeader(field));
    const formatCell = options?.formatCell ?? ((field: string, value: any) => this.formatRagCitationCell(field, value));
    return fields.map(field => ({
      field,
      headerName: headerName(field),
      sortable: true,
      filter: true,
      valueFormatter: params => formatCell(field, params.value),
      tooltipValueGetter: params => formatCell(field, params.value),
      cellClass: 'scraii-ai-rag-ag-cell',
      maxWidth: 400,
    }));
  }

  private buildRagCitationAgColumns(citations: RagCitation[] | undefined): ColDef[] | undefined {
    return this.buildRagGridColumns(citations);
  }

  private buildContractRagGridPayload(tables: ContractRagTable[]): {
    columns: ColDef[][];
    rowData: Record<string, string>[][];
  } {
    const columns: ColDef[][] = [];
    const rowData: Record<string, string>[][] = [];
    for (const table of tables) {
      const fields = table.headers.map((_, ci) => `col_${ci}`);
      columns.push(
        this.buildRagGridColumns(
          table.rows.map(row => {
            const obj: Record<string, string> = {};
            fields.forEach((field, ci) => {
              obj[field] = this.formatContractTableCell(row[ci]);
            });
            return obj;
          }),
          {
            fieldOrder: fields,
            headerName: field => table.headers[Number(field.replace('col_', ''))] ?? field,
            formatCell: (_field, value) => this.formatContractTableCell(value),
          }
        ) ?? []
      );
      rowData.push(
        table.rows.map(row => {
          const obj: Record<string, string> = {};
          fields.forEach((field, ci) => {
            obj[field] = this.formatContractTableCell(row[ci]);
          });
          return obj;
        })
      );
    }
    return { columns, rowData };
  }

  getRagGridKey(messageIndex: number, kind: 'citations' | 'table', tableIndex?: number): string {
    return tableIndex !== undefined
      ? `${messageIndex}:table:${tableIndex}`
      : `${messageIndex}:${kind}`;
  }

  onRagGridReady(gridKey: string, params: { api: any }): void {
    this.ragGridApis.set(gridKey, params.api);
    this.refreshRagGrid(params.api);
  }

  private refreshRagGrid(api: any): void {
    if (!api) return;
    setTimeout(() => {
      try {
        const colCount = api.getColumns()?.length ?? 0;
        if (colCount > 0 && colCount < 5) {
          api.sizeColumnsToFit();
        } else if (colCount > 0) {
          api.autoSizeAllColumns(false);
        }
      } catch (error) {
        console.error('Error sizing RAG grid columns:', error);
      }
    }, 0);
  }

  private humanizeCitationHeader(field: string): string {
    if (field === 'source_s3_key') return 'Source';
    return field
      .replace(/_/g, ' ')
      .replace(/\b\w/g, ch => ch.toUpperCase());
  }

  // Render a single citation cell — show only the filename for S3 keys.
  formatRagCitationCell(field: string, value: any): string {
    if (value === null || value === undefined) return '';
    if (field === 'source_s3_key' && typeof value === 'string') {
      const parts = value.split('/');
      return parts[parts.length - 1] || value;
    }
    return String(value);
  }

  formatContractTableCell(value: any): string {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  }

  // ===== contract_rag: parse [Table: Title] markers into segments =====

  private parseContractRagSegments(answer: string, tables: ContractRagTable[]): ContractRagSegment[] {
    const regex = /\[Table:\s*(.+?)\]/g;
    const segments: ContractRagSegment[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(answer)) !== null) {
      const textBefore = answer.slice(lastIndex, match.index).trim();
      if (textBefore) {
        segments.push({ type: 'text', content: textBefore });
      }
      const title = match[1].trim();
      const idx = tables.findIndex(t => t.title === title);
      if (idx !== -1) {
        segments.push({ type: 'table', tableIndex: idx });
      }
      lastIndex = match.index + match[0].length;
    }
    const rest = answer.slice(lastIndex).trim();
    if (rest) {
      segments.push({ type: 'text', content: rest });
    }
    return segments;
  }

  isRagCitationsExpanded(msg: ChatMessage): boolean {
    return msg.ragCitationsExpanded === true;
  }

  toggleRagCitations(msg: ChatMessage, messageIndex?: number): void {
    const wasExpanded = this.isRagCitationsExpanded(msg);
    msg.ragCitationsExpanded = !wasExpanded;
    if (!wasExpanded && messageIndex !== undefined) {
      const api = this.ragGridApis.get(this.getRagGridKey(messageIndex, 'citations'));
      if (api) {
        this.refreshRagGrid(api);
      }
    }
  }

  private processAsyncResponse(res: any, isFirstMessage: boolean, prompt: string): void {
    this.stopStatusRotation();

    // Prefer message id from result payload; fall back to invoke-time pending id
    const messageId =
      (typeof res?.message_id === 'string' && res.message_id) ||
      (typeof res?.messageId === 'string' && res.messageId) ||
      this.pendingMessageId ||
      undefined;
    this.pendingMessageId = null;

    // Access-denied gate: backend denied access to the requested data. Show the
    // backend's error message verbatim (not sanitized) in the error card. Runs
    // before the error-first gate so the raw message is preserved.
    if (res?.access_denied === true) {
      if (res.conversation_id) {
        this.conversation_id = res.conversation_id;
      }
      this.removeOpsPlaceholder();
      const accessMsg = (typeof res.error === 'string' && res.error.trim().length > 0)
        ? res.error.trim()
        : 'Access denied. A valid team_name is required for project access validation.';
      this.messages.push({
        role: "assistant",
        content: '',
        apiError: accessMsg,
        hideSql: true,
        messageId,
      });
      this.isLoading = false;
      this.isTyping = false;
      this.isGeneratingChart = false;
      this.isAutoExecuting = false;
      this.typingMessageIndex = null;
      setTimeout(() => this.scrollToBottom(), 100);
      if (isFirstMessage) {
        this.addNewConversationToSidebar(prompt);
      }
      return;
    }

    // Verification-audit gate: when the SQL result is null / [] and the backend
    // supplied verification_audit.answer, show that answer instead of the error
    // or empty-result card. Without an audit answer, fall through as usual.
    const auditAnswer = this.getVerificationAuditAnswer(res);
    if (auditAnswer) {
      if (res.conversation_id) {
        this.conversation_id = res.conversation_id;
      }
      this.removeOpsPlaceholder();
      const messageIndex = this.messages.length;
      this.messages.push({
        role: "assistant",
        content: "",
        project: 'verification_audit',
        hideSql: true,
        llmTime: res.llm_time,
        dbTime: res.db_time,
        messageId,
      });
      this.typeMessage(auditAnswer, messageIndex);
      this.isLoading = false;
      this.isGeneratingChart = false;
      this.isAutoExecuting = false;
      if (isFirstMessage) {
        this.addNewConversationToSidebar(prompt);
      }
      return;
    }

    // Error-first gate: backend surfaced an error (e.g., timeout, query too long).
    // Show the message text alone, as a plain assistant bubble — no SQL, chart,
    // table, or summary — regardless of success / sql_query / result fields.
    if (typeof res?.error === 'string' && res.error.trim().length > 0) {
      if (res.conversation_id) {
        this.conversation_id = res.conversation_id;
      }
      this.removeOpsPlaceholder();
      this.messages.push({
        role: "assistant",
        content: '',
        apiError: this.sanitizeApiError(res.error, 'sql'),
        hideSql: true,
        messageId,
      });
      this.isLoading = false;
      this.isTyping = false;
      this.isGeneratingChart = false;
      this.isAutoExecuting = false;
      this.typingMessageIndex = null;
      setTimeout(() => this.scrollToBottom(), 100);
      if (isFirstMessage) {
        this.addNewConversationToSidebar(prompt);
      }
      return;
    }

    // multi_sql — one metric card per query (KPI tiles / table + independent SQL card)
    if (res?.response_kind === 'multi_sql' && Array.isArray(res.sql_queries) && res.sql_queries.length) {
      if (res.conversation_id) {
        this.conversation_id = res.conversation_id;
      }
      this.removeOpsPlaceholder();
      this.messages.push({
        role: 'assistant',
        content: '',
        hideSql: true,
        multiMetrics: res.sql_queries.map((q: MultiMetricQuery) => ({
          ...q,
          error: q.error ? this.sanitizeApiError(q.error, 'sql') : null,
          showSql: false,
          // Tiles cover the single-row case; only multi-row blocks need a grid.
          columns: (q.result && q.result.length > 1)
            ? this.buildSqlTableColumns(Object.keys(q.result[0]))
            : undefined,
        })),
        llmTime: res.llm_time,
        dbTime: res.db_time,
        messageId,
      });
      this.isTyping = false;
      this.typingMessageIndex = null;
      this.isLoading = false;
      this.isGeneratingChart = false;
      this.isAutoExecuting = false;
      setTimeout(() => this.scrollToBottom(), 100);
      if (isFirstMessage) {
        this.addNewConversationToSidebar(prompt);
      }
      return;
    }

    // ops_overview — monitor status card grid
    if (res?.is_ops_overview === true && Array.isArray(res.monitors)) {
      if (res.conversation_id) {
        this.conversation_id = res.conversation_id;
      }
      const STATUS_ORDER: Record<string, number> = { critical: 0, warning: 1, ok: 2 };
      const sorted: OpsMonitor[] = [...res.monitors].sort((a: OpsMonitor, b: OpsMonitor) =>
        (STATUS_ORDER[a.status] ?? 3) - (STATUS_ORDER[b.status] ?? 3)
      );
      const opsMessage: ChatMessage = {
        role: 'assistant',
        content: '',
        hideSql: true,
        isLoadingOpsOverview: false,
        opsMonitors: sorted,
        opsOverallStatus: res.overall_status ?? undefined,
        opsOverallSummary: typeof res.summary === 'string' ? res.summary : undefined,
        llmTime: res.llm_time,
        dbTime: res.db_time,
        opsVisibleMonitorCount: 0,
        opsBannerVisible: false,
        opsRevealComplete: false,
        messageId,
      };
      const placeholderIdx = this.messages.findIndex((m: ChatMessage) => m.isLoadingOpsOverview);
      let targetIdx: number;
      if (placeholderIdx >= 0) {
        this.messages[placeholderIdx] = opsMessage;
        targetIdx = placeholderIdx;
      } else {
        this.messages.push(opsMessage);
        targetIdx = this.messages.length - 1;
      }
      this.isTyping = false;
      this.typingMessageIndex = null;
      this.isLoading = false;
      this.isGeneratingChart = false;
      this.isAutoExecuting = false;
      this.cdr.detectChanges();
      this.startOpsReveal(targetIdx);
      if (isFirstMessage) {
        this.addNewConversationToSidebar(prompt);
      }
      return;
    }

    this.removeOpsPlaceholder();

    if (res?.project === 'config_ai') {
      if (res.conversation_id) {
        this.conversation_id = res.conversation_id;
      }

      const answerText = typeof res.answer === 'string' ? res.answer.trim() : '';
      const messageIndex = this.messages.length;

      this.messages.push({
        role: "assistant",
        content: "",
        project: 'config_ai',
        hideSql: true,
        llmTime: res.llm_time,
        dbTime: res.db_time,
        messageId,
      });

      if (answerText) {
        this.typeMessage(answerText, messageIndex);
      } else {
        this.messages[messageIndex].content = 'No answer received.';
        this.isTyping = false;
        this.typingMessageIndex = null;
        setTimeout(() => this.scrollToBottom(), 100);
      }

      this.isLoading = false;
      this.isGeneratingChart = false;
      this.isAutoExecuting = false;

      if (isFirstMessage) {
        this.addNewConversationToSidebar(prompt);
      }
      return;
    }

    // enrollment_rag / data_dictionary — answer-only bubble with typed markdown and a
    // dynamically-columned citations table (field defs vary per project shape).
    if (res?.project === 'enrollment_rag' || res?.project === 'data_dictionary') {
      if (res.conversation_id) {
        this.conversation_id = res.conversation_id;
      }

      const answerText = typeof res.answer === 'string' ? res.answer.trim() : '';
      const citations = this.extractRagCitations(res);
      const messageIndex = this.messages.length;

      this.messages.push({
        role: "assistant",
        content: "",
        project: res.project,
        hideSql: true,
        ragCitations: citations,
        ragCitationAgColumns: this.buildRagCitationAgColumns(citations),
        ragCitationsExpanded: false,
        llmTime: res.llm_time,
        dbTime: res.db_time,
        messageId,
      });

      if (answerText) {
        this.typeMessage(answerText, messageIndex);
      } else {
        this.messages[messageIndex].content = 'No answer received.';
        this.isTyping = false;
        this.typingMessageIndex = null;
        setTimeout(() => this.scrollToBottom(), 100);
      }

      this.isLoading = false;
      this.isGeneratingChart = false;
      this.isAutoExecuting = false;

      if (isFirstMessage) {
        this.addNewConversationToSidebar(prompt);
      }
      return;
    }

    // contract_rag — answer with inline [Table: Title] markers replaced by paginated
    // structured tables from res.tables[]. Citations still render below.
    if (res?.project === 'contract_rag') {
      if (res.conversation_id) {
        this.conversation_id = res.conversation_id;
      }

      const answerText = typeof res.answer === 'string' ? res.answer.trim() : '';
      const tables: ContractRagTable[] = Array.isArray(res.tables) ? res.tables : [];
      const segments: ContractRagSegment[] = (answerText && tables.length)
        ? this.parseContractRagSegments(answerText, tables)
        : [];
      const citations = this.extractRagCitations(res);
      const contractGridPayload = tables.length ? this.buildContractRagGridPayload(tables) : undefined;

      this.messages.push({
        role: "assistant",
        content: answerText || 'No answer received.',
        project: 'contract_rag',
        hideSql: true,
        contractRagSegments: segments.length ? segments : undefined,
        contractRagTables: tables.length ? tables : undefined,
        contractRagAgColumns: contractGridPayload?.columns,
        contractRagRowData: contractGridPayload?.rowData,
        ragCitations: citations,
        ragCitationAgColumns: this.buildRagCitationAgColumns(citations),
        ragCitationsExpanded: false,
        llmTime: res.llm_time,
        dbTime: res.db_time,
        messageId,
      });

      // No typeMessage — [Table: …] markers look broken mid-stream; render immediately.
      this.isTyping = false;
      this.typingMessageIndex = null;
      this.isLoading = false;
      this.isGeneratingChart = false;
      this.isAutoExecuting = false;
      setTimeout(() => this.scrollToBottom(), 100);

      if (isFirstMessage) {
        this.addNewConversationToSidebar(prompt);
      }
      return;
    }

    let responseText = "";
    let needsClarification = res?.needs_clarification || false;
    let chartConfig: any = undefined;

    // Extract result data from async response
    const asyncResultData = res.result && Array.isArray(res.result) ? res.result : null;
    const programResultData = Array.isArray(res.program_result) ? res.program_result : undefined;

    // Check if response contains chart data
    if (res.patient_journey && res.chart_type === 'gantt_chart') {
      chartConfig = {
        chart_type: 'gantt_chart',
        patient_journey: true,
        title: res.title || 'Patient Journey',
        sql_query: res.sql_query || '',
      };
    } else if (isScraiiGanttPocEnabled() && this.isJourneyPocPrompt(prompt)) {
      chartConfig = {
        chart_type: 'gantt_chart',
        patient_journey: true,
        title: 'Patient Journey',
      };
    } else if (res.chart_type && res.x_axis && res.y_axis && res.sql_query) {
      chartConfig = {
        chart_type: res.chart_type,
        chart_variant: res.chart_variant || 'simple',
        x_axis: res.x_axis,
        y_axis: res.y_axis,
        title: res.title || 'Chart Visualization',
        sql_query: res.sql_query
      };
    }

    // Determine response text based on response type
    if (res.needs_clarification && res.clarification_question) {
      responseText = `${res.clarification_question}❓`;
    } else if (res.success && res.sql_query) {
      responseText = `🧠 SQL Generated:\n${res.sql_query}`;
    } else if (res.error) {
      responseText = `⚠️ Error: ${this.sanitizeApiError(res.error, 'sql')}`;
    } else {
      responseText = `No SQL generated. Response received but no query available.`;
    }

    // Update conversation_id if returned in response
    if (res.conversation_id) {
      this.conversation_id = res.conversation_id;
    }

    // Determine if this is a non-chart SQL response (should auto-execute)
    const isNonChartSql = !chartConfig && !needsClarification && !res.error && res.success && res.sql_query;

    // Push empty message first — include resultData and summary for caching.
    // summaryHidden is initialised from the AI Insights toggle so the toggle state
    // is LOCKED at send time; flipping the toggle later does not affect past messages.
    // Exception: empty result (result: []) with a backend summary always shows it.
    // Downstream error/unsupported/empty branches may later set summaryHidden = true
    // for their own reasons — those still override this initial value (true wins).
    const messageIndex = this.messages.length;
    this.messages.push({
      role: "assistant",
      content: "",
      needs_clarification: needsClarification,
      chartConfig: chartConfig,
      hideSql: !!chartConfig || !!isNonChartSql,
      isGenerating: !!chartConfig,
      isLoadingTable: !!isNonChartSql,
      resultData: asyncResultData || undefined,
      programResultData: programResultData,
      summary: res.summary || undefined,
      summaryHidden: !this.summaryInsightEnabled,
      citationSources: this.extractCitationSources(res),
      llmTime: res.llm_time,
      dbTime: res.db_time,
      // Default chart messages to the chart view; switchResultView() flips it
      // to 'table' on demand and lazily builds tableColumns from resultData.
      activeView: chartConfig ? 'chart' : undefined,
      messageId,
    });

    // Handle display (typing vs immediate)
    if (res.error || isNonChartSql) {
      this.messages[messageIndex].content = responseText;
      this.isTyping = false;
      this.typingMessageIndex = null;
      setTimeout(() => this.scrollToBottom(), 100);
    } else if (chartConfig) {
      // Chart path — SQL text is hidden (hideSql: true), no need to animate.
      // Previously called typeMessage() which set isTyping = true and churned
      // the SQL string through a 15ms/char interval into an invisible field,
      // keeping the stop button visible for seconds after the chart rendered.
      this.messages[messageIndex].content = responseText;
    } else {
      this.typeMessage(responseText, messageIndex);
    }

    // If we have a chart — use result data directly if available, else fallback to testSqlQuery
    if (chartConfig && this.isGanttChart(chartConfig)) {
      const msg = this.messages[messageIndex];
      const apiReturnedGantt = !!(res.patient_journey && res.chart_type === 'gantt_chart');
      if (apiReturnedGantt && asyncResultData && asyncResultData.length > 0) {
        if (programResultData) {
          msg.programResultData = programResultData;
        }
        this.generateChartFromData(messageIndex, asyncResultData);
      } else if (isScraiiGanttPocEnabled()) {
        this.loadGanttPocData(messageIndex);
      } else if (asyncResultData !== null) {
        if (msg) {
          msg.isGenerating = false;
          msg.chartEmpty = true;
          msg.summaryHidden = !res.summary;
        }
        this.isGeneratingChart = false;
        this.isLoading = false;
      } else if (msg) {
        msg.isGenerating = false;
        msg.chartEmpty = true;
        msg.summaryHidden = !res.summary;
        this.isGeneratingChart = false;
        this.isLoading = false;
      }
    } else if (chartConfig && asyncResultData && asyncResultData.length > 0) {
      this.generateChartFromData(messageIndex, asyncResultData);
    } else if (chartConfig && asyncResultData !== null) {
      // Backend returned result: [] — SQL executed, no rows found.
      // Do NOT re-execute via get-ai-data. This is a valid "no rows" answer,
      // NOT an error — flag it with chartEmpty so the template renders the
      // friendly info card instead of the red error card.
      const msg = this.messages[messageIndex];
      if (msg) {
        msg.isGenerating = false;
        msg.chartEmpty = true;
        if (res.summary) {
          // Empty result + summary: always show the explanation, even when
          // Insights Summary is off — it supersedes the generic info card.
          msg.summaryHidden = false;
        } else {
          msg.summaryHidden = true;
        }
      }
      this.isGeneratingChart = false;
      this.isLoading = false;
    } else if (chartConfig) {
      // asyncResultData is null — presigned response had no result field at all.
      // Fall back to testSqlQuery as last resort.
      this.generateChart(messageIndex, true);
    }

    // If we have a non-chart SQL — use result data directly if available, else fallback
    if (isNonChartSql && asyncResultData && asyncResultData.length > 0) {
      this.populateTableFromData(messageIndex, asyncResultData);
    } else if (isNonChartSql && asyncResultData !== null) {
      // Backend returned result: [] — SQL executed, no rows found.
      // Valid "no rows" answer, NOT an error — use tableEmpty so the template
      // renders the friendly info card instead of the red error card.
      //
      // IMPORTANT: leave tableData undefined (not []). Setting it to []
      // would make the table container's *ngIf match (tableData truthy) and
      // render the existing "No results found" empty state — redundant with
      // the new info card. Undefined tableData keeps that container hidden.
      const msg = this.messages[messageIndex];
      if (msg) {
        msg.isLoadingTable = false;
        msg.tableEmpty = true;
        if (res.summary) {
          // Empty result + summary: always show the explanation, even when
          // Insights Summary is off — it supersedes the generic info card.
          msg.summaryHidden = false;
        } else {
          msg.summaryHidden = true;
        }
      }
      this.isAutoExecuting = false;
      this.isLoading = false;
    } else if (isNonChartSql) {
      // asyncResultData is null — no result field in response.
      // Fall back to autoExecuteQuery as last resort.
      this.autoExecuteQuery(messageIndex, res.sql_query, true);
    }

    // Trigger summary typing animation after chart/table renders
    if (res.summary) {
      const summaryDelay = chartConfig ? this.SUMMARY_DELAY_CHART_MS : (isNonChartSql ? this.SUMMARY_DELAY_TABLE_MS : this.SUMMARY_DELAY_DEFAULT_MS);
      const summaryTimeout = setTimeout(() => this.typeSummary(messageIndex, res.summary), summaryDelay);
      this.pendingSummaryTimeouts.push(summaryTimeout);
    }

    // If this was the first message, add the conversation to the sidebar
    if (isFirstMessage) {
      this.addNewConversationToSidebar(prompt);
    }

    this.isLoading = false;
  }

  // Start rotating status messages during polling
  private startStatusRotation(opsMode = false): void {
    this.statusMessagePool = opsMode ? this.OPS_STATUS_MESSAGES : this.STATUS_MESSAGES;
    this.statusMessageIndex = 0;
    this.processingStatusMessage = this.statusMessagePool[0];
    this.statusRotationInterval = setInterval(() => {
      this.statusMessageIndex = (this.statusMessageIndex + 1) % this.statusMessagePool.length;
      this.processingStatusMessage = this.statusMessagePool[this.statusMessageIndex];
    }, this.STATUS_ROTATION_MS);
  }

  // Stop rotating status messages
  private stopStatusRotation(): void {
    if (this.statusRotationInterval) {
      clearInterval(this.statusRotationInterval);
      this.statusRotationInterval = null;
    }
    this.processingStatusMessage = '';
  }

  // Reset all async-related state flags
  private resetAsyncState(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
    if (this.fetchResultSubscription) {
      this.fetchResultSubscription.unsubscribe();
      this.fetchResultSubscription = null;
    }
    this.pollAttemptCount = 0;
    this.removeOpsPlaceholder();
    this.isLoading = false;
    this.isTyping = false;
    this.typingMessageIndex = null;
    this.stopStatusRotation();
    if (this.typingInterval) {
      clearInterval(this.typingInterval);
      this.typingInterval = null;
    }
  }

  /** Consume and clear the invoke-time messageId (for error / late assistant bubbles). */
  private takePendingMessageId(): string | undefined {
    const id = this.pendingMessageId || undefined;
    this.pendingMessageId = null;
    return id;
  }

  // Demo-safe error sanitizer. The UI never shows raw backend text — Oracle
  // errors (ORA-XXXXX), SQL failures, success:false payloads, HTTP errors,
  // timeouts, JSON parse failures all collapse to one of a small set of
  // friendly strings. Raw text is still logged to the console for debugging.
  //
  // `context` selects the friendly fallback when the error is generic:
  //   'submit'  — failed to send the user's prompt
  //   'fetch'   — failed to retrieve the prepared result
  //   'sql'     — SQL execution / data-retrieval failure
  //   'generic' — anything else
  private sanitizeApiError(
    raw: any,
    context: 'submit' | 'fetch' | 'sql' | 'generic' = 'generic'
  ): string {
    try {
      console.warn('[scraii-ai] raw error (sanitized for UI):', raw);
    } catch { /* noop */ }

    const rawStr = this.stringifyErrorInput(raw);
    const lower = rawStr.toLowerCase();

    const isTimeout =
      (raw && (raw.name === 'TimeoutError' || raw?.constructor?.name === 'TimeoutError')) ||
      lower.includes('timed out') ||
      lower.includes('timeout');
    if (isTimeout) {
      return API_ERROR_COPY.timeout;
    }

    // Any hint of a DB/SQL-layer failure (Oracle ORA codes, SELECT-list /
    // syntax / column / query messages, or explicit success:false text) is
    // collapsed to a single phrasing so the user never sees raw SQL details.
    const looksLikeSqlLeak =
      /ora-\d+/i.test(rawStr) ||
      lower.includes('select-list') ||
      lower.includes('select list') ||
      lower.includes('order by') ||
      lower.includes('oracle') ||
      lower.includes('syntax') ||
      lower.includes('sql') ||
      lower.includes('query failed') ||
      lower.includes('execution failed') ||
      lower.includes('database') ||
      lower.includes('expression') ||
      lower.includes('column') ||
      lower.includes('table');
    if (looksLikeSqlLeak || context === 'sql') {
      return "We couldn't retrieve the data for this request. Please try rephrasing your question or try again.";
    }

    // Network-layer hints
    if (
      lower.includes('network') ||
      lower.includes('failed to fetch') ||
      lower.includes('connection') ||
      lower.includes('offline')
    ) {
      return API_ERROR_COPY.offline;
    }

    switch (context) {
      case 'submit':
        return "We couldn't send your request right now. Please try again.";
      case 'fetch':
        return "We couldn't load the result. Please try again.";
      default:
        return API_ERROR_COPY.unknown;
    }
  }

  // Flatten anything throwable or a response body into a single searchable
  // string so sanitizeApiError can keyword-match it without null checks.
  private stringifyErrorInput(raw: any): string {
    if (raw == null) return '';
    if (typeof raw === 'string') return raw;
    const parts: string[] = [];
    if (typeof raw.name === 'string') parts.push(raw.name);
    if (typeof raw.message === 'string') parts.push(raw.message);
    if (typeof raw.error === 'string') parts.push(raw.error);
    else if (raw.error && typeof raw.error === 'object') {
      if (typeof raw.error.message === 'string') parts.push(raw.error.message);
      if (typeof raw.error.error === 'string') parts.push(raw.error.error);
    }
    if (parts.length === 0) {
      try { parts.push(JSON.stringify(raw)); } catch { /* noop */ }
    }
    return parts.join(' ');
  }

  // Extract SQL query from message content
  extractSqlQuery(content: string): string {
    // Extract SQL from "🧠 SQL Generated:\n{query}" format
    const sqlMatch = content.match(/🧠 SQL Generated:\s*([\s\S]+)/);
    return sqlMatch ? sqlMatch[1].trim() : "";
  }

  // Check if message contains SQL query
  hasSqlQuery(content: any): boolean {
    return content.includes("🧠 SQL Generated:");
  }

  // ============ SQL Toggle Card ============

  // Return the SQL for an assistant message regardless of whether it's a chart
  // or a table response. Mirrors the lookup pattern in buildExecuteSqlPayload.
  getSqlForMessage(msg: ChatMessage): string {
    if (!msg) return '';
    return msg.chartConfig?.sql_query || this.extractSqlQuery(msg.content || '') || '';
  }

  // Decide whether to render the "Show SQL" toggle button for a given message.
  // Gated so the toggle only appears on current-session results that have
  // actually rendered a chart or a table.
  canShowSqlToggle(msg: ChatMessage): boolean {
    if (!msg || msg.role !== 'assistant' || msg.isHistorical) return false;
    if (msg.needs_clarification) return false;
    const hasRendered = !!msg.chartInstance || msg.tableData !== undefined;
    if (!hasRendered) return false;
    return !!this.getSqlForMessage(msg);
  }

  // Flip the per-message SQL card visibility.
  toggleSqlCard(i: number): void {
    const msg = this.messages[i];
    if (!msg) return;
    msg.showSql = !msg.showSql;
    // When expanding, scroll the SQL card fully into view. 200ms lets the
    // max-height transition finish before we measure — otherwise we'd scroll
    // to the collapsed height and undershoot. Collapse path skips the scroll.
    if (msg.showSql) {
      setTimeout(() => this.scrollToSqlCard(i), 200);
    }
  }

  // Copy the formatted SQL for message `i` to the clipboard. Briefly flips
  // copiedSqlIndex so the button can show a "Copied!" checkmark for 2s.
  copySqlToClipboard(i: number): void {
    const msg = this.messages[i];
    if (!msg) return;
    const formatted = this.formatSql(this.getSqlForMessage(msg));
    if (!formatted) return;
    navigator.clipboard.writeText(formatted).then(() => {
      this.copiedSqlIndex = i;
      setTimeout(() => { this.copiedSqlIndex = null; }, 2000);
    }).catch((err) => {
      console.error('Failed to copy SQL:', err);
    });
  }

  // ============ multi_sql metric cards ============

  // Per-card SQL expand/collapse — independent of every other card.
  toggleMetricSql(msgIndex: number, metricIndex: number): void {
    const metric = this.messages[msgIndex]?.multiMetrics?.[metricIndex];
    if (metric) metric.showSql = !metric.showSql;
  }

  // Copy one card's SQL. copiedMetricKey drives the per-card checkmark.
  copyMetricSql(msgIndex: number, metricIndex: number): void {
    const metric = this.messages[msgIndex]?.multiMetrics?.[metricIndex];
    const formatted = this.formatSql(metric?.sql || '');
    if (!formatted) return;
    navigator.clipboard.writeText(formatted).then(() => {
      this.copiedMetricKey = `${msgIndex}-${metricIndex}`;
      setTimeout(() => { this.copiedMetricKey = null; }, 2000);
    }).catch((err) => console.error('Failed to copy SQL:', err));
  }

  // Single-row result → KPI tiles, one per column.
  getMetricTiles(metric: MultiMetricQuery): { label: string; value: string }[] {
    const row = metric?.result?.length === 1 ? metric.result[0] : null;
    if (!row) return [];
    return Object.keys(row).map(key => ({
      label: formatColumnDisplayName(key),
      value: this.formatMetricValue(key, row[key]),
    }));
  }

  // Display value for a KPI tile — thousands separators, capped decimals,
  // and a % suffix for columns the backend names as percentages/rates.
  private formatMetricValue(key: string, value: any): string {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value !== 'number') return String(value);
    const formatted = value.toLocaleString('en-US', { maximumFractionDigits: 2 });
    return /(_pct|_percent|percentage|_rate)$/i.test(key) ? `${formatted}%` : formatted;
  }

  // Lightweight SQL pretty-printer — uppercases keywords, breaks major
  // clauses onto their own line, and indents AND/OR/ON continuations.
  // Pure function; safe to call from the template.
  // Known limitation: does not escape keywords that happen to appear inside
  // string literals. Syntax highlighting is explicitly out of scope.
  formatSql(sql: string): string {
    if (!sql || typeof sql !== 'string') return '';

    // Normalize every whitespace run (including existing newlines) to a single space.
    let s = sql.replace(/\s+/g, ' ').trim();

    // Major keywords — break the line, no indent.
    // Multi-word entries must appear before single-word prefixes so "LEFT JOIN"
    // matches before "JOIN" alone.
    const majors = [
      'LEFT JOIN', 'RIGHT JOIN', 'INNER JOIN', 'OUTER JOIN',
      'FULL JOIN', 'CROSS JOIN',
      'GROUP BY', 'ORDER BY', 'INSERT INTO',
      'SELECT', 'FROM', 'WHERE', 'JOIN', 'HAVING',
      'LIMIT', 'UNION', 'INSERT', 'UPDATE', 'DELETE',
      'SET', 'VALUES', 'INTO'
    ];
    for (const kw of majors) {
      const pattern = kw.replace(/\s+/g, '\\s+');
      s = s.replace(new RegExp(`\\b${pattern}\\b`, 'gi'), '\n' + kw);
    }

    // Continuation keywords — break the line and indent 2 spaces.
    const continuations = ['AND', 'OR', 'ON'];
    for (const kw of continuations) {
      s = s.replace(new RegExp(`\\b${kw}\\b`, 'gi'), '\n  ' + kw);
    }

    // Inline keywords — uppercase only, no newline.
    const inlineKeywords = [
      'CASE', 'WHEN', 'THEN', 'ELSE', 'END',
      'AS', 'IN', 'IS', 'NOT', 'NULL', 'LIKE', 'BETWEEN',
      'DISTINCT', 'ASC', 'DESC'
    ];
    for (const kw of inlineKeywords) {
      s = s.replace(new RegExp(`\\b${kw}\\b`, 'gi'), kw);
    }

    // Drop a leading newline introduced by the first major keyword match.
    return s.replace(/^\s*\n/, '').trim();
  }

  // Open query execution confirmation dialog
  openQueryDialog(messageIndex: number) {
    const message = this.messages[messageIndex];
    this.queryToExecute = this.extractSqlQuery(message.content);
    this.currentQueryMessageIndex = messageIndex;
    this.showQueryDialog = true;
  }

  // Close query execution dialog
  closeQueryDialog(onexe?: any) {
    this.showQueryDialog = false;
    onexe ? "" : (this.queryToExecute = "");
    this.currentQueryMessageIndex = null;
  }

  // Execute the SQL query
  executeQuery(index?: number) {
    if (typeof index === 'number') {
      this.currentQueryMessageIndex = index;
      const message = this.messages[index];
      this.queryToExecute = this.extractSqlQuery(message.content);
    }

    if (!this.queryToExecute) return;

    // Clear per-message table data to prevent dual tables when manually executing
    if (this.currentQueryMessageIndex !== null && this.currentQueryMessageIndex !== undefined) {
      const msg = this.messages[this.currentQueryMessageIndex];
      if (msg) {
        msg.tableData = undefined;
        msg.tableColumns = undefined;
        msg.tableError = undefined;
        msg.isLoadingTable = false;
      }
    }

    this.isExecutingQuery = true;
    this.showQueryResults = true; // Show results section immediately to display loader
    this.activeResultIndex = this.currentQueryMessageIndex;
    this.queryError = null; // Clear any previous errors

    // IMPORTANT: capture the message index BEFORE closing the dialog.
    // closeQueryDialog() nulls this.currentQueryMessageIndex, so the
    // executeSql branch below must use this local, not the field, or it
    // would silently fall through to the legacy testSqlQuery path.
    const executionIndex = this.currentQueryMessageIndex;

    this.closeQueryDialog(true);

    // New executeSql path (default) — returns result + summary in one call
    if (!this.isLegacyAiMode() && executionIndex !== null) {
      if (this.executeSqlSubscription) {
        this.executeSqlSubscription.unsubscribe();
      }
      const execPayload = this.buildExecuteSqlPayload(executionIndex, false);
      this.executeSqlSubscription = this.chatService.executeSql(execPayload)
        .pipe(timeout(this.EXECUTE_SQL_TIMEOUT_MS))
        .subscribe({
          next: (res: ExecuteSqlResponse) => this.handleExecuteQueryResponse(res, executionIndex),
          error: (error: any) => {
            if (this.activeResultIndex !== executionIndex) return;
            this.isExecutingQuery = false;
            this.queryError = this.sanitizeApiError(error, 'sql');
            this.queryResults = [];
            this.columnDefs = [];
            this.queryResultColumns = [];
            console.error('executeSql query dialog error:', error);
          }
        });
      return;
    }

    // Legacy testSqlQuery path (ai-mode === 'true')
    const payload = {
      sqlQuery: this.queryToExecute,
    };

    this.chatService.testSqlQuery(payload)
      .pipe(timeout(this.EXECUTE_SQL_TIMEOUT_MS))
      .subscribe({
        next: (response: any) => {
          // Discard result if user navigated away or closed results
          if (this.activeResultIndex !== executionIndex) return;

          // Extract results from API response
          if (response && Array.isArray(response)) {
            this.queryResults = response;
          } else if (response && response.data && Array.isArray(response.data)) {
            this.queryResults = response.data;
          } else if (
            response &&
            response.results &&
            Array.isArray(response.results)
          ) {
            this.queryResults = response.results;
          } else {
            this.queryResults = [];
          }

          // Dynamically extract column names and create columnDefs
          if (this.queryResults.length > 0) {
            const columns = Object.keys(this.queryResults[0]);
            this.queryResultColumns = columns;

            this.columnDefs = this.buildSqlTableColumns(columns);
            setTimeout(() => this.scrollToBottom(), 100);
          } else {
            this.queryResultColumns = [];
            this.columnDefs = [];
          }

          this.isExecutingQuery = false;
        },
        error: (error: any) => {
          // Discard error if user navigated away or closed results
          if (this.activeResultIndex !== executionIndex) return;
          this.isExecutingQuery = false;
          this.showQueryResults = true;
          this.queryResults = [];
          this.queryResultColumns = [];
          this.columnDefs = [];

          this.queryError = this.sanitizeApiError(error, 'sql');

          // Enable scrolling to show the error card
          setTimeout(() => this.scrollToBottom(), 100);
          console.error('SQL Execution Error:', error);

          // Keeping snackbar as additional feedback, but primary is now the error card
          this.dialogService.openSnackBar(this.queryError!, "OK");
        }
      });
  }

  closeQueryResults() {
    this.showQueryResults = false;
    this.queryResults = [];
    this.queryResultColumns = [];
    this.columnDefs = [];
    this.activeResultIndex = null;
    this.isResultFullScreen = false; // Reset full screen state
    this.queryError = null;
    this.isExecutingQuery = false;
  }

  // ============ Chart Methods ============
  // Check if message contains chart configuration
  hasChartData(message: ChatMessage): boolean {
    return !!message.chartConfig;
  }

  // Extract chart configuration from message content
  extractChartConfig(content: string): ChartConfig | null {
    try { // Try to extract JSON from the message
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const config = JSON.parse(jsonMatch[0]);
        if (config.chart_type && config.sql) {
          return config as ChartConfig;
        }
      }
      return null;
    } catch (error) {
      console.error("Error parsing chart config:", error);
      return null;
    }
  }

  // Find matching key case-insensitively and fuzzy (ignoring underscores/spaces)
  findMatchingKey(item: any, key: string): string | undefined {
    if (!item || !key) return undefined;

    // 1. Direct match
    if (Object.prototype.hasOwnProperty.call(item, key)) return key;

    const keys = Object.keys(item);
    const lowerKey = key.toLowerCase();

    // 2. Case-insensitive match 
    const caseMatch = keys.find(k => k.toLowerCase() === lowerKey);
    if (caseMatch) return caseMatch;

    // 3. Fuzzy match (remove underscores and spaces) Like "Dispense Volume" -> "dispensevolume" | "dispense_volume" -> "dispensevolume"
    const cleanKey = lowerKey.replace(/[\s_]/g, '');
    const fuzzyMatch = keys.find(k => k.toLowerCase().replace(/[\s_]/g, '') === cleanKey);
    if (fuzzyMatch) return fuzzyMatch;

    return undefined;
  }

  /**
   * Transform data for stacked charts  Detects grouping field and creates separate datasets for each group
   /** 
   * Example input:
   * [
   *   { month: "2025-01", rx_type: "N", dispense_count: 94529 },
   *   { month: "2025-01", rx_type: "R", dispense_count: 230554 },
   *   { month: "2025-02", rx_type: "N", dispense_count: 94106 },
   *   { month: "2025-02", rx_type: "R", dispense_count: 211420 }
   * ]
   * 
   * Output: { labels: ["2025-01", "2025-02"], datasets: [{ label: "N", data: [94529, 94106] }, { label: "R", data: [230554, 211420] }] }
   */
  private transformDataForStacked(
    data: any[],
    config: ChartConfig,
    matchedXAxis: string,
    matchedYAxis: string
  ): { labels: any[], datasets: any[] } | null {
    if (!data || data.length === 0) return null;

    const firstItem = data[0];

    // STEP 1: Determine data format (long vs wide)
    // Long format: Multiple rows per x-axis value with a grouping field (e.g., rx_type)
    // Wide format: Single row per x-axis value with multiple y-columns (e.g., new_rx, refill_rx)

    // Check if y_axis column exists in data
    const yAxisExists = this.findMatchingKey(firstItem, matchedYAxis);

    if (!yAxisExists) {
      // WIDE FORMAT: Find all numeric columns except x_axis
      const numericColumns = Object.keys(firstItem).filter(key => {
        if (key === matchedXAxis || key.toLowerCase() === matchedXAxis.toLowerCase()) {
          return false; // Exclude x-axis
        }
        const value = firstItem[key];
        return typeof value === 'number' || !isNaN(Number(value));
      });

      if (numericColumns.length === 0) {
        console.error('No numeric columns found for wide-format stacked chart');
        return null;
      }

      // Extract labels (x-axis values)
      const labels = data.map(item => item[matchedXAxis]);

      // Create datasets from numeric columns
      const colors = this.CHART_COLORS;

      const datasets = numericColumns.map((colName, index) => {
        const color = colors[index % colors.length];
        const values = data.map(item => item[colName]);
        const isLine = config.chart_type === 'line' || config.chart_type === 'area_line' || config.chart_variant === 'area' || config.chart_variant === 'stepped';
        const isArea = config.chart_type === 'area_line' || config.chart_variant === 'area';

        return {
          label: this.formatChartLabel(colName),
          data: values,
          backgroundColor: color,
          borderColor: color,
          pointBackgroundColor: color,
          borderWidth: isLine ? 2 : 0,
          fill: isArea,
          tension: isArea ? 0.4 : 0,
        };
      });

      return {
        labels: labels,
        datasets: datasets
      };
    }

    // LONG FORMAT: y_axis column exists, need to find grouping field(s)
    // Identify all candidate fields (not x_axis or y_axis)
    const candidateFields = Object.keys(firstItem).filter(key =>
      key !== matchedXAxis &&
      key !== matchedYAxis &&
      key.toLowerCase() !== matchedXAxis.toLowerCase() &&
      key.toLowerCase() !== matchedYAxis.toLowerCase()
    );

    if (candidateFields.length === 0) {
      console.warn('No grouping field found for long-format stacked chart');
      return null;
    }

    // Calculate unique value counts for each candidate field
    const uniqueCounts = candidateFields.map(field => ({
      field,
      count: new Set(data.map(item => item[field])).size
    }));

    // Sort by count to identify grouping vs stacking dimensions
    uniqueCounts.sort((a, b) => b.count - a.count);

    // CHECK FOR 3-DIMENSIONAL DATA (grouped-stacked chart)
    if (candidateFields.length >= 2) {
      // console.log('🔍 Detected 3-dimensional data for grouped-stacked chart');

      // Grouping dimension: field with MORE unique values (e.g., sp_description with 3 values)
      // Stacking dimension: field with FEWER unique values (e.g., rx_type with 2 values)
      const groupingField = uniqueCounts[0].field;
      const stackingField = uniqueCounts[1].field;

      // console.log(`📊 Grouping by: ${groupingField}, Stacking by: ${stackingField}`);

      // Extract unique values for each dimension
      const uniqueXValues: any[] = [];
      const seenX = new Set();
      data.forEach(item => {
        const xValue = item[matchedXAxis];
        if (!seenX.has(xValue)) {
          uniqueXValues.push(xValue);
          seenX.add(xValue);
        }
      });

      const groupValues = [...new Set(data.map(item => item[groupingField]))];
      const stackValues = [...new Set(data.map(item => item[stackingField]))];

      // console.log(`Groups: ${groupValues.length}, Stacks: ${stackValues.length}, X-values: ${uniqueXValues.length}`);

      // Base colors for each group
      // Helper to extract RGB values from HEX string "#RRGGBB"
      const hexToRgb = (hex: string): number[] => {
        const h = hex.replace('#', '');
        return [parseInt(h.substring(0, 2), 16), parseInt(h.substring(2, 4), 16), parseInt(h.substring(4, 6), 16)];
      };

      // Base colors for each group derived from CHART_COLORS
      const groupBaseColors = this.CHART_COLORS.map(c => hexToRgb(c));

      // Helper function to generate colors for grouped-stacked bars
      const getGroupedColor = (groupIndex: number, stackIndex: number, isBackground: boolean) => {
        const baseColor = groupBaseColors[groupIndex % groupBaseColors.length];
        // Create shades within the same color family for stacking
        // Darker shades for earlier stack values, lighter for later ones
        const opacity = isBackground ? 1 : 1; // Solid colors for all
        const adjustment = stackIndex * 30; // Lighten subsequent stacks

        return isBackground
          ? `rgba(${Math.min(baseColor[0] + adjustment, 255)}, ${Math.min(baseColor[1] + adjustment, 255)}, ${Math.min(baseColor[2] + adjustment, 255)}, ${opacity})`
          : `rgba(${baseColor[0]}, ${baseColor[1]}, ${baseColor[2]}, ${opacity})`;
      };

      // Create datasets for each combination of group × stack
      const datasets: any[] = [];

      groupValues.forEach((groupValue, groupIndex) => {
        stackValues.forEach((stackValue, stackIndex) => {
          // For each x-axis value, find the matching data point
          const dataPoints = uniqueXValues.map(xValue => {
            const matchingItem = data.find(
              item => item[matchedXAxis] === xValue &&
                item[groupingField] === groupValue &&
                item[stackingField] === stackValue
            );
            return matchingItem ? matchingItem[matchedYAxis] : 0;
          });

          datasets.push({
            label: `${groupValue} - ${stackValue}`,
            data: dataPoints,
            backgroundColor: getGroupedColor(groupIndex, stackIndex, true),
            borderColor: getGroupedColor(groupIndex, stackIndex, false),
            borderWidth: 0, // Grouped-stacked is always bar-like
            stack: `stack-${groupIndex}`, // Critical: groups bars together
          });
        });
      });

      return {
        labels: uniqueXValues,
        datasets: datasets
      };
    }

    // 2-DIMENSIONAL DATA (simple stacked chart) - backward compatibility
    const groupingField = config.group_by || uniqueCounts[0].field;

    // console.log(`📊 Simple stacked chart by: ${groupingField}`);

    // Extract unique x-axis labels (in order of appearance)
    const uniqueXValues: any[] = [];
    const seenX = new Set();
    data.forEach(item => {
      const xValue = item[matchedXAxis];
      if (!seenX.has(xValue)) {
        uniqueXValues.push(xValue);
        seenX.add(xValue);
      }
    });

    // Extract unique group values
    const groupValues = [...new Set(data.map(item => item[groupingField]))];

    // Create datasets for each group
    const colors = this.CHART_COLORS;

    const datasets = groupValues.map((groupValue, index) => {
      const color = colors[index % colors.length];

      // For each x-axis label, find the corresponding y value for this group
      const dataPoints = uniqueXValues.map(xValue => {
        const matchingItem = data.find(
          item => item[matchedXAxis] === xValue && item[groupingField] === groupValue
        );
        return matchingItem ? matchingItem[matchedYAxis] : 0; // Use 0 for missing data points
      });

      const isLine = config.chart_type === 'line' || config.chart_type === 'area_line' || config.chart_variant === 'area' || config.chart_variant === 'stepped';
      const isArea = config.chart_type === 'area_line' || config.chart_variant === 'area';

      return {
        label: String(groupValue),
        data: dataPoints,
        backgroundColor: color,
        borderColor: color,
        pointBackgroundColor: color,
        borderWidth: isLine ? 2 : 0,
        fill: isArea,
        tension: isArea ? 0.4 : 0,
      };
    });

    return {
      labels: uniqueXValues,
      datasets: datasets
    };
  }

  // Supported chart types and variants
  // private readonly SUPPORTED_TYPES = ['bar', 'line', 'horizontalBar', 'mixed', 'pie', 'doughnut'];
  private readonly SUPPORTED_TYPES = ['bar', 'line', 'mixed', 'area_line'];
  private readonly SUPPORTED_VARIANTS = ['simple', 'grouped', 'multi', 'clustered', 'stacked', 'cluster_stacked', 'horizontal', 'area', 'stepped'];

  // Control charts are rendered by the standalone D3 component
  // <app-scraii-control-chart> (bound to msg.resultData + msg.chartConfig in
  // the template), NOT by the Chart.js renderChart() path. These helpers let
  // the various render entry points short-circuit the Chart.js / SUPPORTED_TYPES
  // pipeline and simply clear loading state so the template can mount the D3 chart.
  private isControlChart(config: any): boolean {
    return config?.chart_type === 'control_chart';
  }

  private isGanttChart(config: any): boolean {
    return config?.patient_journey === true && config?.chart_type === 'gantt_chart';
  }

  /** Detect journey-category prompts for POC gantt injection before API support. */
  private isJourneyPocPrompt(prompt: string): boolean {
    const lower = (prompt || '').toLowerCase();
    return /patient\s+journey|journey\s+over\s+time/.test(lower)
      || (/patient\s+id/.test(lower) && /journey|transaction|pending/.test(lower));
  }

  // Reset loading/error flags so the control-chart container becomes visible.
  // No Chart.js instance is created — the D3 component owns its own lifecycle.
  private prepareControlChart(messageIndex: number): void {
    const message = this.messages[messageIndex];
    if (!message) return;
    message.isGenerating = false;
    this.isGeneratingChart = false;
    message.isUnsupportedChart = false;
    message.chartError = undefined;
    message.chartEmpty = false;
    if (message.chartInstance) {
      message.chartInstance.destroy();
      message.chartInstance = null;
    }
    this.destroyBrush(messageIndex);
    message.brushState = undefined;
    message.activeView = message.activeView || 'chart';
    this.activeChartIndex = messageIndex;
  }

  // Reset loading/error flags so the gantt-chart container becomes visible.
  private prepareGanttChart(messageIndex: number): void {
    const message = this.messages[messageIndex];
    if (!message) return;
    message.isGenerating = false;
    this.isGeneratingChart = false;
    message.isUnsupportedChart = false;
    message.chartError = undefined;
    message.chartEmpty = false;
    if (message.chartInstance) {
      message.chartInstance.destroy();
      message.chartInstance = null;
    }
    this.destroyBrush(messageIndex);
    message.brushState = undefined;
    message.activeView = message.activeView || 'chart';
    this.activeChartIndex = messageIndex;
    this.isLoading = false;
  }

  /** Load demo gantt data from assets when API is not yet returning rows. */
  private loadGanttPocData(messageIndex: number): void {
    const message = this.messages[messageIndex];
    if (!message) return;

    forkJoin([
      this.dataService.getJson('GanttChart-New'),
      this.dataService.getJson('GanttChart-New-Program'),
    ]).subscribe({
      next: ([transactions, programs]: [any, any]) => {
        message.resultData = Array.isArray(transactions) ? transactions : [];
        message.programResultData = Array.isArray(programs) ? programs : [];
        if (message.resultData.length > 0) {
          this.prepareGanttChart(messageIndex);
          setTimeout(() => this.scrollToChart(messageIndex), 300);
        } else {
          message.isGenerating = false;
          this.isGeneratingChart = false;
          message.chartEmpty = true;
          message.summaryHidden = true;
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Gantt POC data load failed:', err);
        message.isGenerating = false;
        this.isGeneratingChart = false;
        message.chartError = 'Failed to load patient journey demo data.';
        message.summaryHidden = true;
        this.isLoading = false;
      },
    });
  }

  // Brush navigator: show when category count exceeds this threshold
  private readonly BRUSH_THRESHOLD = 12;
  // Map of brush canvas event listener cleanup functions, keyed by message index
  private brushCleanupMap: Map<number, () => void> = new Map();
  private readonly AXIS_TITLE_MAX_LENGTH = 40;
  private chartAxisTitleTooltipEl: HTMLDivElement | null = null;
  private readonly axisTitleTooltipPlugin: Plugin = {
    id: 'scraiiAxisTitleTooltip',
    afterEvent: (chart, args) => this.handleAxisTitleTooltipEvent(chart, args.event),
    beforeDestroy: () => this.hideChartAxisTitleTooltip(),
  };


  // Check if legacy AI mode is active (testSqlQuery path)
  private isLegacyAiMode(): boolean {
    return sessionStorage.getItem(this.AI_MODE_KEY) === 'true';
  }

  // Get the original user prompt for an assistant message at the given index
  private getOriginalPrompt(messageIndex: number): string {
    const message = this.messages[messageIndex];
    if (message?.originalPrompt) return message.originalPrompt;
    // Walk backwards to find the preceding user message
    for (let j = messageIndex - 1; j >= 0; j--) {
      if (this.messages[j]?.role === 'user') {
        return this.messages[j].content;
      }
    }
    return '';
  }

  // Build an ExecuteSqlPayload from a ChatMessage's chart config and its originating prompt
  private buildExecuteSqlPayload(messageIndex: number, forChart: boolean): ExecuteSqlPayload {
    const message = this.messages[messageIndex];
    const question = this.getOriginalPrompt(messageIndex);
    const sqlQuery = message.chartConfig?.sql_query || this.extractSqlQuery(message.content);

    const payload: ExecuteSqlPayload = { question, sql_query: sqlQuery };

    if (forChart && message.chartConfig) {
      payload.chart_type = message.chartConfig.chart_type;
      payload.x_axis = message.chartConfig.x_axis;
      // API expects y_axis as string; use first element if array
      const yAxis = message.chartConfig.y_axis;
      payload.y_axis = Array.isArray(yAxis) ? yAxis[0] : yAxis;
      payload.title = message.chartConfig.title;
    }

    return payload;
  }

  // Shared handler for executeSql API response — wires result into chart or table rendering
  private handleExecuteSqlResponse(response: ExecuteSqlResponse, messageIndex: number): void {
    const message = this.messages[messageIndex];
    if (!message) return;

    if (!response.success || response.error) {
      const errorMsg = this.sanitizeApiError(response.error, 'sql');
      // Determine whether this was a chart or table request
      if (message.chartConfig) {
        message.isGenerating = false;
        this.isGeneratingChart = false;
        message.chartError = errorMsg;
        message.summaryHidden = true;
      } else {
        message.isLoadingTable = false;
        this.isAutoExecuting = false;
        message.tableError = errorMsg;
      }
      console.error('handleExecuteSqlResponse error:', response);
      return;
    }

    const resultData = response.result && Array.isArray(response.result) ? response.result : [];

    // Cache the result data on the message for regeneration
    message.resultData = resultData;
    message.summary = response.summary || undefined;
    message.citationSources = this.extractCitationSources(response);
    message.summaryHidden = false;

    if (message.chartConfig && this.isControlChart(message.chartConfig)) {
      // Control chart path — render via the standalone D3 component.
      if (resultData.length > 0) {
        this.prepareControlChart(messageIndex);
        setTimeout(() => this.scrollToChart(messageIndex), 300);
      } else {
        message.isGenerating = false;
        this.isGeneratingChart = false;
        message.chartEmpty = true;
        message.summaryHidden = !response.summary;
      }
    } else if (message.chartConfig && this.isGanttChart(message.chartConfig)) {
      const programRows = (response as any).program_result;
      if (Array.isArray(programRows)) {
        message.programResultData = programRows;
      }
      if (resultData.length > 0) {
        this.prepareGanttChart(messageIndex);
        setTimeout(() => this.scrollToChart(messageIndex), 300);
      } else if (isScraiiGanttPocEnabled()) {
        this.loadGanttPocData(messageIndex);
      } else {
        message.isGenerating = false;
        this.isGeneratingChart = false;
        message.chartEmpty = true;
        message.summaryHidden = !response.summary;
      }
    } else if (message.chartConfig) {
      // Chart path — validate type/variant before rendering
      const type = message.chartConfig.chart_type;
      const variant = message.chartConfig.chart_variant || 'simple';
      if (!this.SUPPORTED_TYPES.includes(type) || !this.SUPPORTED_VARIANTS.includes(variant)) {
        message.isUnsupportedChart = true;
        message.isGenerating = false;
        this.isGeneratingChart = false;
        message.summaryHidden = true;
        return;
      }
      if (resultData.length > 0) {
        this.renderChart(message.chartConfig as any, resultData);
        setTimeout(() => this.scrollToChart(messageIndex), 300);
      } else {
        // Regenerate path returned zero rows — same "valid but empty" case as
        // the async flow; route to the info card via chartEmpty, not chartError.
        message.isGenerating = false;
        this.isGeneratingChart = false;
        message.chartEmpty = true;
        message.summaryHidden = !response.summary;
      }
    } else {
      // Table path
      this.populateTableFromData(messageIndex, resultData);
    }

    // Trigger summary typing if present
    if (response.summary) {
      const delay = message.chartConfig ? this.SUMMARY_DELAY_CHART_MS : this.SUMMARY_DELAY_TABLE_MS;
      const summaryTimeout = setTimeout(() => this.typeSummary(messageIndex, response.summary!), delay);
      this.pendingSummaryTimeouts.push(summaryTimeout);
    }
  }

  // Handle executeSql response for the manual query dialog (component-level state, not per-message)
  private handleExecuteQueryResponse(response: ExecuteSqlResponse, executionIndex: number | null): void {
    if (this.activeResultIndex !== executionIndex) return;

    if (!response.success || response.error) {
      this.isExecutingQuery = false;
      this.queryError = this.sanitizeApiError(response.error, 'sql');
      this.queryResults = [];
      this.queryResultColumns = [];
      this.columnDefs = [];
      console.error('handleExecuteQueryResponse error:', response);
      return;
    }

    const results = response.result && Array.isArray(response.result) ? response.result : [];
    this.queryResults = results;

    if (results.length > 0) {
      const columns = Object.keys(results[0]);
      this.queryResultColumns = columns;
      this.columnDefs = this.buildSqlTableColumns(columns);
      setTimeout(() => this.scrollToBottom(), 100);
    } else {
      this.queryResultColumns = [];
      this.columnDefs = [];
    }

    this.isExecutingQuery = false;
  }

  // Generate and display chart
  // forceLegacy: when true, always uses testSqlQuery (skips executeSql). Used by async flow fallback.
  generateChart(messageIndex: number, forceLegacy?: boolean) {
    // Prevent multiple concurrent chart generations
    if (this.isGeneratingChart && this.activeChartIndex !== messageIndex) {
      this.dialogService.openSnackBar("Please wait for the current chart to finish generating", "OK");
      return;
    }

    const message = this.messages[messageIndex];
    const chartConfig = message.chartConfig;

    if (!chartConfig) {
      this.dialogService.openSnackBar("Invalid chart configuration", "OK");
      return;
    }

    // Clear existing error before generating
    message.chartError = undefined;
    message.isUnsupportedChart = false; // Reset unsupported flag
    // Clear empty-state flag — a re-generation might return rows this time.
    message.chartEmpty = false;

    // Control chart regeneration — render from cached rows via the D3 component.
    if (this.isControlChart(chartConfig)) {
      message.summaryHidden = false;
      if (message.resultData && message.resultData.length > 0) {
        this.prepareControlChart(messageIndex);
        setTimeout(() => this.scrollToChart(messageIndex), 300);
      } else {
        message.isGenerating = false;
        this.isGeneratingChart = false;
        message.chartEmpty = true;
      }
      return;
    }

    // Gantt chart regeneration — render from cached rows via app-scraii-gantt-chart.
    if (this.isGanttChart(chartConfig)) {
      message.summaryHidden = false;
      if (message.resultData && message.resultData.length > 0) {
        this.prepareGanttChart(messageIndex);
        setTimeout(() => this.scrollToChart(messageIndex), 300);
      } else if (isScraiiGanttPocEnabled()) {
        this.loadGanttPocData(messageIndex);
      } else {
        message.isGenerating = false;
        this.isGeneratingChart = false;
        message.chartEmpty = true;
      }
      return;
    }

    // Validate chart type and variant
    const type = chartConfig.chart_type;
    const variant = chartConfig.chart_variant || 'simple';

    const isTypeSupported = this.SUPPORTED_TYPES.includes(type);
    const isVariantSupported = this.SUPPORTED_VARIANTS.includes(variant);

    if (!isTypeSupported || !isVariantSupported) {
      console.warn(`⚠️ Unsupported chart type: ${type} or variant: ${variant}`);
      message.isUnsupportedChart = true;
      message.summaryHidden = true;
      message.isGenerating = false;
      this.isGeneratingChart = false;
      return;
    }

    // Cancel any in-progress chart request
    if (this.chartSubscription) {
      this.chartSubscription.unsubscribe();
    }

    // Set as active chart
    this.activeChartIndex = messageIndex;

    // IMPORTANT: Destroy existing chart instance to prevent duplicates
    if (message.chartInstance) {
      message.chartInstance.destroy();
      message.chartInstance = null;
    }
    // Also cleanup brush navigator
    this.destroyBrush(messageIndex);
    message.brushState = undefined;

    message.isGenerating = true;
    this.isGeneratingChart = true;
    this.currentChartTitle = chartConfig.title || "Chart Visualization";
    message.citationSources = undefined;
    message.summaryHidden = false; // Re-show summary on regeneration

    // Use cached result data if available (from async response) — skip API call
    if (message.resultData && message.resultData.length > 0) {
      this.renderChart(chartConfig, message.resultData);
      setTimeout(() => this.scrollToChart(messageIndex), 300);
      return;
    }

    // New executeSql path (default) — only for user-initiated actions, not async flow fallback
    if (!forceLegacy && !this.isLegacyAiMode()) {
      if (this.executeSqlSubscription) {
        this.executeSqlSubscription.unsubscribe();
      }
      const execPayload = this.buildExecuteSqlPayload(messageIndex, true);
      this.executeSqlSubscription = this.chatService.executeSql(execPayload)
        .pipe(timeout(this.EXECUTE_SQL_TIMEOUT_MS))
        .subscribe({
          next: (res: ExecuteSqlResponse) => this.handleExecuteSqlResponse(res, messageIndex),
          error: (error: any) => {
            message.isGenerating = false;
            this.isGeneratingChart = false;
            message.chartError = this.sanitizeApiError(error, 'sql');
            message.summaryHidden = true;
            console.error("executeSql chart error:", error);
          }
        });
      return;
    }

    // Legacy testSqlQuery path (ai-mode === 'true')
    const payload = { sqlQuery: chartConfig.sql_query, };

    // Call real API with cancellation support
    this.chartSubscription = this.chatService.testSqlQuery(payload).pipe(timeout(this.EXECUTE_SQL_TIMEOUT_MS)).subscribe({
      next: (response: any) => {
        // Extract results from API response
        let chartData = [];
        if (response && Array.isArray(response)) {
          chartData = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          chartData = response.data;
        } else if (
          response &&
          response.results &&
          Array.isArray(response.results)
        ) {
          chartData = response.results;
        } else {
          chartData = [];
        }

        // Render the chart with real data
        this.renderChart(chartConfig, chartData);
        // NOTE: isGenerating is now set to false inside renderChart() after chart instance is created
        setTimeout(() => this.scrollToChart(messageIndex), 300); // Scroll to the chart after it's rendered
      },
      error: (error: any) => {
        message.isGenerating = false;
        this.isGeneratingChart = false;
        message.chartError = this.sanitizeApiError(error, 'sql');
        message.summaryHidden = true;
        console.error("Chart generation error:", error);
      }
    });
  }

  // Generate chart directly from data (skips testSqlQuery HTTP call)
  private generateChartFromData(messageIndex: number, data: any[]): void {
    if (this.isGeneratingChart && this.activeChartIndex !== messageIndex) {
      this.dialogService.openSnackBar("Please wait for the current chart to finish generating", "OK");
      return;
    }

    const message = this.messages[messageIndex];
    const chartConfig = message.chartConfig;
    if (!chartConfig) {
      this.dialogService.openSnackBar("Invalid chart configuration", "OK");
      return;
    }

    // Control charts bypass the Chart.js pipeline — the D3 component renders
    // itself from msg.resultData; we only clear loading flags here.
    if (this.isControlChart(chartConfig)) {
      this.prepareControlChart(messageIndex);
      setTimeout(() => this.scrollToChart(messageIndex), 300);
      return;
    }

    if (this.isGanttChart(chartConfig)) {
      message.resultData = data;
      this.prepareGanttChart(messageIndex);
      setTimeout(() => this.scrollToChart(messageIndex), 300);
      return;
    }

    message.chartError = undefined;
    message.isUnsupportedChart = false;
    // Re-rendering from cached data — clear empty flag so the info card
    // doesn't linger if the same resultData is reused.
    message.chartEmpty = false;

    const type = chartConfig.chart_type;
    const variant = chartConfig.chart_variant || 'simple';
    if (!this.SUPPORTED_TYPES.includes(type) || !this.SUPPORTED_VARIANTS.includes(variant)) {
      message.isUnsupportedChart = true;
      message.summaryHidden = true;
      message.isGenerating = false;
      this.isGeneratingChart = false;
      return;
    }

    if (this.chartSubscription) {
      this.chartSubscription.unsubscribe();
    }

    this.activeChartIndex = messageIndex;
    if (message.chartInstance) {
      message.chartInstance.destroy();
      message.chartInstance = null;
    }
    this.destroyBrush(messageIndex);
    message.brushState = undefined;

    message.isGenerating = true;
    this.isGeneratingChart = true;
    this.currentChartTitle = chartConfig.title || "Chart Visualization";

    // Use data directly — no HTTP call needed
    this.renderChart(chartConfig, data);
    setTimeout(() => this.scrollToChart(messageIndex), 300);
  }

  // Scroll to the specific chart element for a given message index
  scrollToChart(messageIndex: number): void {
    if (!this.chatContainer?.nativeElement) {
      return;
    }

    // Find the specific chart container element by targeting the canvas ID's parent
    const canvasId = `chartCanvas-${messageIndex}`;
    const canvasElement = document.getElementById(canvasId);

    // Control charts have no <canvas> — fall back to the control-chart container.
    const chartContainer =
      (canvasElement && canvasElement.closest('.scraii-ai-chart-container')) ||
      document.getElementById(`controlChart-${messageIndex}`) ||
      document.getElementById(`ganttChart-${messageIndex}`);

    if (chartContainer) {
      // Scroll the specific chart into view with smooth behavior
      chartContainer.scrollIntoView({
        behavior: "smooth",
        block: "nearest", // Don't scroll if already visible
        inline: "nearest",
      });
    }
  }

  // Helper function to format column names into readable chart labels
  private formatChartLabel(columnName: string): string {
    return formatColumnDisplayName(columnName);
  }

  formatTime(seconds: number | undefined): string {
    if (seconds === undefined || seconds === null || isNaN(seconds)) return '';
    if (seconds < 1) return `${(seconds * 1000).toFixed(0)}ms`;
    return `${seconds.toFixed(2)}s`;
  }

  private getAxisTitleFullText(axis: string | string[] | undefined | null): string {
    if (axis == null) return '';
    const names = (Array.isArray(axis) ? axis : [axis])
      .filter(n => typeof n === 'string' && n.trim().length > 0);
    if (names.length === 0) return '';

    if (names.length === 1) {
      return this.formatChartLabel(names[0]);
    }
    if (names.length === 2) {
      return `${this.formatChartLabel(names[0])} / ${this.formatChartLabel(names[1])}`;
    }
    return `${this.formatChartLabel(names[0])} & ${names.length - 1} others`;
  }

  private getAxisTitleDisplayText(axis: string | string[] | undefined | null): string {
    const full = this.getAxisTitleFullText(axis);
    if (!full) return '';
    if (full.length <= this.AXIS_TITLE_MAX_LENGTH) return full;
    return full.slice(0, this.AXIS_TITLE_MAX_LENGTH - 1).trimEnd() + '…';
  }

  private isAxisTitleTruncated(axis: string | string[] | undefined | null): boolean {
    const full = this.getAxisTitleFullText(axis);
    return full.length > this.AXIS_TITLE_MAX_LENGTH;
  }

  private handleAxisTitleTooltipEvent(chart: any, event: any): void {
    const pluginOpts = chart?.options?.plugins?.scraiiAxisTitleTooltip;
    if (!pluginOpts?.yTruncated || !pluginOpts?.yFull) {
      this.hideChartAxisTitleTooltip();
      return;
    }

    const eventType = event?.type;
    if (eventType === 'mouseout' || eventType === 'mouseleave') {
      this.hideChartAxisTitleTooltip();
      if (chart?.canvas) {
        chart.canvas.style.cursor = '';
      }
      return;
    }

    if (eventType !== 'mousemove') return;

    const nativeEvent = event.native as MouseEvent | undefined;
    const pointerX = event.x;
    const pointerY = event.y;
    if (!nativeEvent || pointerX == null || pointerY == null) return;

    if (this.isPointerOverYAxisTitle(chart, pointerX, pointerY)) {
      this.showChartAxisTitleTooltip(pluginOpts.yFull, nativeEvent);
      if (chart.canvas) {
        chart.canvas.style.cursor = 'help';
      }
    } else {
      this.hideChartAxisTitleTooltip();
      if (chart.canvas) {
        chart.canvas.style.cursor = '';
      }
    }
  }

  private isPointerOverYAxisTitle(chart: any, pointerX: number, pointerY: number): boolean {
    const area = chart?.chartArea;
    const yScale = chart?.scales?.y;
    if (!area || !yScale?.options?.title?.display) return false;
    if (pointerY < area.top || pointerY > area.bottom) return false;

    const titleHitWidth = Math.min(36, Math.max(18, area.left * 0.3));
    return pointerX <= titleHitWidth;
  }

  private ensureChartAxisTitleTooltipEl(): HTMLDivElement {
    if (!this.chartAxisTitleTooltipEl) {
      const el = document.createElement('div');
      el.setAttribute('role', 'tooltip');
      el.style.cssText = [
        'position:fixed',
        'z-index:10000',
        'pointer-events:none',
        'display:none',
        'max-width:320px',
        'padding:6px 10px',
        'border-radius:4px',
        'background:rgba(0,0,0,0.9)',
        'color:#fff',
        'font:12px Poppins,sans-serif',
        'line-height:1.4',
        'box-shadow:0 2px 8px rgba(0,0,0,0.2)',
      ].join(';');
      document.body.appendChild(el);
      this.chartAxisTitleTooltipEl = el;
    }
    return this.chartAxisTitleTooltipEl;
  }

  private showChartAxisTitleTooltip(text: string, nativeEvent: MouseEvent): void {
    const el = this.ensureChartAxisTitleTooltipEl();
    el.textContent = text;
    el.style.display = 'block';
    el.style.left = `${nativeEvent.clientX + 12}px`;
    el.style.top = `${nativeEvent.clientY + 12}px`;
  }

  private hideChartAxisTitleTooltip(): void {
    if (this.chartAxisTitleTooltipEl) {
      this.chartAxisTitleTooltipEl.style.display = 'none';
    }
  }

  private removeChartAxisTitleTooltipEl(): void {
    if (this.chartAxisTitleTooltipEl) {
      this.chartAxisTitleTooltipEl.remove();
      this.chartAxisTitleTooltipEl = null;
    }
  }

  private getAxisTitleText(axis: string | string[] | undefined | null): string {
    return this.getAxisTitleDisplayText(axis);
  }

  // TYPING ANIMATION METHOD
  private typeMessage(fullText: string, messageIndex: number): void {
    if (!this.ENABLE_TYPING_ANIMATION) {
      // If typing animation is disabled, show full text immediately
      this.messages[messageIndex].content = fullText;
      this.isTyping = false;
      this.typingMessageIndex = null;
      this.scrollToBottom();
      return;
    }

    // Clear any existing typing interval
    if (this.typingInterval) {
      clearInterval(this.typingInterval);
    }

    this.isTyping = true;
    this.typingMessageIndex = messageIndex;
    let currentIndex = 0;

    this.typingInterval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        this.messages[messageIndex].content = fullText.substring(0, currentIndex);
        currentIndex++;
      } else {
        // Typing complete
        clearInterval(this.typingInterval);
        this.typingInterval = null;
        this.isTyping = false;
        this.typingMessageIndex = null;
        // Scroll to bottom once typing is fully complete
        setTimeout(() => this.scrollToBottom(), 100);
      }
    }, this.TYPING_SPEED_MS);
  }

  // Check if the user is scrolled near the bottom of the chat container
  private isNearBottom(thresholdPx = 150): boolean {
    const el = this.chatContainer?.nativeElement as HTMLElement;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < thresholdPx;
  }

  // Animate summary text below chart/table with character-by-character typing.
  // Scroll behaviour:
  //   • BEFORE typing starts: put the summary element at the top of the
  //     viewport so the user's eye lands where the new text will appear.
  //   • DURING typing: NEVER auto-scroll — the user must be free to move
  //     around the response (inspect chart, scroll to table, etc).
  //   • AFTER typing completes: if the summary is already visible, do
  //     nothing (non-disruptive). If it scrolled out of view because the
  //     chart/table grew below it AND the user was following near the
  //     bottom, gently re-anchor so the finished summary is on-screen.
  //     If the user scrolled far away on purpose, leave them alone.
  private typeSummary(messageIndex: number, fullSummary: string): void {
    const message = this.messages[messageIndex];
    if (!message || !fullSummary) return;

    const existingInterval = this.summaryTypingIntervals.get(messageIndex);
    if (existingInterval) clearInterval(existingInterval);

    message.isSummaryTyping = true;
    message.displayedSummary = '';
    let currentIndex = 0;

    // Pre-typing anchor: put the summary at the top of the viewport.
    // 50ms is enough for Angular to render the first character (which
    // flips the summary container's *ngIf from hidden to visible).
    // Guard with isLastMessage — if the user sent a newer prompt in the
    // meantime, leave them on the newer message instead of yanking back.
    setTimeout(() => {
      if (messageIndex === this.messages.length - 1) {
        this.scrollToSummary(messageIndex, 'top');
      }
    }, 50);

    const intervalId = setInterval(() => {
      if (currentIndex <= fullSummary.length) {
        message.displayedSummary = fullSummary.substring(0, currentIndex);
        currentIndex++;
        // No per-character scroll — let the user scroll freely.
      } else {
        clearInterval(intervalId);
        this.summaryTypingIntervals.delete(messageIndex);
        message.isSummaryTyping = false;

        // Post-typing re-anchor decision tree:
        //   not last message        → do nothing (user moved on)
        //   summary still visible   → do nothing (non-disruptive)
        //   summary gone + near bottom → re-anchor (user was following)
        //   summary gone + scrolled far → do nothing (respect intent)
        const isLastMessage = messageIndex === this.messages.length - 1;
        if (isLastMessage && !this.isSummaryVisible(messageIndex) && this.isNearBottom()) {
          setTimeout(() => this.scrollToSummary(messageIndex, 'top'), 100);
        }
      }
    }, this.TYPING_SPEED_MS);

    this.summaryTypingIntervals.set(messageIndex, intervalId);
  }

  // Render chart using Chart.js
  renderChart(config: ChartConfig, data: any[]) {
    if (!data || data.length === 0) {
      // Upstream path handed us an empty rowset — surface it via the info
      // card (chartEmpty), NOT the red error card. This catches the legacy
      // testSqlQuery fallback and any future caller that forgets to pre-check.
      const message = this.messages[this.activeChartIndex!];
      if (message) {
        message.chartEmpty = true;
        message.summaryHidden = !message.summary;
        message.isGenerating = false;
      }
      this.isGeneratingChart = false;
      return;
    }

    // Get the message to store the chart instance
    const message = this.messages[this.activeChartIndex!];

    // Destroy existing chart for THIS message if any (for regeneration)
    if (message && message.chartInstance) {
      message.chartInstance.destroy();
      message.chartInstance = null;
    }

    // Wait for canvas to be available in DOM
    setTimeout(() => {
      // Find the canvas element by unique ID for this message
      const canvasId = `chartCanvas-${this.activeChartIndex}`;
      const canvasElement = document.getElementById(canvasId) as HTMLCanvasElement;

      if (!canvasElement) {
        console.error(`❌ Chart canvas not found with ID: ${canvasId}`);
        if (message) {
          message.chartError = "Failed to render chart: Canvas element not found";
          message.summaryHidden = true;
          message.isGenerating = false;
        }
        this.isGeneratingChart = false;
        return;
      }

      const ctx = canvasElement.getContext("2d");
      if (!ctx) {
        console.error("Could not get canvas context");
        if (message) {
          message.chartError = "Failed to render chart: Could not get canvas context";
          message.summaryHidden = true;
          message.isGenerating = false;
        }
        this.isGeneratingChart = false;
        return;
      }

      const variant = config.chart_variant || "simple";
      let yAxisArray = Array.isArray(config.y_axis) ? config.y_axis : [config.y_axis];
      let chartData: any;
      let chartType: any = config.chart_type;

      // Normalize area_line → line type with area rendering
      // Also detect: line + area variant
      const isAreaChart = chartType === 'area_line' || (chartType === 'line' && variant === 'area');
      if (chartType === 'area_line') {
        chartType = 'line';
      }


      // Color palette for datasets
      const colors = this.CHART_COLORS;

      // Flag to track if horizontal chart should be stacked (for long-format data)
      let shouldStackHorizontal = false;

      // Find actual key in data that matches x_axis (fuzzy match)
      let matchedXAxis = config.x_axis;
      if (data.length > 0) {
        const key = this.findMatchingKey(data[0], config.x_axis);
        if (key) {
          matchedXAxis = key;
        } else {
          console.warn(`✗ Could not find matching key for x-axis: '${config.x_axis}'`);
          console.warn('Available keys:', Object.keys(data[0]));

          // FALLBACK: Use first non-numeric column, or first column if all numeric
          const keys = Object.keys(data[0]);
          const nonNumericKey = keys.find(k => {
            const val = data[0][k];
            return typeof val !== 'number' && isNaN(Number(val));
          });

          matchedXAxis = nonNumericKey || keys[0];
          console.warn(`⚠️ Using fallback x-axis: '${matchedXAxis}'`);
        }
      }

      if (variant === "simple" || variant === "grouped" || variant === "area" || variant === "stepped" || (isAreaChart && variant !== "multi" && variant !== "clustered" && variant !== "stacked" && variant !== "cluster_stacked" && variant !== "horizontal")) {
        // Simple, area, stepped, and grouped charts - single dataset
        const labels = data.map((item) => item[matchedXAxis]);

        // Find actual key in data that matches y_axis (fuzzy match)
        let matchedYAxis = yAxisArray[0];
        if (data.length > 0) {
          const key = this.findMatchingKey(data[0], matchedYAxis);
          if (key) {
            matchedYAxis = key;

            // SIMPLE/AREA/STEPPED VARIANTS: Check for long-format data (multiple rows per x-axis)
            if (variant === "simple" || variant === "area" || variant === "stepped") {
              // Check if data has duplicate x-axis values (indicates long-format with grouping field)
              const xValues = data.map(item => item[matchedXAxis]);
              const uniqueXValues = new Set(xValues);
              const hasLongFormat = xValues.length > uniqueXValues.size;

              if (hasLongFormat) {
                // Use the same transformation logic as grouped
                const transformed = this.transformDataForStacked(data, config, matchedXAxis, matchedYAxis);

                if (transformed) {
                  chartData = { labels: transformed.labels, datasets: transformed.datasets };

                  // Skip the rest of the logic
                  matchedYAxis = undefined as any;
                }
              }
            }

            // GROUPED VARIANT: Check for long-format data (multiple rows per x-axis)
            if (variant === "grouped") {
              // Check if data has duplicate x-axis values (indicates long-format with grouping field)
              const xValues = data.map(item => item[matchedXAxis]);
              const uniqueXValues = new Set(xValues);
              const hasLongFormat = xValues.length > uniqueXValues.size;

              if (hasLongFormat) {
                // Use the same transformation logic as stacked, but without stacking
                const transformed = this.transformDataForStacked(data, config, matchedXAxis, matchedYAxis);

                if (transformed) {
                  chartData = { labels: transformed.labels, datasets: transformed.datasets };
                  // Skip the rest of the logic
                  matchedYAxis = undefined as any;
                }
              }
            }
          } else {
            // Y-axis column not found - check if this is wide format data
            console.warn(`✗ Could not find matching key for y-axis: '${matchedYAxis}'`);
            console.warn('Available keys:', Object.keys(data[0]));

            // Detect wide format: find all numeric columns except x_axis
            const numericColumns = Object.keys(data[0]).filter(key => {
              if (key === matchedXAxis) return false; // Exclude x-axis
              const value = data[0][key];
              return typeof value === 'number' || !isNaN(Number(value));
            });

            if (numericColumns.length > 0) {
              // Create multi-dataset chart
              const datasets = numericColumns.map((colName, index) => {
                const values = data.map((item) => item[colName]);
                const color = colors[index % colors.length];
                const isLine = config.chart_type === "line" || isAreaChart || variant === "stepped";

                return {
                  label: this.formatChartLabel(colName),
                  data: values,
                  backgroundColor: isLine
                    ? isAreaChart
                      ? `rgba(${19 + index * 50}, ${99 - index * 20}, ${223 - index * 50}, 0.3)`
                      : `rgba(${19 + index * 50}, ${99 - index * 20}, ${223 - index * 50}, 0.2)`
                    : color,
                  borderColor: color,
                  pointBackgroundColor: color,
                  borderWidth: isLine ? 2 : 0,
                  fill: isAreaChart,
                  tension: variant === "stepped" ? 0 : 0.4,
                  stepped: variant === "stepped" ? "before" : false,
                };
              });

              chartData = { labels: labels, datasets: datasets };

              // Skip the single-dataset logic below
              matchedYAxis = undefined as any;
            }
          }
        }


        // Only create single-dataset chart if we have a valid y-axis (not wide format)
        if (matchedYAxis) {
          const values = data.map((item) => item[matchedYAxis]);

          const isLine = config.chart_type === "line" || isAreaChart || variant === "stepped";

          chartData = {
            labels: labels,
            datasets: [
              {
                label: config.datasets?.[0] || config.title || yAxisArray[0],
                data: values,
                backgroundColor: isLine
                  ? isAreaChart
                    ? "rgba(19, 99, 223, 0.3)"
                    : "rgba(19, 99, 223, 0.2)"
                  : colors[0],
                borderColor: colors[0],
                pointBackgroundColor: colors[0],
                borderWidth: isLine ? 2 : 0,
                fill: isAreaChart, // Fill only for area charts
                tension: variant === "stepped" ? 0 : 0.4, // No tension for stepped
                stepped: variant === "stepped" ? "before" : false, // Stepped line
              },
            ],
          };
        }
      } else if (
        variant === "multi" ||
        variant === "clustered" ||
        variant === "stacked" ||
        variant === "cluster_stacked"
      ) {
        // Multi-dataset and stacked charts
        const labels = data.map((item) => item[matchedXAxis]);

        // STACKED VARIANT ONLY: Check if we need to transform the data
        // This transformation handles BOTH long-format (with grouping field) and wide-format (multiple y-columns)
        // Only applies when chart_variant === "stacked" or "cluster_stacked"
        if (variant === "stacked" || variant === "cluster_stacked") {
          // Try to find y_axis in data to determine if transformation is needed
          let matchedYAxis = yAxisArray[0];
          let needsTransformation = false;

          if (data.length > 0) {
            const matchedKey = this.findMatchingKey(data[0], yAxisArray[0]);
            if (matchedKey) {
              matchedYAxis = matchedKey;

              // Check if data has duplicate x-axis values (indicates need for transformation)
              const xValues = data.map(item => item[matchedXAxis]);
              const uniqueXValues = new Set(xValues);
              needsTransformation = xValues.length > uniqueXValues.size;

              if (needsTransformation) {
                console.log('🔄 Detected multiple rows per x-axis value - transforming for stacked chart');
              }
            } else {
              // Y-axis not found - might be wide format, try transformation anyway
              needsTransformation = true;
            }
          }

          if (needsTransformation && matchedYAxis) {
            // Use transformation logic (handles both long and wide formats)
            const transformed = this.transformDataForStacked(data, config, matchedXAxis, matchedYAxis);

            if (transformed) {
              chartData = { labels: transformed.labels, datasets: transformed.datasets };
              // Skip the rest of the multi/clustered logic
              matchedYAxis = undefined as any;
            }
          }
        }

        // If not stacked or transformation not applied, continue with normal multi/clustered logic
        if (chartData === undefined) {
          // AUTO-DETECT: If y_axis is single value and doesn't exist, try wide format detection
          if (yAxisArray.length === 1 && data.length > 0) {
            const matchedKey = this.findMatchingKey(data[0], yAxisArray[0]);
            if (!matchedKey) {
              console.warn(`✗ Could not find y-axis '${yAxisArray[0]}' for multi-dataset chart`);
              console.warn('Available keys:', Object.keys(data[0]));

              // Auto-detect numeric columns (same logic as simple/grouped)
              const numericColumns = Object.keys(data[0]).filter(key => {
                if (key === matchedXAxis) return false; // Exclude x-axis
                const value = data[0][key];
                return typeof value === 'number' || !isNaN(Number(value));
              });

              if (numericColumns.length > 0) {
                // Override yAxisArray with detected columns
                yAxisArray = numericColumns;
              } else {
                console.error('❌ No numeric columns found for chart');
              }
            }
          }

          // Handle mixed chart type
          if (config.chart_type === "mixed" && config.mixed_types) {
            chartType = "bar"; // Base type for mixed charts
            const datasets = yAxisArray.map((yAxis, index) => {
              // Find actual key in data that matches yAxis (fuzzy match)
              let matchedYAxis = yAxis;
              if (data.length > 0) {
                const matchedKey = this.findMatchingKey(data[0], yAxis);
                if (matchedKey && matchedKey !== yAxis) {
                  matchedYAxis = matchedKey;
                }
              }

              const values = data.map((item) => item[matchedYAxis]);
              const color = colors[index % colors.length];
              const datasetType = config.mixed_types?.[index] || "bar";

              return {
                type: datasetType,
                label: config.datasets?.[index] || yAxis,
                data: values,
                backgroundColor:
                  datasetType === "bar" ? color
                    : `rgba(${index * 50 + 19}, ${99 - index * 20}, ${223 - index * 50}, 0.2)`,
                borderColor: color,
                pointBackgroundColor: color,
                borderWidth: datasetType === 'line' ? 2 : 0,
                fill: datasetType === "line",
                tension: 0.4,
                order: datasetType === "line" ? 1 : 2, // Lines on top of bars
              };
            });

            chartData = {
              labels: labels,
              datasets: datasets,
            };
          } else {
            // Regular multi-dataset charts
            const datasets = yAxisArray.map((yAxis, index) => {
              // Find actual key in data that matches yAxis (fuzzy match)
              let matchedYAxis = yAxis;
              if (data.length > 0) {
                const matchedKey = this.findMatchingKey(data[0], yAxis);
                if (matchedKey && matchedKey !== yAxis) {
                  matchedYAxis = matchedKey;
                }
              }

              const values = data.map((item) => item[matchedYAxis]);
              const color = colors[index % colors.length];

              return {
                label: config.datasets?.[index] || yAxis,
                data: values,
                backgroundColor:
                  config.chart_type === "bar"
                    ? color : `rgba(${index * 50 + 19}, ${99 - index * 20}, ${223 - index * 50}, 0.2)`,
                borderColor: color,
                pointBackgroundColor: color,
                borderWidth: 2,
                fill: config.chart_type === "line" || isAreaChart,
                tension: 0.4, // Smooth line curves
              };
            });

            chartData = { labels: labels, datasets: datasets };
          }
        }
      } else if (variant === "horizontal") {
        // Horizontal bar chart (can be simple or multi-dataset for wide format)
        // AI is inconsistent with axis conventions, so we need to detect which is which

        // Try to intelligently determine which field is labels (categories) vs data (values)
        let labelField: string;
        let dataField: string;

        if (data.length > 0) {
          // Check x_axis field type
          const xAxisKey = this.findMatchingKey(data[0], config.x_axis);
          const yAxisKey = this.findMatchingKey(data[0], yAxisArray[0]);

          let xAxisValue = xAxisKey ? data[0][xAxisKey] : null;
          let yAxisValue = yAxisKey ? data[0][yAxisKey] : null;

          const xIsNumeric = xAxisValue !== null && (typeof xAxisValue === 'number' || !isNaN(Number(xAxisValue)));
          const yIsNumeric = yAxisValue !== null && (typeof yAxisValue === 'number' || !isNaN(Number(yAxisValue)));

          // Determine which is label and which is data based on types
          if (xIsNumeric && !yIsNumeric) {
            // x_axis is numeric (data), y_axis is categorical (labels)
            dataField = config.x_axis;
            labelField = yAxisArray[0];
          } else if (!xIsNumeric && yIsNumeric) {
            // x_axis is categorical (labels), y_axis is numeric (data)
            labelField = config.x_axis;
            dataField = yAxisArray[0];
          } else {
            // Fallback: assume y_axis is labels, x_axis is data (original convention)
            labelField = yAxisArray[0];
            dataField = config.x_axis;
          }
        } else {
          // No data, use default convention
          labelField = yAxisArray[0];
          dataField = config.x_axis;
        }

        // Find matched keys
        let matchedLabelField = labelField;
        let matchedDataField = dataField;
        let dataFieldFound = false;

        if (data.length > 0) {
          const labelKey = this.findMatchingKey(data[0], labelField);
          if (labelKey) {
            matchedLabelField = labelKey;
          }

          const dataKey = this.findMatchingKey(data[0], dataField);
          if (dataKey) {
            matchedDataField = dataKey;
            dataFieldFound = true;

            // HORIZONTAL VARIANT: Check for long-format data (multiple rows per label)
            const labels = data.map((item) => item[matchedLabelField]);
            const labelValues = labels;
            const uniqueLabelValues = new Set(labelValues);
            const hasLongFormat = labelValues.length > uniqueLabelValues.size;

            if (hasLongFormat) {
              // Use transformation logic
              const transformed = this.transformDataForStacked(data, config, matchedLabelField, matchedDataField);

              if (transformed) {
                chartData = {
                  labels: transformed.labels,
                  datasets: transformed.datasets
                };

                // Enable stacking for horizontal long-format
                shouldStackHorizontal = true;

                // Skip the rest of the horizontal logic - we're done! & Don't process wide-format or simple chart logic
              } else {
                // Transformation failed, continue with normal logic
                dataFieldFound = true; // Reset flag to try other logic
              }
            }
          }
        }

        // Only continue if we haven't already created chartData from transformation
        if (!chartData) {
          const labels = data.map((item) => item[matchedLabelField]);

          // Check if we have wide-format data (data field not found)
          if (!dataFieldFound && data.length > 0) {
            // Find all numeric columns except the label field
            const numericColumns = Object.keys(data[0]).filter(key => {
              if (key === matchedLabelField || key.toLowerCase() === matchedLabelField.toLowerCase()) {
                return false;
              }
              const value = data[0][key];
              return typeof value === 'number' || !isNaN(Number(value));
            });

            if (numericColumns.length > 0) {
              // Create multiple datasets for horizontal chart
              const datasets = numericColumns.map((colName, index) => {
                const color = colors[index % colors.length];
                const values = data.map(item => item[colName]);

                return {
                  label: this.formatChartLabel(colName),
                  data: values,
                  backgroundColor: color,
                  borderColor: color,
                  borderWidth: 2,
                };
              });

              chartData = { labels: labels, datasets: datasets };
            } else {
              console.error('No numeric columns found for horizontal chart');
            }
          } else if (dataFieldFound) {
            // Simple horizontal chart
            const values = data.map((item) => item[matchedDataField]);

            chartData = {
              labels: labels, // Categories from y_axis
              datasets: [
                {
                  label: config.datasets?.[0] || config.title || dataField,
                  data: values, // Values from x_axis
                  backgroundColor: colors[0],
                  borderColor: colors[0],
                  borderWidth: 2,
                },
              ],
            };
          }
        }
      }

      // B3 (horizontal) builds datasets without setting `fill`. For area_line,
      // enable fill here so the area renders. Multi-dataset color transparency
      // is handled by the existing isAreaChart block further down.
      if (isAreaChart && variant === 'horizontal' && chartData?.datasets) {
        chartData.datasets.forEach((ds: any) => { ds.fill = true; });
      }

      // ── UNIVERSAL GATES (run after ALL variant branches, before Chart.js creation) ──

      // Gate 1: Normalize "mixed" → "bar".
      // Chart.js has no "mixed" type. The correct approach is to set the base type
      // to "bar" and let per-dataset .type properties (if any) handle mixing.
      if (chartType === 'mixed') {
        chartType = 'bar';
      }

      // Gate 2: Validate chartType is a real Chart.js type.
      // Catches any future chart_type value that slips past SUPPORTED_TYPES
      // but doesn't map to a valid Chart.js constructor type.
      const VALID_CHARTJS_TYPES = ['bar', 'line', 'pie', 'doughnut', 'radar', 'polarArea', 'bubble', 'scatter'];
      if (!VALID_CHARTJS_TYPES.includes(chartType)) {
        if (message) {
          message.chartError = `Unsupported chart type: "${chartType}"`;
          message.summaryHidden = true;
          message.isGenerating = false;
        }
        this.isGeneratingChart = false;
        return;
      }


      // Create chart configuration
      const floatingChart = this.isFloatingView;
      const legendFontSize = floatingChart ? 9 : 10;
      const tooltipTitleSize = floatingChart ? 10 : 12;
      const tooltipBodySize = floatingChart ? 9 : 11;
      const axisTitleSize = floatingChart ? 9 : 11;
      const chartTitleSize = floatingChart ? 12 : 16;

      const yAxisTitleFull = this.getAxisTitleFullText(config.y_axis);
      const yAxisTitleDisplay = this.getAxisTitleDisplayText(config.y_axis);

      const chartConfig: any = {
        type: chartType,
        data: chartData,
        plugins: [this.axisTitleTooltipPlugin],
        options: {
          // Handle horizontal orientation
          // Check both chart_type and chart_variant for horizontal
          indexAxis: (variant === "horizontal") ? "y" : "x",
          responsive: true,
          maintainAspectRatio: true,
          ...(floatingChart ? { aspectRatio: this.isFloatingExpanded ? 2.2 : 1.5 } : {}),
          plugins: {
            scraiiAxisTitleTooltip: {
              yFull: yAxisTitleFull,
              yTruncated: this.isAxisTitleTruncated(config.y_axis),
            },
            legend: {
              display: true,
              position: "top",
              onClick: (_event: any, legendItem: any, legend: any) => {
                const index = legendItem.datasetIndex;
                const chart = legend.chart;
                if (index == null || !chart) return;
                const meta = chart.getDatasetMeta(index);
                meta.hidden = meta.hidden === null ? !chart.data.datasets[index].hidden : null;
                chart.update();
              },
              labels: {
                font: {
                  size: legendFontSize,
                  family: 'Poppins'
                },
                padding: floatingChart ? 6 : 10,
                boxWidth: floatingChart ? 6 : 8,
                boxHeight: floatingChart ? 6 : 8,
                usePointStyle: true,
                pointStyle: 'circle',
              }
            },
            tooltip: {
              enabled: true,
              mode: 'index', // Show all datasets at the same x-axis point
              intersect: true, // Only show when hovering directly on a bar/point
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              titleColor: '#fff',
              titleFont: {
                size: tooltipTitleSize,
                weight: 'bold',
                family: 'Poppins'

              },
              bodyColor: '#fff',
              bodyFont: {
                size: tooltipBodySize,
                family: 'Poppins'

              },
              footerColor: '#fff',
              footerFont: {
                size: tooltipBodySize,
                weight: 'bold',
                family: 'Poppins'

              },
              borderColor: 'rgba(255, 255, 255, 0.3)',
              borderWidth: 1,
              cornerRadius: 4, // Rounded corners matching legend boxes
              padding: floatingChart ? 6 : 10,
              displayColors: true,
              boxWidth: floatingChart ? 6 : 8,
              boxHeight: floatingChart ? 6 : 8,
              boxPadding: floatingChart ? 3 : 4,
              usePointStyle: true,
              callbacks: {
                title: (tooltipItems: any[]) => {
                  // Show the x-axis or y-axis label depending on chart orientation
                  if (tooltipItems && tooltipItems.length > 0) {
                    const item = tooltipItems[0];
                    return item.label || '';
                  }
                  return '';
                },
                beforeBody: (tooltipItems: any[]) => {
                  // Add a separator line if there are many items
                  if (tooltipItems.length > 5) {
                    return '━━━━━━━━━━━━━━━━';
                  }
                  return '';
                },
                label: (context: any) => {
                  // Dynamically show dataset label and value
                  const label = context.dataset.label || '';

                  // Get the value based on chart orientation
                  let value;
                  if (variant === "horizontal") {
                    value = context.parsed.x;
                  } else {
                    value = context.parsed.y;
                  }

                  // Format the value with locale-specific separators
                  const formattedValue = value !== null && value !== undefined
                    ? value.toLocaleString(undefined, { maximumFractionDigits: 2 })
                    : 'N/A';

                  // Return formatted label
                  return label ? `${label}: ${formattedValue}` : formattedValue;
                },
                footer: (tooltipItems: any[]) => {
                  // Show total for stacked charts at the bottom (footer)
                  // Handle vertical stacked (variant="stacked" or "cluster_stacked") and horizontal stacked
                  const isStacked = variant === "stacked" || variant === "cluster_stacked" || (variant === "horizontal" && shouldStackHorizontal);

                  if (isStacked && tooltipItems.length > 1) {
                    const total = tooltipItems.reduce((sum: number, item: any) => {
                      // Check orientation based on variant
                      const value = variant === "horizontal"
                        ? item.parsed.x
                        : item.parsed.y;
                      return sum + (value || 0);
                    }, 0);
                    return `━━━━━━━━━━━━━━━━\nTotal: ${total.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
                  }
                  return '';
                }
              }
            },
            title: {
              // display: !!config.title, // Removed - Title inside chart for better Chart visualization
              text: config.title || "",
              font: { size: chartTitleSize, weight: "bold", family: 'Poppins' },
            },
          },
          scales: {
            x: {
              // For horizontal charts, swap the stacking logic
              // Stack when: variant is "stacked" OR shouldStackHorizontal flag is true
              stacked: (variant === "horizontal")
                ? shouldStackHorizontal  // Stack x-axis for horizontal long-format
                : (variant === "stacked" || variant === "cluster_stacked"), // Stack x-axis for stacked variants
              grid: {
                display: variant === "horizontal",
              },
              title: {
                // Backend convention: x_axis -> scales.x, y_axis -> scales.y for both orientations.
                // For horizontal (indexAxis:"y"), x_axis is the numeric data field shown on the bottom;
                // for vertical, x_axis is the category field shown on the bottom.
                display: this.getAxisTitleText(config.x_axis).length > 0,
                text: this.getAxisTitleText(config.x_axis),
                font: { size: axisTitleSize, weight: '600', family: 'Poppins' },
                color: '#6b7280',
                padding: { top: floatingChart ? 4 : 6, bottom: 2 },
              },
              ticks: floatingChart ? { font: { size: 9, family: 'Poppins' } } : {},
            },
            y: {
              // For horizontal charts, swap the stacking logic
              // Stack when: variant is "stacked" OR shouldStackHorizontal flag is true
              stacked: (variant === "horizontal")
                ? shouldStackHorizontal  // Stack y-axis for horizontal stacked OR long-format
                : (variant === "stacked" || variant === "cluster_stacked"), // Stack y-axis for stacked variants
              beginAtZero: true,
              grid: {
                color: "rgba(0, 0, 0, 0.05)",
              },
              title: {
                display: yAxisTitleDisplay.length > 0,
                text: yAxisTitleDisplay,
                font: { size: axisTitleSize, weight: '600', family: 'Poppins' },
                color: '#6b7280',
                padding: { top: 0, bottom: floatingChart ? 4 : 6 },
              },
              ticks: floatingChart ? { font: { size: 9, family: 'Poppins' } } : {},
            },
          },
        },
      }

      // Helper function to display error message in chart canvas
      const displayChartError = (errorMessage: string, details?: string) => {
        console.error('❌ Chart Error:', errorMessage);
        if (details) console.error('Details:', details);

        // Draw error message on canvas
        ctx.fillStyle = '#f8f9fa';
        ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

        // Error icon and message
        ctx.fillStyle = '#dc3545';
        ctx.font = 'bold 16px Poppins';
        ctx.textAlign = 'center';
        ctx.fillText('⚠️ Chart Error', ctx.canvas.width / 2, ctx.canvas.height / 2 - 30);

        ctx.fillStyle = '#6c757d';
        ctx.font = '14px Poppins';
        ctx.fillText(errorMessage, ctx.canvas.width / 2, ctx.canvas.height / 2);

        if (details) {
          ctx.font = '12px Poppins';
          ctx.fillStyle = '#adb5bd';
          ctx.fillText(details, ctx.canvas.width / 2, ctx.canvas.height / 2 + 25);
        }

        // Reset flags
        if (message) {
          message.isGenerating = false;
        }
        this.isGeneratingChart = false;
      };

      // VALIDATION: Check if chartData is valid before creating chart
      if (!chartData) {
        displayChartError('No chart data available', 'The data structure does not match the chart configuration');
        return;
      }

      if (!chartData.datasets || chartData.datasets.length === 0) {
        displayChartError('No datasets found', `Expected column "${config.y_axis}" not found in data`);
        return;
      }

      // Check if datasets have data
      const hasData = chartData.datasets.some((dataset: any) =>
        dataset.data && dataset.data.length > 0
      );

      if (!hasData) {
        displayChartError('All datasets are empty', 'No data points available to display');
        return;
      }

      // // commented cause of color theme
      // AREA CHART: Sort datasets largest-first and ensure transparent fills
      // This ensures smaller datasets render on top and aren't hidden behind larger ones
      if (isAreaChart && chartData.datasets && chartData.datasets.length > 1) {
        // Sort datasets by total value DESCENDING — largest drawn first (background)
        chartData.datasets.sort((a: any, b: any) => {
          const totalA = (a.data || []).reduce((sum: number, v: number) => sum + (v || 0), 0);
          const totalB = (b.data || []).reduce((sum: number, v: number) => sum + (v || 0), 0);
          return totalB - totalA; // Largest first = drawn in background
        });

        // Ensure all area fills use semi-transparent backgroundColor
        // Convert opaque HEX colors (#RRGGBB) to rgba with 0.3 opacity
        chartData.datasets.forEach((ds: any) => {
          if (ds.backgroundColor && typeof ds.backgroundColor === 'string') {
            const hex = ds.backgroundColor.replace('#', '');
            if (/^[0-9A-Fa-f]{6}$/.test(hex)) {
              const r = parseInt(hex.substring(0, 2), 16);
              const g = parseInt(hex.substring(2, 4), 16);
              const b = parseInt(hex.substring(4, 6), 16);
              ds.backgroundColor = `rgba(${r}, ${g}, ${b}, 0.2)`;
            }
          }
        });
      }

      this.chartConfig = chartConfig;

      // Create chart instance and store it in the message
      if (message) {
        try {
          message.chartInstance = new Chart(ctx, chartConfig);
          if (this.isFloatingView) {
            (message.chartInstance as any)._floatingLayoutExpanded = this.isFloatingExpanded;
          }
        } catch (chartError) {
          console.error('Chart.js rendering error:', chartError);
          message.isGenerating = false;
          this.isGeneratingChart = false;
          this.isLoading = false;
          displayChartError('Failed to render chart', 'The chart could not be generated with the provided data.');
          return;
        }

        // IMPORTANT: Set isGenerating to false AFTER chart instance is created
        // This ensures the canvas stays in DOM during rendering
        message.isGenerating = false;
        this.isGeneratingChart = false;

        // ── BRUSH NAVIGATOR (purely additive, does not affect chart config) ──
        const labelCount: number = chartData?.labels?.length ?? 0;
        const datasetCount: number = chartData?.datasets?.length ?? 1;
        const isHorizontalChart: boolean = (variant === 'horizontal');

        // Trigger conditions:
        //   1. Categories meet or exceed the threshold (applies to all chart types)
        //   2. Chart is overcrowded: categories × datasets > crowding threshold (applies to all chart types)
        const CROWDING_THRESHOLD = 20; // categories × datasets above which charts become unreadable
        const isCrowded = (labelCount * datasetCount) > CROWDING_THRESHOLD;
        const shouldShowBrush = labelCount >= this.BRUSH_THRESHOLD || isCrowded;

        if (shouldShowBrush && labelCount > 0) {
          // Determine a sensible initial window size:
          //   - For crowded charts: show enough categories so bars aren't too thin
          //   - Cap at BRUSH_THRESHOLD so the window isn't the full dataset
          const windowSize = Math.min(this.BRUSH_THRESHOLD, labelCount);
          message.brushState = {
            show: true,
            isHorizontal: isHorizontalChart,
            startIndex: 0,
            endIndex: windowSize,
            totalCount: labelCount,
          };
          // Apply initial zoom window to the chart (after Angular renders brush canvas)
          this.applyBrushWindow(message.chartInstance, message.brushState);
          const capturedIndex = this.activeChartIndex!;
          setTimeout(() => this.initBrush(capturedIndex, message), 50);
        } else {
          // Clear any stale brush state from a previous render
          message.brushState = undefined;
        }
        // ── END BRUSH NAVIGATOR ──

      }
    }, 300); // Increased timeout to allow Angular to render the canvas element
  }

  // ============ Brush Navigator Methods ============

  /**
   * Applies the current brush window to the chart by updating axis min/max.
   * This is the ONLY place where the chart's visible range is changed.
   * It does NOT modify any data, datasets, or chart configuration.
   */
  private applyBrushWindow(chart: any, brush: NonNullable<ChatMessage['brushState']>): void {
    if (!chart || !chart.options?.scales) return;
    if (brush.isHorizontal) {
      // Horizontal bar chart: categories are on the Y axis
      if (chart.options.scales['y']) {
        chart.options.scales['y'].min = brush.startIndex;
        chart.options.scales['y'].max = brush.endIndex - 1;
      }
    } else {
      // Vertical chart: categories are on the X axis
      if (chart.options.scales['x']) {
        chart.options.scales['x'].min = brush.startIndex;
        chart.options.scales['x'].max = brush.endIndex - 1;
      }
    }
    chart.update('none'); // Instant update, no animation
  }

  /**
   * Initialises the brush canvas: draws the overview minimap and wires
   * mouse/touch events for drag-to-scroll and handle-to-resize.
   * Purely additive — only reads chart data, never modifies it.
   */
  private initBrush(messageIndex: number, message: ChatMessage): void {
    if (!message.brushState || !message.chartInstance) return;

    const brushCanvas = document.getElementById(`brushCanvas-${messageIndex}`) as HTMLCanvasElement | null;
    if (!brushCanvas) return;

    const ctx = brushCanvas.getContext('2d');
    if (!ctx) return;

    const brush = message.brushState;
    const chart = message.chartInstance;
    const isVertical = brush.isHorizontal; // vertical strip = horizontal bar chart
    const HANDLE_SIZE = 8; // px — height of top/bottom handles (vertical) or width of left/right handles (horizontal)

    // ── Lock canvas pixel dimensions ONCE to prevent layout-reflow growing bug ──
    // Reading offsetWidth/offsetHeight inside drawBrush on every call causes the
    // canvas to grow because setting brushCanvas.width/height triggers reflow.
    const CANVAS_W = brushCanvas.offsetWidth || 48;
    const CANVAS_H = brushCanvas.offsetHeight || 200;
    brushCanvas.width = CANVAS_W;
    brushCanvas.height = CANVAS_H;

    // ── Rounded rect helper (roundRect not in all TS lib targets) ──────────
    const roundedRect = (x: number, y: number, w: number, h: number, r: number) => {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + w - r, y);
      ctx.arcTo(x + w, y, x + w, y + r, r);
      ctx.lineTo(x + w, y + h - r);
      ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
      ctx.lineTo(x + r, y + h);
      ctx.arcTo(x, y + h, x, y + h - r, r);
      ctx.lineTo(x, y + r);
      ctx.arcTo(x, y, x + r, y, r);
      ctx.closePath();
    };

    // Detect the top-level chart type from Chart.js config
    const globalChartType: string = (chart.config?.type ?? 'bar').toString().toLowerCase();
    // A dataset is "line-like" if the chart is a line/area chart OR the dataset itself overrides to 'line'
    const isLineLike = (ds: any): boolean => {
      const dsType = (ds.type ?? '').toString().toLowerCase();
      if (dsType === 'line') return true;
      if (dsType === 'bar') return false;
      return globalChartType === 'line';
    };

    // ── Shared: find max value across all datasets for normalisation ────────
    const datasets: any[] = chart.data?.datasets ?? [];
    const isStacked = chart.options?.scales?.['x']?.stacked === true || chart.options?.scales?.['y']?.stacked === true;
    let maxVal = 0;

    if (isStacked) {
      const numPoints = datasets[0]?.data?.length || 0;
      for (let i = 0; i < numPoints; i++) {
        let sum = 0;
        datasets.forEach((ds: any) => {
          if (!isLineLike(ds)) {
            const v = ds.data[i];
            const raw = typeof v === 'object' && v !== null ? (v.x ?? v.y ?? v.v ?? 0) : v;
            sum += (Number(raw) || 0);
          }
        });
        if (sum > maxVal) maxVal = sum;
      }
      datasets.forEach((ds: any) => {
        if (isLineLike(ds)) {
          (ds.data as any[]).forEach((v: any) => {
            const raw = typeof v === 'object' && v !== null ? (v.x ?? v.y ?? v.v ?? 0) : v;
            const n = Number(raw) || 0;
            if (n > maxVal) maxVal = n;
          });
        }
      });
    } else {
      datasets.forEach((ds: any) => {
        (ds.data as any[]).forEach((v: any) => {
          const raw = typeof v === 'object' && v !== null ? (v.x ?? v.y ?? v.v ?? 0) : v;
          const n = Number(raw) || 0;
          if (n > maxVal) maxVal = n;
        });
      });
    }
    if (maxVal === 0) maxVal = 1;



    // Helper: draw a polyline for the full dataset in the horizontal brush minimap
    const drawLineH = (ds: any, W: number, H: number, padding: number) => {
      const data = ds.data as number[];
      if (!data || data.length === 0) return;
      const availH = H - padding * 2;
      const color = ds.borderColor || ds.backgroundColor || '#1363df';
      ctx.strokeStyle = typeof color === 'string' ? color : '#1363df';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      data.forEach((val: any, i: number) => {
        const raw = typeof val === 'object' && val !== null ? (val.x ?? val.y ?? val.v ?? 0) : val;
        const numVal = Number(raw) || 0;
        const x = (i / (data.length - 1 || 1)) * W;
        const y = H - padding - (numVal / maxVal) * availH;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
      ctx.stroke();
      if (ds.fill) {
        ctx.lineTo(W, H - padding);
        ctx.lineTo(0, H - padding);
        ctx.closePath();
        ctx.fillStyle = typeof ds.backgroundColor === 'string' ? ds.backgroundColor : 'rgba(19,99,223,0.3)';
        ctx.fill();
      }
    };

    // Helper: draw a polyline for the full dataset in the vertical brush minimap
    const drawLineV = (ds: any, W: number, H: number, padding: number) => {
      const data = ds.data as number[];
      if (!data || data.length === 0) return;
      const availW = W - padding * 2;
      const color = ds.borderColor || ds.backgroundColor || '#1363df';
      ctx.strokeStyle = typeof color === 'string' ? color : '#1363df';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      data.forEach((val: any, i: number) => {
        const raw = typeof val === 'object' && val !== null ? (val.x ?? val.y ?? val.v ?? 0) : val;
        const numVal = Number(raw) || 0;
        const y = (i / (data.length - 1 || 1)) * H;
        const x = padding + (numVal / maxVal) * availW;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
      ctx.stroke();
      if (ds.fill) {
        ctx.lineTo(padding, H);
        ctx.lineTo(padding, 0);
        ctx.closePath();
        ctx.fillStyle = typeof ds.backgroundColor === 'string' ? ds.backgroundColor : 'rgba(19,99,223,0.3)';
        ctx.fill();
      }
    };

    // ── Draw function — uses locked CANVAS_W / CANVAS_H, never re-reads offsetHeight ──
    const drawBrush = () => {
      if (!message.brushState) return;
      try {
        const W = CANVAS_W;
        const H = CANVAS_H;
        // Re-assert locked size in case browser reset it
        brushCanvas.width = W;
        brushCanvas.height = H;

        const total = brush.totalCount;
        const padding = 4;

        // Background
        ctx.fillStyle = '#f0f2f7';
        ctx.fillRect(0, 0, W, H);

        if (!isVertical) {
          // ── HORIZONTAL BRUSH (vertical/normal charts — X axis) ──────────────
          const barW = W / total;
          const availH = H - padding * 2;

          const barDatasets = datasets.filter(d => !isLineLike(d));
          const numBarDatasets = barDatasets.length;
          const stackTotals = new Array(total).fill(0);

          datasets.forEach((ds: any) => {
            if (isLineLike(ds)) {
              // Draw as a line so the brush preview matches the line chart
              drawLineH(ds, W, H, padding);
            } else {
              const barIdx = barDatasets.indexOf(ds);
              const color = ds.backgroundColor || ds.borderColor || 'rgba(19,99,223,0.6)';
              ctx.fillStyle = typeof color === 'string' ? color : 'rgba(19,99,223,0.6)';
              const _dsData: any[] = Array.isArray(ds.data) ? ds.data : [];
              _dsData.forEach((val: any, i: number) => {
                // Convert to number, falling back to 0 if NaN/null/undefined
                const raw = typeof val === 'object' && val !== null ? (val.x ?? val.y ?? val.v ?? 0) : val;
                let numVal = Number(raw);
                if (isNaN(numVal)) numVal = 0;

                if (isStacked) {
                  const prevVal = stackTotals[i] || 0;
                  const valH = (numVal / maxVal) * availH;
                  const bottomY = H - padding - (prevVal / maxVal) * availH;
                  ctx.fillRect(i * barW + 1, bottomY - valH, Math.max(barW - 2, 1), valH);
                  stackTotals[i] = prevVal + numVal;
                } else {
                  const groupBarW = Math.max((barW - 2) / numBarDatasets, 1);
                  const xOffset = i * barW + 1 + (barIdx * groupBarW);
                  const valH = (numVal / maxVal) * availH;
                  ctx.fillRect(xOffset, H - padding - valH, groupBarW, valH);
                }
              });
            }
          });

          const selX = (brush.startIndex / total) * W;
          const selW = ((brush.endIndex - brush.startIndex) / total) * W;

          // Dim unselected
          ctx.fillStyle = 'rgba(255,255,255,0.55)';
          ctx.fillRect(0, 0, selX, H);
          ctx.fillRect(selX + selW, 0, W - selX - selW, H);

          // Border
          ctx.strokeStyle = '#1363df';
          ctx.lineWidth = 1.5;
          ctx.strokeRect(selX + 0.75, 0.75, selW - 1.5, H - 1.5);

          // Clamped handle positions so they stay within canvas bounds
          const leftHandleX = Math.max(0, selX - HANDLE_SIZE / 2);
          const rightHandleX = Math.min(W - HANDLE_SIZE, selX + selW - HANDLE_SIZE / 2);

          // Left handle — centered on the selection border
          ctx.fillStyle = '#FFFFFF';
          ctx.beginPath();
          roundedRect(leftHandleX, (H - 20) / 2, HANDLE_SIZE, 20, 3);
          ctx.fill();

          // Right handle — centered on the selection border
          ctx.beginPath();
          roundedRect(rightHandleX, (H - 20) / 2, HANDLE_SIZE, 20, 3);
          ctx.fill();

          // Grip lines (vertical lines inside handles)
          const leftGripX = leftHandleX + HANDLE_SIZE / 2;
          const rightGripX = rightHandleX + HANDLE_SIZE / 2;
          ctx.strokeStyle = '#8A98AB';
          ctx.lineWidth = 1;
          [leftGripX - 1, leftGripX + 1].forEach(lx => {
            ctx.beginPath(); ctx.moveTo(lx, (H - 10) / 2); ctx.lineTo(lx, (H + 10) / 2); ctx.stroke();
          });
          [rightGripX - 1, rightGripX + 1].forEach(lx => {
            ctx.beginPath(); ctx.moveTo(lx, (H - 10) / 2); ctx.lineTo(lx, (H + 10) / 2); ctx.stroke();
          });

        } else {
          // ── VERTICAL BRUSH (horizontal bar charts — Y axis) ─────────────────
          // Categories run top-to-bottom; each mini-bar runs left-to-right
          const barH = H / total;
          const availW = W - padding * 2;

          const barDatasets = datasets.filter(d => !isLineLike(d));
          const numBarDatasets = barDatasets.length;
          const stackTotals = new Array(total).fill(0);

          datasets.forEach((ds: any) => {
            if (isLineLike(ds)) {
              // Draw as a line so the brush preview matches the line chart
              drawLineV(ds, W, H, padding);
            } else {
              const barIdx = barDatasets.indexOf(ds);
              const color = ds.backgroundColor || ds.borderColor || 'rgba(19,99,223,0.6)';
              ctx.fillStyle = typeof color === 'string' ? color : 'rgba(19,99,223,0.6)';
              const _dsData: any[] = Array.isArray(ds.data) ? ds.data : [];
              _dsData.forEach((val: any, i: number) => {
                // Convert to number, falling back to 0 if NaN/null/undefined
                const raw = typeof val === 'object' && val !== null ? (val.x ?? val.y ?? val.v ?? 0) : val;
                let numVal = Number(raw);
                if (isNaN(numVal)) numVal = 0;

                if (isStacked) {
                  const prevVal = stackTotals[i] || 0;
                  const valW = (numVal / maxVal) * availW;
                  const startX = padding + (prevVal / maxVal) * availW;
                  ctx.fillRect(startX, i * barH + 1, valW, Math.max(barH - 2, 1));
                  stackTotals[i] = prevVal + numVal;
                } else {
                  const groupBarH = Math.max((barH - 2) / numBarDatasets, 1);
                  const yOffset = i * barH + 1 + (barIdx * groupBarH);
                  const valW = (numVal / maxVal) * availW;
                  ctx.fillRect(padding, yOffset, valW, groupBarH);
                }
              });
            }
          });

          const selY = (brush.startIndex / total) * H;
          const selH = ((brush.endIndex - brush.startIndex) / total) * H;

          // Dim unselected
          ctx.fillStyle = 'rgba(255,255,255,0.55)';
          ctx.fillRect(0, 0, W, selY);
          ctx.fillRect(0, selY + selH, W, H - selY - selH);

          // Border
          ctx.strokeStyle = '#1363df';
          ctx.lineWidth = 1.5;
          ctx.strokeRect(0.75, selY + 0.75, W - 1.5, selH - 1.5);

          // Clamped handle positions so they stay within canvas bounds
          const topHandleY = Math.max(0, selY - HANDLE_SIZE / 2);
          const bottomHandleY = Math.min(H - HANDLE_SIZE, selY + selH - HANDLE_SIZE / 2);

          // Top handle (horizontal pill) — centered on the selection border
          ctx.fillStyle = '#FFFFFF';
          ctx.beginPath();
          roundedRect((W - 20) / 2, topHandleY, 20, HANDLE_SIZE, 3);
          ctx.fill();

          // Bottom handle — centered on the selection border
          ctx.beginPath();
          roundedRect((W - 20) / 2, bottomHandleY, 20, HANDLE_SIZE, 3);
          ctx.fill();

          // Grip lines (horizontal lines inside top/bottom handles)
          const topGripY = topHandleY + HANDLE_SIZE / 2;
          const bottomGripY = bottomHandleY + HANDLE_SIZE / 2;
          ctx.strokeStyle = '#8A98AB';
          ctx.lineWidth = 1;
          [topGripY - 1, topGripY + 1].forEach(ly => {
            ctx.beginPath(); ctx.moveTo((W - 10) / 2, ly); ctx.lineTo((W + 10) / 2, ly); ctx.stroke();
          });
          [bottomGripY - 1, bottomGripY + 1].forEach(ly => {
            ctx.beginPath(); ctx.moveTo((W - 10) / 2, ly); ctx.lineTo((W + 10) / 2, ly); ctx.stroke();
          });
        }
      } catch (e) {
        console.error('Brush render error:', e);
      }
    };

    // Initial draw
    drawBrush();

    // ── Interaction ─────────────────────────────────────────────────────────
    type DragMode = 'none' | 'start-handle' | 'end-handle' | 'selection';
    let dragMode: DragMode = 'none';
    let dragStartPos = 0;
    let dragStartBrush = { startIndex: 0, endIndex: 0 };

    // Returns the relevant coordinate: X for horizontal brush, Y for vertical brush
    const getPos = (e: MouseEvent | TouchEvent): number => {
      const rect = brushCanvas.getBoundingClientRect();
      if (!isVertical) {
        const clientX = e instanceof MouseEvent ? e.clientX : e.touches[0].clientX;
        return clientX - rect.left;
      } else {
        const clientY = e instanceof MouseEvent ? e.clientY : e.touches[0].clientY;
        return clientY - rect.top;
      }
    };

    const hitTest = (pos: number): DragMode => {
      const total = brush.totalCount;
      if (!isVertical) {
        const sel0 = (brush.startIndex / total) * CANVAS_W;
        const sel1 = (brush.endIndex / total) * CANVAS_W;
        if (pos >= sel0 && pos <= sel0 + HANDLE_SIZE) return 'start-handle';
        if (pos >= sel1 - HANDLE_SIZE && pos <= sel1) return 'end-handle';
        if (pos > sel0 && pos < sel1) return 'selection';
      } else {
        const sel0 = (brush.startIndex / total) * CANVAS_H;
        const sel1 = (brush.endIndex / total) * CANVAS_H;
        if (pos >= sel0 && pos <= sel0 + HANDLE_SIZE) return 'start-handle'; // top handle
        if (pos >= sel1 - HANDLE_SIZE && pos <= sel1) return 'end-handle';   // bottom handle
        if (pos > sel0 && pos < sel1) return 'selection';
      }
      return 'none';
    };

    const getCursor = (mode: DragMode): string => {
      if (mode === 'start-handle' || mode === 'end-handle') return isVertical ? 'ns-resize' : 'ew-resize';
      if (mode === 'selection') return 'grab';
      return 'default';
    };

    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const pos = getPos(e);
      dragMode = hitTest(pos);
      if (dragMode !== 'none') {
        dragStartPos = pos;
        dragStartBrush = { startIndex: brush.startIndex, endIndex: brush.endIndex };
        brushCanvas.style.cursor = getCursor(dragMode);
        e.preventDefault();
      }
    };

    const onPointerMove = (e: MouseEvent | TouchEvent) => {
      if (dragMode === 'none' || !message.brushState) return;
      e.preventDefault();

      const pos = getPos(e);
      const total = brush.totalCount;
      const windowSize = dragStartBrush.endIndex - dragStartBrush.startIndex;
      // Use locked dimension — never read offsetWidth/offsetHeight during drag
      const dimension = isVertical ? CANVAS_H : CANVAS_W;
      const delta = Math.round(((pos - dragStartPos) / dimension) * total);

      if (dragMode === 'selection') {
        let newStart = dragStartBrush.startIndex + delta;
        newStart = Math.max(0, Math.min(newStart, total - windowSize));
        brush.startIndex = newStart;
        brush.endIndex = newStart + windowSize;
      } else if (dragMode === 'start-handle') {
        const MIN_WINDOW = 2; // minimum categories always visible
        let newStart = dragStartBrush.startIndex + delta;
        newStart = Math.max(0, Math.min(newStart, brush.endIndex - MIN_WINDOW));
        brush.startIndex = newStart;
      } else if (dragMode === 'end-handle') {
        const MIN_WINDOW = 2; // minimum categories always visible
        let newEnd = dragStartBrush.endIndex + delta;
        newEnd = Math.max(brush.startIndex + MIN_WINDOW, Math.min(newEnd, total));
        brush.endIndex = newEnd;
      }

      this.applyBrushWindow(message.chartInstance, brush);
      drawBrush();
      brushCanvas.style.cursor = getCursor(hitTest(pos));
    };

    const onPointerUp = () => { dragMode = 'none'; };

    const onMouseMove = (e: MouseEvent) => {
      if (dragMode !== 'none') { onPointerMove(e); return; }
      brushCanvas.style.cursor = getCursor(hitTest(getPos(e)));
    };

    // Attach events
    brushCanvas.addEventListener('mousedown', onPointerDown as EventListener);
    window.addEventListener('mousemove', onMouseMove as EventListener);
    window.addEventListener('mouseup', onPointerUp);
    brushCanvas.addEventListener('touchstart', onPointerDown as EventListener, { passive: false });
    window.addEventListener('touchmove', onPointerMove as EventListener, { passive: false });
    window.addEventListener('touchend', onPointerUp);

    // Store cleanup
    const cleanup = () => {
      brushCanvas.removeEventListener('mousedown', onPointerDown as EventListener);
      window.removeEventListener('mousemove', onMouseMove as EventListener);
      window.removeEventListener('mouseup', onPointerUp);
      brushCanvas.removeEventListener('touchstart', onPointerDown as EventListener);
      window.removeEventListener('touchmove', onPointerMove as EventListener);
      window.removeEventListener('touchend', onPointerUp);
    };
    this.brushCleanupMap.set(messageIndex, cleanup);
  }


  /** Removes brush event listeners for a specific message index. */
  private destroyBrush(messageIndex: number): void {
    const cleanup = this.brushCleanupMap.get(messageIndex);
    if (cleanup) {
      cleanup();
      this.brushCleanupMap.delete(messageIndex);
    }
  }

  // ============ End Brush Navigator Methods ============

  // Close a specific chart by message index
  closeChart(messageIndex?: number) {
    // If no index provided, close the currently active chart
    const indexToClose = messageIndex !== undefined ? messageIndex : this.activeChartIndex;

    if (indexToClose === null || indexToClose === undefined) {
      return;
    }

    const message = this.messages[indexToClose];
    if (!message) {
      return;
    }

    // Cancel subscription if this is the active chart being generated
    if (this.activeChartIndex === indexToClose && this.chartSubscription) {
      this.chartSubscription.unsubscribe();
      this.chartSubscription = undefined;
    }

    // Destroy the chart instance for this specific message
    if (message.chartInstance) {
      message.chartInstance.destroy();
      message.chartInstance = null;
    }

    // Destroy brush navigator for this message (removes event listeners)
    this.destroyBrush(indexToClose);
    message.brushState = undefined;

    // Clear chart-related state for this message
    // IMPORTANT: Keep chartConfig so the Generate Chart button remains visible
    message.isGenerating = false;
    message.chartError = undefined;
    message.isUnsupportedChart = false; // Reset unsupported flag

    // DO NOT clear chartConfig - this would hide the Generate Chart button

    // Reset active chart index if we closed the active chart
    if (this.activeChartIndex === indexToClose) {
      this.activeChartIndex = null;
      this.chartConfig = null;
      this.isGeneratingChart = false;
      this.currentChartTitle = "Chart Visualization";
    }

    // Hide summary when chart is closed
    message.summaryHidden = true;
    // Stop summary typing if still running
    const summaryInterval = this.summaryTypingIntervals.get(indexToClose);
    if (summaryInterval) {
      clearInterval(summaryInterval);
      this.summaryTypingIntervals.delete(indexToClose);
      message.isSummaryTyping = false;
    }

    // If we still have the raw rows, keep the result card usable by flipping
    // the tab bar to the table view instead of leaving an empty card. The
    // Chart tab can rebuild the Chart.js instance from resultData on demand.
    if (message.resultData && message.resultData.length > 0 && message.chartConfig) {
      this.ensureChartTableView(message);
      message.activeView = 'table';
    }

    // Reset full screen if this chart was in full screen
    if (this.fullScreenChartIndex === indexToClose) {
      this.fullScreenChartIndex = null;
    }
  }

  /** Relayout the gantt chart for a specific message index (fullscreen / resize). */
  private getGanttChartAt(messageIndex: number): ScraiiGanttChartComponent | null {
    const gantts = this.ganttCharts?.toArray() ?? [];
    const ganttIndices: number[] = [];
    this.messages.forEach((msg: ChatMessage, i: number) => {
      if (msg.chartConfig && this.isGanttChart(msg.chartConfig)) {
        ganttIndices.push(i);
      }
    });
    const ganttSlot = ganttIndices.indexOf(messageIndex);
    return ganttSlot >= 0 && gantts[ganttSlot] ? gantts[ganttSlot] : null;
  }

  toggleGanttTreeExpand(messageIndex: number): void {
    const gantt = this.getGanttChartAt(messageIndex);
    const msg = this.messages[messageIndex];
    if (!gantt || !msg) {
      return;
    }
    const nextExpanded = !msg.ganttTreeExpanded;
    if (nextExpanded) {
      gantt.expandAllTree();
    } else {
      gantt.collapseAllTree();
    }
    msg.ganttTreeExpanded = nextExpanded;
  }

  private restoreGanttTreeExpandState(messageIndex: number): void {
    const msg = this.messages[messageIndex];
    if (!msg?.ganttTreeExpanded) {
      return;
    }
    requestAnimationFrame(() => {
      setTimeout(() => {
        const gantt = this.getGanttChartAt(messageIndex);
        if (gantt && msg.ganttTreeExpanded) {
          gantt.expandAllTree();
        }
      }, 200);
    });
  }

  private relayoutGanttChartAt(messageIndex: number): void {
    const gantt = this.getGanttChartAt(messageIndex);
    if (gantt) {
      gantt.relayout();
    }
  }

  resizeFloatingCharts(): void {
    this.relayoutFloatingContent();
  }

  relayoutFloatingContent(): void {
    if (!this.isFloatingView) {
      return;
    }

    this.messages.forEach((msg: ChatMessage, index: number) => {
      const chart: any = msg.chartInstance;
      if (!chart) {
        return;
      }

      const expanded = this.isFloatingExpanded;
      if (chart._floatingLayoutExpanded !== expanded) {
        this.applyFloatingChartOptions(chart, expanded);
        chart._floatingLayoutExpanded = expanded;
      }

      try {
        chart.resize();
      } catch (error) {
        console.error('Error resizing floating chart:', error);
      }

      if (msg.brushState?.show) {
        this.reinitChartBrush(index, msg);
      }
    });

    this.ganttCharts?.forEach((gantt) => gantt.relayout());

    if (this.gridApi) {
      try {
        this.gridApi.sizeColumnsToFit();
      } catch (error) {
        console.error('Error resizing query results grid:', error);
      }
    }

    this.messageGridApis.forEach((api) => {
      try {
        api.sizeColumnsToFit();
      } catch (error) {
        console.error('Error resizing message table grid:', error);
      }
    });

    this.ragGridApis.forEach((api) => {
      try {
        this.refreshRagGrid(api);
      } catch (error) {
        console.error('Error resizing RAG grid:', error);
      }
    });

    this.syncActiveTextareaHeight();
    this.cdr.detectChanges();
  }

  private applyFloatingChartOptions(chart: any, expanded: boolean): void {
    const legendFontSize = expanded ? 10 : 9;
    const tooltipTitleSize = expanded ? 11 : 10;
    const tooltipBodySize = expanded ? 10 : 9;
    const axisTitleSize = expanded ? 10 : 9;
    const axisTickSize = expanded ? 9 : 8;

    chart.options.aspectRatio = expanded ? 2.2 : 1.5;
    chart.options.maintainAspectRatio = true;

    if (chart.options.plugins?.legend?.labels) {
      chart.options.plugins.legend.labels.font.size = legendFontSize;
      chart.options.plugins.legend.labels.padding = expanded ? 8 : 6;
      chart.options.plugins.legend.labels.boxWidth = expanded ? 7 : 6;
      chart.options.plugins.legend.labels.boxHeight = expanded ? 7 : 6;
    }

    if (chart.options.plugins?.tooltip) {
      chart.options.plugins.tooltip.titleFont = {
        ...chart.options.plugins.tooltip.titleFont,
        size: tooltipTitleSize,
      };
      chart.options.plugins.tooltip.bodyFont = {
        ...chart.options.plugins.tooltip.bodyFont,
        size: tooltipBodySize,
      };
      chart.options.plugins.tooltip.footerFont = {
        ...chart.options.plugins.tooltip.footerFont,
        size: tooltipBodySize,
      };
    }

    const scales = chart.options.scales || {};
    Object.keys(scales).forEach((scaleKey) => {
      const scale = scales[scaleKey];
      if (!scale) {
        return;
      }
      if (scale.title?.font) {
        scale.title.font.size = axisTitleSize;
      }
      if (scale.ticks?.font) {
        scale.ticks.font.size = axisTickSize;
      }
    });

    chart.update('none');
  }

  private reinitChartBrush(messageIndex: number, message: ChatMessage): void {
    if (!message.brushState?.show || !message.chartInstance) {
      return;
    }

    this.destroyBrush(messageIndex);
    const brushCanvas = document.getElementById(`brushCanvas-${messageIndex}`) as HTMLCanvasElement | null;
    if (brushCanvas) {
      brushCanvas.width = 0;
      brushCanvas.height = 0;
    }

    requestAnimationFrame(() => {
      this.initBrush(messageIndex, message);
    });
  }

  // Destroy all chart instances (called when switching chats)
  private destroyAllCharts() {
    this.messages.forEach((msg: ChatMessage, idx: number) => {
      if (msg.chartInstance) {
        try {
          msg.chartInstance.destroy();
        } catch (error) {
          console.error('Error destroying chart instance:', error);
        }
        msg.chartInstance = null;
      }
      // Destroy brush navigator for each message
      this.destroyBrush(idx);
      msg.brushState = undefined;
    });

    // Cancel any ongoing chart generation
    if (this.chartSubscription) {
      this.chartSubscription.unsubscribe();
      this.chartSubscription = undefined;
    }

    this.activeChartIndex = null;
    this.chartConfig = null;
    this.isGeneratingChart = false;
    this.currentChartTitle = "Chart Visualization";
    this.messageGridApis.clear();
    this.hideChartAxisTitleTooltip();
  }

  // LIFECYCLE HOOK - CLEANUP
  ngOnDestroy(): void {
    // Destroy all chart instances to prevent memory leaks
    this.destroyAllCharts();
    this.removeChartAxisTitleTooltipEl();
    this.dismissFullscreenSnackBar();

    // Remove click-outside listener for sample queries dropdown
    document.removeEventListener('click', this.clickOutsideHandler);

    // Tear down every AI operation subscription/timer/flag.
    this.resetAllOperationState();

    // History loading is independent of AI operation state — cancel on destroy only.
    if (this.historySubscription) {
      this.historySubscription.unsubscribe();
    }

    if (this.transcriptionSubscription) {
      this.transcriptionSubscription.unsubscribe();
    }
    if (this.errorSubscription) {
      this.errorSubscription.unsubscribe();
    }
    if (this.speechService.isRecording) {
      this.speechService.stopRecording();
    }
  }

  // Voice input methods
  initializeSpeechRecognition(): void {
    // Check if speech recognition is supported
    this.speechSupported = this.speechService.isSupported();

    if (!this.speechSupported) {
      console.warn("Speech recognition is not supported in this browser");
      return;
    }

    // Subscribe to transcription results
    this.transcriptionSubscription =
      this.speechService.transcription$.subscribe((transcription) => {
        // Replace userInput with initial text + new transcription for real-time display
        this.userInput = this.initialUserInput + transcription;

        // Trigger textarea height adjustment
        setTimeout(() => {
          const textarea =
            this.centeredTextarea?.nativeElement ||
            this.bottomTextarea?.nativeElement;
          if (textarea) {
            const event = new Event("input", { bubbles: true });
            textarea.dispatchEvent(event);
          }
        }, 0);
      });

    // Subscribe to errors
    this.errorSubscription = this.speechService.error$.subscribe((error) => {
      if (error) {
        this.dialogService.openSnackBar(error, "OK");
      }
    });
  }

  // Handle Enter key press - prevent sending while recording
  handleEnterKey(event: Event): void {
    event.preventDefault();
    if (this.speechService.isRecording) {
      this.dialogService.openSnackBar(
        "Please stop voice recording before sending",
        "OK"
      );
      return;
    }
    // Don't send if already loading or input is empty
    if (this.isLoading || !this.userInput.trim()) {
      return;
    }
    // Send the message
    this.sendChatbotMessage();
  }

  toggleVoiceInput(): void {
    if (!this.speechSupported) {
      this.dialogService.openSnackBar(
        "Speech recognition is not supported in your browser",
        "OK"
      );
      return;
    }

    if (this.speechService.isRecording) {
      this.speechService.stopRecording();
      // Clear initial input after stopping
      this.initialUserInput = "";
    } else {
      // Close the Browse Queries popup before the mic takes focus.
      this.closeQueryPopup();
      // Store current input before starting
      this.initialUserInput = this.userInput;
      this.speechService.startRecording();
    }
  }

  // Check if any operation is currently running
  get isAnyOperationRunning(): boolean {
    return this.isLoading || this.isTyping || this.isGeneratingChart || this.isExecutingQuery || this.isAutoExecuting;
  }

  // Stop any currently running operation.
  //
  // Every subscription, timer, animation, and flag MUST be reset unconditionally
  // so the UI returns to a clean idle state regardless of which phase of the
  // async flow was active when Stop was pressed.
  //
  // In particular, during async polling `isTyping` is set true BEFORE any
  // typingInterval exists (see sendChatbotMessage), so guarded resets that
  // only fire inside `if (this.typingInterval)` would leave typing dots stuck.
  //
  // NOTE: historySubscription is intentionally NOT cancelled here — chat history
  // loading is independent of AI chat operations.
  stopCurrentOperation(): void {
    const operationStopped = this.isAnyOperationRunning;
    this.resetAllOperationState({ popPartialAssistantMessage: true });
    if (operationStopped) {
      this.dialogService.openSnackBar("Operation stopped", "OK");
    }
  }

  // Shared cleanup used by stopCurrentOperation() and ngOnDestroy() so the two
  // cannot drift. Tears down every subscription, timer, animation, and flag
  // associated with an AI chat operation.
  private resetAllOperationState(opts: { popPartialAssistantMessage?: boolean } = {}): void {
    // Unsubscribe every HTTP / polling / animation source
    if (this.messageSubscription) { this.messageSubscription.unsubscribe(); this.messageSubscription = undefined; }
    if (this.pollingSubscription) { this.pollingSubscription.unsubscribe(); this.pollingSubscription = null; }
    if (this.fetchResultSubscription) { this.fetchResultSubscription.unsubscribe(); this.fetchResultSubscription = null; }
    if (this.executeSqlSubscription) { this.executeSqlSubscription.unsubscribe(); this.executeSqlSubscription = undefined; }
    if (this.chartSubscription) { this.chartSubscription.unsubscribe(); this.chartSubscription = undefined; }
    if (this.autoExecuteSubscription) { this.autoExecuteSubscription.unsubscribe(); this.autoExecuteSubscription = undefined; }
    if (this.loadChatMessagesSubscription) { this.loadChatMessagesSubscription.unsubscribe(); this.loadChatMessagesSubscription = undefined; }

    // Kill every timer
    if (this.typingInterval) {
      clearInterval(this.typingInterval);
      this.typingInterval = null;
    }
    this.stopStatusRotation(); // clears statusRotationInterval + processingStatusMessage
    this.stopOpsReveal();
    this.summaryTypingIntervals.forEach(intervalId => clearInterval(intervalId));
    this.summaryTypingIntervals.clear();
    this.pendingSummaryTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this.pendingSummaryTimeouts = [];

    this.removeOpsPlaceholder();

    // Remove partial assistant message being typed (only when user-initiated stop)
    if (opts.popPartialAssistantMessage &&
        this.typingMessageIndex !== null &&
        this.messages.length > 0 &&
        this.messages[this.messages.length - 1].role === 'assistant') {
      this.messages.pop();
    }

    // Reset per-message transient state
    this.messages.forEach((msg: ChatMessage) => {
      msg.isGenerating = false;
      msg.isLoadingTable = false;
      msg.isSummaryTyping = false;
    });

    // Reset ALL component-level flags unconditionally — returns UI to idle
    this.isLoading = false;
    this.isTyping = false;
    this.isGeneratingChart = false;
    this.isExecutingQuery = false;
    this.isAutoExecuting = false;
    this.isLoadingMessages = false;
    this.typingMessageIndex = null;
    this.pollAttemptCount = 0;
    this.activeChartIndex = null;
    this.fullScreenTableIndex = null;

    // Close the manual query results dialog if it was open
    this.closeQueryResults();
  }


  // Full-screen state
  isResultFullScreen: boolean = false;
  fullScreenChartIndex: number | null = null;

  toggleResultFullScreen() {
    if (this.isFloatingView) {
      return;
    }
    this.isResultFullScreen = !this.isResultFullScreen;

    if (this.isResultFullScreen) {
      this.showFullscreenSnackBar();
    } else {
      this.dismissFullscreenSnackBar();
    }

    // Resize the grid after fullscreen toggle
    if (this.gridApi) {
      setTimeout(() => {
        this.gridApi.sizeColumnsToFit();
      }, 200);
    }
  }

  onGridReady(params: any) {
    this.gridApi = params.api;
  }

  onMessageGridReady(messageIndex: number, params: any): void {
    this.messageGridApis.set(messageIndex, params.api);
  }

  toggleChartFullScreen(index: number) {
    if (this.isFloatingView) {
      return;
    }
    if (this.fullScreenChartIndex === index) {
      this.fullScreenChartIndex = null;
    } else {
      this.fullScreenChartIndex = index;
    }

    if (this.fullScreenChartIndex !== null) {
      this.showFullscreenSnackBar();
    } else {
      this.dismissFullscreenSnackBar();
    }

    const message = this.messages[index];
    if (!message) {
      return;
    }

    const isGantt = message.chartConfig && this.isGanttChart(message.chartConfig);
    if (isGantt) {
      requestAnimationFrame(() => {
        setTimeout(() => this.relayoutGanttChartAt(index), 200);
      });
      return;
    }

    if (!message.chartInstance) return;

    // Toggle maintainAspectRatio: false in fullscreen so chart fills container
    // without overflowing; true in normal view to keep proportions
    const isNowFullscreen = this.fullScreenChartIndex === index;
    const chart: any = message.chartInstance;
    // Capture the chart's original aspect ratio exactly once. Chart.js v3 derives
    // this lazily from current canvas dims when options.aspectRatio is unset, so
    // without pinning it, the fullscreen resize mutates the perceived ratio and
    // exit renders at the fullscreen ratio instead of the original.
    if (chart._originalAspectRatio === undefined) {
      chart._originalAspectRatio = chart.options.aspectRatio ?? (chart.width / chart.height);
    }
    chart.options.maintainAspectRatio = !isNowFullscreen;
    chart.options.aspectRatio = isNowFullscreen ? undefined : chart._originalAspectRatio;

    // Resize the chart after DOM reflow from fullscreen class toggle
    requestAnimationFrame(() => {
      setTimeout(() => {
        message.chartInstance?.resize();

        if (message.brushState?.show) {
          this.reinitChartBrush(index, message);
        }
      }, 200);
    });
  }

  // Check if any fullscreen mode is currently active
  private isAnyFullScreen(): boolean {
    if (this.isFloatingView) {
      return false;
    }
    return this.isResultFullScreen
      || this.fullScreenChartIndex !== null
      || this.fullScreenTableIndex !== null;
  }

  private clearFullscreenState(): void {
    this.isResultFullScreen = false;
    this.fullScreenChartIndex = null;
    this.fullScreenTableIndex = null;
    this.dismissFullscreenSnackBar();
  }

  // Exit whichever fullscreen is active (chart, table, or manual query result)
  private exitAnyFullScreen(): void {
    if (this.fullScreenChartIndex !== null) {
      this.toggleChartFullScreen(this.fullScreenChartIndex);
    } else if (this.fullScreenTableIndex !== null) {
      this.toggleTableFullScreen(this.fullScreenTableIndex);
    } else if (this.isResultFullScreen) {
      this.toggleResultFullScreen();
    } else {
      return; // Nothing was fullscreen
    }
    this.dismissFullscreenSnackBar();
  }

  // Show an in-component toast inside the fullscreen container.
  // MatSnackBar renders into cdk-overlay-container (z-index: 1102) which sits
  // BEHIND the fullscreen overlay (z-index: 9999), so we use a component-local
  // element instead.
  private showFullscreenSnackBar(): void {
    if (this.isFloatingView) {
      return;
    }
    this.dismissFullscreenSnackBar();
    this.fullscreenToastVisible = true;
    this.fullscreenToastTimeout = setTimeout(() => {
      this.fullscreenToastVisible = false;
      this.fullscreenToastTimeout = null;
    }, 3000);
  }

  private dismissFullscreenSnackBar(): void {
    if (this.fullscreenToastTimeout) {
      clearTimeout(this.fullscreenToastTimeout);
      this.fullscreenToastTimeout = null;
    }
    this.fullscreenToastVisible = false;
  }

}

// okk